import type { Product } from "@/types";
import { SITE_URL } from "@/lib/constants";

export const products: Product[] = [
  {
    id: "manufacturing-erp",
    slug: "manufacturing-erp-software",
    category: "mx-erp",
    name: "Manufacturing ERP",
    tagline: "Precision Control for Modern Factories | Real-time Production, Inventory & GST Management",
    image: "/images/products/mx-erp.webp",
    href: "/products/manufacturing-erp-software",
    badge: "Most Popular",
    color: "#2C78B9",
    family: "Mx-ERP",
    keyWord: "Built",
    accentColor: "#2C78B9",
    trustCount: "1000+",
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    headlineThin: "Manufacturing ERP", 
    capHeadline: "Make Manufacturing Smarter with Mx-ERP",
    headlineBold: "ERP Software for Manufacturing Industry",
    headlineAccent: "Mx-ERP by ACGIL",
    subheadline: "ACGIL Mx-ERP is the best Manufacturing ERP software designed to give you complete control over production, inventory, and GST compliance — all in one unified platform.",
    dashHeading: "Manage your entire manufacturing operation from a single dashboard. ACGIL Mx-ERP delivers real-time visibility, faster decision-making, and complete control across production, inventory, and finance.",
    PainPointsSubheading: "Trading businesses deal with daily operational challenges that directly impact margins, efficiency, and growth.",
    painPoints: [
      { title: "Spreadsheet Chaos", desc: "Multiple versions, manual entries, and frequent data errors." },
      { title: "Zero Visibility", desc: "No real-time tracking of inventory, production, or orders." },
      { title: "Manual GST", desc: "Time-consuming GST reconciliation and compliance every month." },
      { title: "Siloed Teams", desc: "Departments working separately without integrated data flow." },
      { title: "Production Delays", desc: "Poor planning leading to bottlenecks and missed deadlines." },
    ],
    description: "ACGIL Mx-ERP is a powerful Manufacturing ERP software that connects all business functions into one intelligent system.",
    capdescription: "Advanced ERP modules designed to optimize manufacturing operations and improve efficiency.",
    capabilities: [
      {
        title: "Production Planning",
        description: "Improve production efficiency with real-time planning and AI-driven scheduling to maximize output and reduce downtime.",
        features: [
          "Multi-level BOM & work order management",
          "Live shop floor monitoring",
          "Machine capacity & utilization tracking",
          "Downtime alerts & performance analysis"
        ],
        tag: "Production",
        widget: "planning",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9"
      },
      {
        title: "Inventory",
        description: "Gain full control over inventory with real-time stock tracking across warehouses and production units.",
        features: [
          "Multi-warehouse stock management",
          "Auto-reorder alerts to prevent stockouts",
          "Barcode & RFID integration",
          "Batch & serial number tracking"
        ],
        tag: "Inventory",
        widget: "inventory",
        accentColor: "#2C78B9",
        secondaryColor: "#059A8F"
      },
      {
        title: "Procurement & Vendor Management",
        description: "Optimize procurement with automated purchasing and smarter vendor management.",
        features: [
          "Automated purchase order generation",
          "Vendor performance tracking",
          "3-way matching (PO, GRN, Invoice)",
          "Approval workflows & budget control"
        ],
        tag: "Procurement",
        widget: "vendor",
        accentColor: "#7F77DD",
        secondaryColor: "#059A8F"
      },
      {
        title: "Finance & Order Management",
        description: "Manage your financial operations with GST-ready ERP software built for manufacturing businesses in India.",
        features: [
          "Quotation to dispatch workflow",
          "Real-time order tracking",
          "Automated GST invoicing",
          "Customer pricing & discount management"
        ],
        tag: "Finance",
        widget: "finance",
        accentColor: "#059A8F",
        secondaryColor: "#7F77DD"
      },
      {
        title: "HR & Payroll Management",
        description: "Simplify workforce management with fully automated HR and payroll processes.",
        features: [
          "Payroll automation with PF/ESI/TDS",
          "Biometric attendance integration",
          "Leave & shift management",
          "Employee self-service portal"
        ],
        tag: "HRMS",
        widget: "hr",
        accentColor: "#2C78B9",
        secondaryColor: "#7F77DD"
      },
      {
        title: "MIS & Business Intelligence",
        description: "Make faster, data-driven decisions with real-time analytics and business intelligence tools.",
        features: [
          "150+ pre-built reports",
          "Custom KPI dashboards",
          "Scheduled email reports",
          "Multi-location business insights"
        ],
        tag: "Analytics",
        widget: "mis",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9"
      }
    ],
    dashboardType: "manufacturing",
    businessImpact: [
      {
        value: "30%",
        label: "Inventory Cost",
        description: "Reduce carrying costs with smart inventory optimization",
        color: "#00F2FE",
        icon: "TrendingDown"
      },
      {
        value: "25%",
        label: "Efficiency",
        description: "Improve production performance and operational efficiency",
        color: "#7F77DD",
        icon: "TrendingUp"
      },
      {
        value: "40%",
        label: "Visibility",
        description: "Gain complete real-time visibility across operations",
        color: "#F59E0B",
        icon: "Eye"
      },
      {
        value: "2X",
        label: "Faster Decisions",
        description: "Accelerate decision-making with real-time insights",
        color: "#EC4899",
        icon: "Zap"
      }
    ],
    workflow: [
      { id: 1, title: "Sales Order", step: "Step 1", icon: "order", color: "#2C78B9", bgColor: "bg-indigo-500", shadow: "shadow-indigo-500/20" },
      { id: 2, title: "Production Planning", step: "Step 2", icon: "production", color: "#2C78B9", bgColor: "bg-purple-500", shadow: "shadow-purple-500/20" },
      { id: 3, title: "Material Procurement", step: "Step 3", icon: "inventory", color: "#2C78B9", bgColor: "bg-blue-500", shadow: "shadow-blue-500/20" },
      { id: 4, title: "Manufacturing", step: "Step 4", icon: "production", color: "#2C78B9", bgColor: "bg-emerald-500", shadow: "shadow-emerald-500/20" },
    ],
    features: [
      { id: "production", title: "Production Planning", description: "Automated BOM-based scheduling and machine allocation." },
      { id: "inventory", title: "Inventory Control", description: "Real-time multi-location tracking with batch & serial traceability." },
      { id: "analytics", title: "Real-time Analytics", description: "Live OEE tracking and production variance reporting." },
      { id: "quality", title: "Quality Assurance", description: "Integrated QC checkpoints at every stage of production." },
      { id: "gst", title: "GST & Finance", description: "Automated GST filing and real-time job-work costing." },
      { id: "hr", title: "HR & Payroll", description: "Biometric attendance integration and automated payroll processing." },
    ],
    featureTabs: [
      {
        id: "production",
        label: "Production",
        features: [
          { title: "BOM Management", desc: "Multi-level Bill of Materials with version control." },
          { title: "Production Scheduling", desc: "AI-powered machine and labor allocation." },
          { title: "Quality Control", desc: "Integrated QC checks at every production stage." },
        ],
        image: "/artifacts/manufacturing_erp_dashboard_mockup.png"
      },
      {
        id: "inventory",
        label: "Inventory",
        features: [
          { title: "Multi-location Tracking", desc: "Manage stock across multiple warehouses and plants." },
          { title: "Batch & Serial Tracking", desc: "Full traceability from raw material to finished goods." },
          { title: "Auto-Reordering", desc: "Smart alerts based on consumption patterns." },
        ],
        image: "/images/products/mx-erp.webp"
      },
      {
        id: "finance",
        label: "Finance & GST",
        features: [
          { title: "Auto-GST Filing", desc: "Direct integration with GST portal for seamless filing." },
          { title: "Costing Analysis", desc: "Real-time landing cost and profitability tracking." },
          { title: "Financial Reporting", desc: "Balance sheets, P&L and cash flow in one click." },
        ],
        image: "/images/products/mx-erp.webp"
      }
    ],
    headline: "Manufacturing ERP That Actually Works",
    challengesSubheadline: "Stop fighting with spreadsheets. ACGIL Mx-ERP gives you total control over production, inventory, and GST compliance in one unified platform.",
    heroStats: [
      { label: "Active Clients", value: "1000+" },
      { label: "Years of Trust", value: "30+" },
      { label: "GST Ready", value: "100%" },
    ],
    mockupImage: "/images/products/manufacturing-hero.png",
    solutions: [
      { title: "Unified real-time data", desc: "One source of truth across all your departments." },
      { title: "Live tracking", desc: "Real-time inventory alerts and multi-location tracking." },
      { title: "Automated GST", desc: "File your GST returns in minutes, not days." },
      { title: "Seamless integration", desc: "Connected modules from procurement to dispatch." },
      { title: "AI-driven scheduling", desc: "Optimize production with intelligent guesswork-free planning." },
    ],
    roiEstimate: "₹8–15 lakhs/year",
    implementationSteps: [
      {
        num: "01",
        title: "Process Audit",
        description: "Our experts visit your plant to map existing workflows, identify bottlenecks, and define clear KPIs for the ERP transition.",
        bullets: ["Workflow mapping", "KPI definition", "Gap analysis"],
        image: "/images/products/audit.png",
        bg: "linear-gradient(135deg, #071428, #0d3060)"
      },
      {
        num: "02",
        title: "Seamless Integration",
        description: "We configure the Mx-ERP modules to fit your specific BOM structures and QC requirements, ensuring 100% data accuracy from day one.",
        bullets: ["BOM configuration", "QC integration", "Data migration"],
        image: "/images/products/integration.png",
        bg: "linear-gradient(135deg, #071e1c, #0a3530)"
      },
      {
        num: "03",
        title: "Go-Live & Support",
        description: "Dedicated training sessions for your staff followed by on-site support during the first month of production to ensure zero downtime.",
        bullets: ["Staff training", "On-site support", "Hypercare period"],
        image: "/images/products/support.png",
        bg: "linear-gradient(135deg, #100d28, #1a1040)"
      }
    ],
    clients: [
      { id: "tm", name: "Tata Motors", logo: "/images/clients/acma-logo.webp" },
      { id: "lt", name: "L&T", logo: "/images/clients/ahpl-logo.webp" },
      { id: "hv", name: "Havells", logo: "/images/clients/aiims-logo.webp" },
      { id: "rj", name: "Reliance Jio", logo: "/images/clients/allahabad-bank-logo.webp" },
      { id: "ms", name: "Mahindra & Mahindra", logo: "/images/clients/ant-logo.webp" },
      { id: "itc", name: "ITC Limited", logo: "/images/clients/aos-logo.webp" },
      { id: "apl", name: "Apollo Tyres", logo: "/images/clients/arogyavaram-logo.webp" },
      { id: "hcl", name: "HCL Tech", logo: "/images/clients/arora-aromatics-logo.webp" },
    ],
    industry: "Manufacturing",
    heroImage: "/images/products/manufacturing-hero.png",
    marqueeItems: [
      "GST Compliant",
      "Real-time BOM",
      "Multi-plant Sync",
      "AI Inventory",
      "Cloud Security",
      "ISO Certified",
    ],
    testimonials: [
      {
        id: 101,
        name: "Rajesh Kumar",
        role: "Plant Manager",
        company: "Steel Dynamics India",
        avatar: "/images/partners/1.png",
        quote: "ACGIL Manufacturing ERP reduced our production bottlenecks by 40% in just three months. The real-time visibility into inventory is a game changer.",
        rating: 5
      },
      {
        id: 102,
        name: "Anjali Gupta",
        role: "CFO",
        company: "Precision Auto Parts",
        avatar: "/images/partners/2.webp",
        quote: "The automated GST filing and job-work costing saved us countless hours. It's the first ERP that actually understands Indian manufacturing complexities.",
        rating: 5
      },
      {
        id: 103,
        name: "Vikram Singh",
        role: "Operations Director",
        company: "Metro Tech Manufacturing",
        avatar: "/images/partners/3.webp",
        quote: "We struggled with tracking our BOMs and multi-level assemblies before ACGIL. Now, everything is seamlessly integrated from procurement to final dispatch.",
        rating: 5
      },
      {
        id: 104,
        name: "Priya Sharma",
        role: "Supply Chain Head",
        company: "Apex Industrial Solutions",
        avatar: "/images/partners/4.webp",
        quote: "Multi-location inventory tracking was a nightmare. ACGIL ERP gave us complete control over our stock across all 5 warehouses with zero discrepancies.",
        rating: 5
      },
      {
        id: 105,
        name: "Amit Desai",
        role: "CEO",
        company: "Desai Engineering Works",
        avatar: "/images/partners/5.webp",
        quote: "The ROI has been phenomenal. We cut down our material wastage by 15% and improved our delivery timelines significantly thanks to the automated scheduling.",
        rating: 5
      }
    ],
    integrations: [
      { name: "Tally Prime", icon: "calculator", category: "Accounting" },
      { name: "Razorpay", icon: "credit-card", category: "Payments" },
      { name: "Shopify", icon: "shopping-bag", category: "E-commerce" },
      { name: "GSTN Portal", icon: "file-text", category: "Government" },
      { name: "E-way Bill", icon: "truck", category: "Government" },
      { name: "Delhivery", icon: "package", category: "Logistics" },
      { name: "Zoho Books", icon: "book-open", category: "Accounting" },
      { name: "PayPal", icon: "dollar-sign", category: "Payments" },
      { name: "WooCommerce", icon: "shopping-cart", category: "E-commerce" },
      { name: "Shiprocket", icon: "rocket", category: "Logistics" },
      { name: "Stripe", icon: "credit-card", category: "Payments" },
      { name: "E-Invoice", icon: "file-digit", category: "Government" },
    ],
    faqs: [
      {
        q: "What is ERP software for manufacturing industry?",
        a: "ERP software for manufacturing industry is an all-in-one system that helps manage production, inventory, finance, and compliance in real time."
      },
      {
        q: "Why is ERP important for manufacturing businesses?",
        a: "ERP improves efficiency, reduces costs, and provides complete visibility into operations."
      },
      {
        q: "Can ERP software reduce production delays?",
        a: "Yes, ERP software helps in better production planning and real-time tracking, reducing delays and bottlenecks."
      },
      {
        q: "Is ERP software GST compliant?",
        a: "Modern ERP systems include built-in GST compliance features for invoicing and tax reporting."
      },
      {
        q: "How does ERP improve inventory management?",
        a: "ERP provides real-time stock tracking, auto-reorder alerts, and warehouse management."
      }
    ],
    industriesServed: [
      "Automotive & Auto Components",
      "Food & Beverage Processing",
      "Electrical & Electronics",
      "Metal Fabrication & Engineering",
      "Chemical & Paint",
      "Packaging & Plastics",
      "Aerospace & Defence"
    ],
    comparison: [
      {
        feature: "Implementation Time",
        mxerp: "6–8 Weeks",
        others: "6–12 Months"
      },
      {
        feature: "Total Cost of Ownership",
        mxerp: "Low",
        others: "High"
      },
      {
        feature: "Customization Flexibility",
        mxerp: "High",
        others: "Limited"
      },
      {
        feature: "GST & Compliance",
        mxerp: "Built-in",
        others: "Add-on"
      },
      {
        feature: "Industry Focus",
        mxerp: "Purpose-Built for Manufacturing",
        others: "Generic"
      },
      {
        feature: "Cloud + On-Premise",
        mxerp: "Both Options",
        others: "Cloud Only"
      },
      {
        feature: "Local Support (India)",
        mxerp: "24/7 Dedicated",
        others: "Timezone Issues"
      },
      {
        feature: "Setup Complexity",
        mxerp: "Simple",
        others: "Complex"
      }
    ],
    seo: {
      title: "Best erp software for manufacturing Industry in india",
      description: "ACGIL’s ERP software for the manufacturing industry in India, designed to manage production, inventory, procurement, finance, sales, and business operations efficiently.",
      keywords: "ERP software for manufacturing industry, manufacturing ERP software India, production management ERP, inventory ERP system, GST ERP software",
      canonical: SITE_URL + "/products/manufacturing-erp-software",
      robots: "index, follow",
      ogTitle: "Best erp software for manufacturing Industry in india",
      ogDescription: "ACGIL’s ERP software for the manufacturing industry in India, designed to manage production, inventory, procurement, finance, sales, and business operations efficiently.",
      ogUrl: SITE_URL + "/products/manufacturing-erp-software",
      ogImage: SITE_URL + "/images/mx-erp-dashboard.webp",
      hreflang: {
        "en-in": SITE_URL + "/products/manufacturing-erp-software"
      }
    }
  },
  {
    id: "trading-erp-software",
    slug: "traders-erp-software",
    category: "mx-erp",
    name: "Traders ERP Software",
    tagline: "Built for Speed. Powered for Profit. | Mx-ERP Traders ERP Software for Trading Industry",
    image: "/images/products/mx-erp-trading.webp",
    href: "/products/traders-erp-software",
    badge: "Most Popular",
    color: "#2C78B9",
    family: "Mx-ERP",
    keyWord: "Built",
    accentColor: "#2C78B9",
    trustCount: "1000+",
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    headlineThin: "Traders ERP",
    capHeadline: "Make Trading Smarter with Mx-ERP",
    headlineBold: "ERP Software for Trading Industry",
    headlineAccent: "Mx-ERP by ACGIL",
    subheadline: "Mx-ERP by ACGIL is a powerful Traders ERP Software designed to give you complete control over inventory, sales, distribution, and GST compliance — all in one unified platform.",
    dashHeading: "Manage your entire trading operations from a single dashboard. Mx-ERP delivers real-time visibility, faster order processing, and complete control across inventory, sales, procurement, and finance.",
    PainPointsSubheading: "Trading businesses deal with daily operational challenges that directly impact margins, efficiency, and growth.",
    painPoints: [
      { title: "Stock Mismanagement", desc: "Overstocking or stockouts due to poor inventory tracking." },
      { title: "Order Errors", desc: "Manual order processing leading to mistakes and delays." },
      { title: "GST Complexity", desc: "Frequent errors in GST billing and compliance." },
      { title: "Multiple Locations", desc: "Difficulty managing warehouses and branches." },
      { title: "Low Visibility", desc: "No real-time insights into stock, sales, or cash flow." },
    ],
    solutionHeadline: "How Traders ERP Software Solves These Challenges",
    description: "Mx-ERP connects all your business processes into one intelligent system:",
    solutionFeatures: [
      "Real-time inventory tracking",
      "Automated order-to-cash workflows",
      "Centralized business operations",
      "Smart analytics for faster decisions"
    ],
    capdescription: "Advanced ERP modules designed to streamline trading operations and maximize profitability.",
    capabilities: [
      {
        title: "Inventory Management",
        description: "Gain full control over inventory with real-time tracking across warehouses and distribution centers.",
        features: [
          "Multi-warehouse stock control",
          "Batch & serial tracking",
          "Barcode & RFID integration",
          "Auto stock alerts"
        ],
        tag: "Inventory",
        widget: "inventory",
        accentColor: "#2C78B9",
        secondaryColor: "#059A8F"
      },
      {
        title: "Sales & Distribution Management",
        description: "Streamline your entire sales cycle from quotation to delivery.",
        features: [
          "Quotation to order automation",
          "Sales tracking",
          "Multi-channel sales",
          "Dispatch management"
        ],
        tag: "Sales",
        widget: "sales",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9"
      },
      {
        title: "Procurement & Vendor Management",
        description: "Simplify purchasing and vendor coordination with automation.",
        features: [
          "Automated purchase orders",
          "Vendor performance tracking",
          "3-way matching",
          "Approval workflows"
        ],
        tag: "Procurement",
        widget: "vendor",
        accentColor: "#7F77DD",
        secondaryColor: "#059A8F"
      },
      {
        title: "Finance & GST Management",
        description: "Manage finances with GST-ready ERP built for Indian trading businesses.",
        features: [
          "GST-ready billing",
          "Accounts receivable/payable",
          "Credit control",
          "Financial reports"
        ],
        tag: "Finance",
        widget: "finance",
        accentColor: "#059A8F",
        secondaryColor: "#7F77DD"
      },
      {
        title: "Warehouse & Logistics Management",
        description: "Optimize warehouse operations and logistics efficiency.",
        features: [
          "Warehouse bin management",
          "Stock movement tracking",
          "Delivery planning",
          "Dispatch visibility"
        ],
        tag: "Warehouse",
        widget: "warehouse",
        accentColor: "#2C78B9",
        secondaryColor: "#7F77DD"
      },
      {
        title: "MIS & Business Intelligence",
        description: "Make faster, data-driven decisions with real-time insights.",
        features: [
          "150+ reports",
          "KPI dashboards",
          "Real-time insights",
          "Multi-location analytics"
        ],
        tag: "Analytics",
        widget: "mis",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9"
      }
    ],
    dashboardType: "trading",
    businessImpact: [
      {
        value: "30%",
        label: "Inventory Accuracy",
        description: "Reduce losses and optimize stock levels",
        color: "#00F2FE",
        icon: "TrendingUp"
      },
      {
        value: "25%",
        label: "Faster Order Processing",
        description: "Faster order fulfillment and delivery",
        color: "#7F77DD",
        icon: "TrendingUp"
      },
      {
        value: "40%",
        label: "Visibility",
        description: "Real-time insights across all operations",
        color: "#F59E0B",
        icon: "Eye"
      },
      {
        value: "2X",
        label: "Faster Decision Making",
        description: "Data-driven decision making",
        color: "#EC4899",
        icon: "Zap"
      }
    ],
    workflow: [
      { id: 1, title: "Customer Order", step: "Step 1", icon: "order", color: "#2C78B9", bgColor: "bg-indigo-500", shadow: "shadow-indigo-500/20" },
      { id: 2, title: "Inventory", step: "Step 2", icon: "production", color: "#2C78B9", bgColor: "bg-purple-500", shadow: "shadow-purple-500/20" },
      { id: 3, title: "Dispatch", step: "Step 3", icon: "truck", color: "#2C78B9", bgColor: "bg-blue-500", shadow: "shadow-blue-500/20" },
      { id: 4, title: "Payment", step: "Step 4", icon: "finance", color: "#2C78B9", bgColor: "bg-emerald-500", shadow: "shadow-emerald-500/20" },
      { id: 5, title: "Accounting", step: "Step 5", icon: "finance", color: "#2C78B9", bgColor: "bg-emerald-500", shadow: "shadow-emerald-500/20" },
    ],
    features: [
      { id: "inventory", title: "Inventory Management", description: "Real-time multi-location tracking with batch & serial number traceability." },
      { id: "sales", title: "Sales & Distribution", description: "End-to-end order processing from quotation to dispatch." },
      { id: "analytics", title: "Real-time Analytics", description: "150+ pre-built reports and custom KPI dashboards." },
      { id: "procurement", title: "Procurement Management", description: "Automated PO generation and 3-way matching for accurate purchasing." },
      { id: "gst", title: "GST & Finance", description: "Automated GST billing, returns, and financial compliance." },
      { id: "warehouse", title: "Warehouse & Logistics", description: "Bin management, route planning, and real-time dispatch visibility." },
    ],
    headline: "Your Entire Trading Business,",
    heroStats: [
      { label: "Active Clients", value: "1000+" },
      { label: "Years of Trust", value: "30+" },
      { label: "GST Ready", value: "100%" },
    ],
    mockupImage: "/images/products/trading-hero.png",
    solutions: [
      { title: "Real-time inventory tracking", desc: "Across all locations and warehouses." },
      { title: "Automated order-to-cash", desc: "Faster order fulfillment and delivery." },
      { title: "Automated GST", desc: "Built-in GST billing and compliance." },
      { title: "Centralized control", desc: "Complete business control from one platform." },
      { title: "Smart analytics", desc: "Faster decisions with real-time data." },
    ],
    roiEstimate: "₹8–15 lakhs/year",
    implementationSteps: [
      {
        num: "01",
        title: "Process Audit",
        description: "Our experts visit your business to map existing workflows, identify bottlenecks, and define clear KPIs for the ERP transition.",
        bullets: ["Workflow mapping", "KPI definition", "Gap analysis"],
        image: "/images/products/audit.png",
        bg: "linear-gradient(135deg, #071428, #0d3060)"
      },
      {
        num: "02",
        title: "Seamless Integration",
        description: "We configure the Mx-ERP modules to fit your specific inventory structures and GST requirements, ensuring 100% data accuracy from day one.",
        bullets: ["Inventory configuration", "GST integration", "Data migration"],
        image: "/images/products/integration.png",
        bg: "linear-gradient(135deg, #071e1c, #0a3530)"
      },
      {
        num: "03",
        title: "Go-Live & Support",
        description: "Dedicated training sessions for your staff followed by on-site support during the first month of operations to ensure zero downtime.",
        bullets: ["Staff training", "On-site support", "Hypercare period"],
        image: "/images/products/support.png",
        bg: "linear-gradient(135deg, #100d28, #1a1040)"
      }
    ],
    clients: [
      { id: "tm", name: "Tata Motors", logo: "/images/clients/acma-logo.webp" },
      { id: "lt", name: "L&T", logo: "/images/clients/ahpl-logo.webp" },
      { id: "hv", name: "Havells", logo: "/images/clients/aiims-logo.webp" },
      { id: "rj", name: "Reliance Jio", logo: "/images/clients/allahabad-bank-logo.webp" },
      { id: "ms", name: "Mahindra & Mahindra", logo: "/images/clients/ant-logo.webp" },
      { id: "itc", name: "ITC Limited", logo: "/images/clients/aos-logo.webp" },
      { id: "apl", name: "Apollo Tyres", logo: "/images/clients/arogyavaram-logo.webp" },
      { id: "hcl", name: "HCL Tech", logo: "/images/clients/arora-aromatics-logo.webp" },
    ],
    industry: "Trading",
    heroImage: "/images/products/trading-hero.png",
    marqueeItems: [
      "GST Compliant",
      "Multi-Warehouse",
      "Real-time Stock",
      "Auto Reorder",
      "Cloud Security",
      "ISO Certified",
    ],
    testimonials: [
      {
        id: 101,
        name: "Rajesh Kumar",
        role: "Business Owner",
        company: "Kumar Traders",
        avatar: "/images/partners/1.png",
        quote: "ACGIL Trading ERP reduced our stock discrepancies by 40% in just three months. The real-time visibility into inventory across all our warehouses is a game changer.",
        rating: 5
      },
      {
        id: 102,
        name: "Anjali Gupta",
        role: "CFO",
        company: "Gupta Distribution Co.",
        avatar: "/images/partners/2.webp",
        quote: "The automated GST billing and financial reporting saved us countless hours every month. It's the first ERP that actually understands Indian trading complexities.",
        rating: 5
      },
      {
        id: 103,
        name: "Vikram Singh",
        role: "Operations Director",
        company: "Metro Trade Solutions",
        avatar: "/images/partners/3.webp",
        quote: "We struggled with managing multiple warehouses before ACGIL. Now everything is seamlessly integrated from purchase to final delivery.",
        rating: 5
      },
      {
        id: 104,
        name: "Priya Sharma",
        role: "Supply Chain Head",
        company: "Apex Wholesale Distributors",
        avatar: "/images/partners/4.webp",
        quote: "Multi-location stock tracking was a nightmare. ACGIL ERP gave us complete control over our inventory across all 5 branches with zero discrepancies.",
        rating: 5
      },
      {
        id: 105,
        name: "Amit Desai",
        role: "CEO",
        company: "Desai Trading Works",
        avatar: "/images/partners/5.webp",
        quote: "The ROI has been phenomenal. We reduced order errors by 30% and improved our delivery timelines significantly thanks to automated order processing.",
        rating: 5
      }
    ],
    integrations: [
      { name: "Tally Prime", icon: "calculator", category: "Accounting" },
      { name: "Razorpay", icon: "credit-card", category: "Payments" },
      { name: "Shopify", icon: "shopping-bag", category: "E-commerce" },
      { name: "GSTN Portal", icon: "file-text", category: "Government" },
      { name: "E-way Bill", icon: "truck", category: "Government" },
      { name: "Delhivery", icon: "package", category: "Logistics" },
      { name: "Zoho Books", icon: "book-open", category: "Accounting" },
      { name: "PayPal", icon: "dollar-sign", category: "Payments" },
      { name: "WooCommerce", icon: "shopping-cart", category: "E-commerce" },
      { name: "Shiprocket", icon: "rocket", category: "Logistics" },
      { name: "Stripe", icon: "credit-card", category: "Payments" },
      { name: "E-Invoice", icon: "file-digit", category: "Government" },
    ],
    faqs: [
      {
        q: "What is Traders ERP Software?",
        a: "Traders ERP Software is an integrated business management system that helps trading companies manage inventory, sales, billing, GST compliance, procurement, and finance from a single platform. It improves operational efficiency, reduces errors, and provides real-time business insights."
      },
      {
        q: "Why do trading businesses need ERP software?",
        a: "Trading businesses deal with complex operations like multi-location inventory, GST billing, and order management. Traders ERP Software automates these processes, reduces manual errors, improves stock visibility, and helps increase profitability."
      },
      {
        q: "Is Mx-ERP suitable for small and large trading businesses?",
        a: "Yes, Mx-ERP by ACGIL is highly scalable and designed for both small traders and large enterprises. It adapts to your business size and grows with your operations without performance issues."
      },
      {
        q: "Does Traders ERP Software support GST billing and compliance?",
        a: "Yes, Traders ERP Software includes built-in GST features such as automated billing, tax calculation, return filing reports, and compliance management as per Indian regulations."
      },
      {
        q: "Can Traders ERP manage multiple warehouses and branches?",
        a: "Absolutely. Traders ERP Software provides real-time tracking of inventory across multiple warehouses, branches, and locations, ensuring accurate stock management and faster decision-making."
      },
      {
        q: "How does Traders ERP Software improve inventory management?",
        a: "It offers real-time inventory tracking, auto stock alerts, batch and serial number tracking, and demand forecasting to prevent overstocking and stockouts."
      },
      {
        q: "What are the key features of Traders ERP Software?",
        a: "Key features include Inventory Management, Sales & Distribution, Procurement & Vendor Management, Finance & GST Compliance, Warehouse & Logistics, and MIS & Business Intelligence."
      },
      {
        q: "How does Traders ERP Software help increase profits?",
        a: "By automating operations, reducing errors, optimizing inventory, and providing real-time insights, Traders ERP Software helps businesses reduce costs and improve overall profitability."
      },
      {
        q: "Is Traders ERP Software cloud-based or on-premise?",
        a: "Mx-ERP offers both cloud-based and on-premise deployment, giving businesses flexibility based on their needs."
      }
    ],
    industriesServed: [
      "Wholesale & Distribution",
      "FMCG & Consumer Goods",
      "Pharmaceutical Distribution",
      "Electronics & Hardware",
      "Building Materials & Construction",
      "Textile & Apparel",
      "Food & Beverage Trading"
    ],
    comparison: [
      {
        feature: "Implementation",
        mxerp: "4–6 Weeks",
        others: "6–12 Months"
      },
      {
        feature: "Cost",
        mxerp: "Low",
        others: "High"
      },
      {
        feature: "Customization",
        mxerp: "High",
        others: "Limited"
      },
      {
        feature: "GST ",
        mxerp: "Built-in",
        others: "Add-on"
      },
      {
        feature: "Industry Focus",
        mxerp: "Trading-Specific",
        others: "Generic"
      },
    ],
    seo: {
      title: "Traders ERP Software | ERP Software for Trading Industry - Acgil",
      description: "Streamline trading operations with ACGIL’s Traders ERP Software, designed to manage inventory, sales, purchases, finance, orders, and day-to-day trading business processes.",
      keywords: "ERP software for trading industry, trading ERP software India, inventory management ERP, sales distribution ERP, GST ERP software",
      canonical: SITE_URL + "/products/traders-erp-software",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      ogTitle: "Traders ERP Software | ERP Software for Trading Industry - Acgil",
      ogDescription: "Streamline trading operations with ACGIL’s Traders ERP Software, designed to manage inventory, sales, purchases, finance, orders, and day-to-day trading business processes.",
      ogUrl: SITE_URL + "/products/traders-erp-software",
      ogImage: SITE_URL + "/images/mx-erp-dashboard.webp",
      hreflang: {
        "en-in": SITE_URL + "/products/traders-erp-software",
        "x-default": SITE_URL + "/products/traders-erp-software"
      }
    }
  },
  {
    id: "project-management",
    slug: "project-management-erp-software",
    category: "mx-construction",
    name: "Project Management ERP",
    tagline: "Built for Project-Driven Businesses. Engineered for On-Time Delivery.",
    image: "/images/products/mx-erp-project.webp",
    href: "/products/project-management-erp-software",
    badge: "Most Popular",
    color: "#2C78B9",
    family: "Mx-ERP",
    keyWord: "Built",
    accentColor: "#2C78B9",
    trustCount: "1000+",
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
     headlineThin: "Project Management ERP",
     capHeadline: "Manage Projects Smarter with Mx-ERP",
     headlineBold: "Project Management ERP Software",
    headlineAccent: "Mx-ERP by ACGIL",
    subheadline:
      "Mx-ERP by ACGIL is a powerful Project Management ERP Software designed to give you complete control over project planning, execution, budgeting, billing, and resource management, all in one unified platform.",
    dashHeading:
      "Manage your entire project lifecycle from a single dashboard. Mx-ERP delivers real-time visibility, better resource allocation, faster billing, and complete control across planning, execution, procurement, and finance.",
    PainPointsSubheading:
      "Project-driven businesses deal with operational complexities that directly impact timelines, costs, and profitability.",
    painPoints: [
      { title: "Project Delays", desc: "Missed deadlines due to poor planning and zero real-time tracking." },
      { title: "Budget Overruns", desc: "Cost leakage and uncontrolled expenses eating into project margins." },
      { title: "Poor Resource Allocation", desc: "Workforce and equipment mismanagement leading to idle capacity." },
      { title: "Zero Visibility", desc: "No centralized view across multiple projects and teams." },
      { title: "Manual Billing", desc: "Delayed invoicing and revenue recognition due to manual processes." },
    ],
    solutionHeadline: "How Project Management ERP Software Solves These Challenges",
    description:
      "Mx-ERP connects all your project processes into one intelligent system.",
    solutionFeatures: [
      "Real-time project tracking across all stages",
      "Automated project workflows to eliminate delays",
      "Centralized data and reporting for better decisions",
      "Better cost and budget control with live alerts",
    ],
    capdescription:
      "Advanced ERP modules designed to streamline project operations and maximize profitability.",
    capabilities: [
      {
        title: "Project Planning & Scheduling",
        description:
          "Create detailed project timelines and track milestones with Gantt chart visibility and task dependency management.",
        features: [
          "Project timeline creation",
          "Milestone tracking",
          "Task dependencies",
          "Gantt chart visibility",
        ],
        tag: "Planning",
        widget: "planning",
        accentColor: "#2C78B9",
        secondaryColor: "#059A8F",
      },
      {
        title: "Budgeting & Cost Control",
        description:
          "Track project costs in real time with budget vs actual analysis and automated cost overrun alerts.",
        features: [
          "Project cost estimation",
          "Budget vs actual tracking",
          "Expense monitoring",
          "Cost control alerts",
        ],
        tag: "Finance",
        widget: "finance",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9",
      },
      {
        title: "Resource Management",
        description:
          "Optimize workforce and equipment utilization across all active projects with productivity monitoring.",
        features: [
          "Workforce allocation",
          "Equipment tracking",
          "Productivity monitoring",
          "Resource optimization",
        ],
        tag: "Resources",
        widget: "hr",
        accentColor: "#7F77DD",
        secondaryColor: "#059A8F",
      },
      {
        title: "Billing & Revenue Management",
        description:
          "Automate RA billing, milestone-based invoicing, and payment follow-ups for faster revenue realization.",
        features: [
          "RA billing & milestone billing",
          "Automated invoicing",
          "Revenue tracking",
          "Payment follow-ups",
        ],
        tag: "Billing",
        widget: "billing",
        accentColor: "#059A8F",
        secondaryColor: "#7F77DD",
      },
      {
        title: "Procurement & Inventory",
        description:
          "Manage material requirements, automate purchase orders, and track inventory across all project sites.",
        features: [
          "Material requirement planning",
          "Purchase order automation",
          "Inventory tracking across sites",
          "Vendor coordination",
        ],
        tag: "Procurement",
        widget: "vendor",
        accentColor: "#2C78B9",
        secondaryColor: "#7F77DD",
      },
      {
        title: "MIS & Business Intelligence",
        description:
          "Make faster decisions with project profitability reports, KPI dashboards, and multi-project analytics.",
        features: [
          "Project profitability reports",
          "KPI dashboards",
          "Real-time insights",
          "Multi-project analytics",
        ],
        tag: "Analytics",
        widget: "mis",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9",
      },
    ],
    dashboardType: "project-management",
    businessImpact: [
      {
        value: "35%",
        label: "Project Efficiency",
        description: "Improve delivery performance with optimized scheduling",
        color: "#00F2FE",
        icon: "TrendingUp",
      },
      {
        value: "30%",
        label: "Cost Overruns",
        description: "Reduce budget leakage with real-time cost tracking",
        color: "#7F77DD",
        icon: "TrendingDown",
      },
      {
        value: "40%",
        label: "Visibility",
        description: "Gain complete real-time visibility across all projects",
        color: "#F59E0B",
        icon: "Eye",
      },
      {
        value: "2X",
        label: "Faster Decisions",
        description: "Accelerate decision-making with live project insights",
        color: "#EC4899",
        icon: "Zap",
      },
    ],
    workflow: [
      { id: 1, title: "Project Planning", step: "Step 1", icon: "order", color: "#2C78B9", bgColor: "bg-indigo-500", shadow: "shadow-indigo-500/20" },
      { id: 2, title: "Resource Allocation", step: "Step 2", icon: "production", color: "#2C78B9", bgColor: "bg-purple-500", shadow: "shadow-purple-500/20" },
      { id: 3, title: "Execution", step: "Step 3", icon: "inventory", color: "#2C78B9", bgColor: "bg-blue-500", shadow: "shadow-blue-500/20" },
      { id: 4, title: "Billing", step: "Step 4", icon: "finance", color: "#2C78B9", bgColor: "bg-emerald-500", shadow: "shadow-emerald-500/20" },
      { id: 5, title: "Payment", step: "Step 5", icon: "finance", color: "#2C78B9", bgColor: "bg-teal-500", shadow: "shadow-teal-500/20" },
      { id: 6, title: "Accounting", step: "Step 6", icon: "finance", color: "#2C78B9", bgColor: "bg-amber-500", shadow: "shadow-amber-500/20" },
    ],
    features: [
      { id: "planning", title: "Project Planning", description: "Gantt-based scheduling with milestone and task dependency tracking." },
      { id: "budgeting", title: "Budgeting & Cost Control", description: "Real-time budget vs actual with cost overrun alerts." },
      { id: "resources", title: "Resource Management", description: "Workforce and equipment allocation with productivity monitoring." },
      { id: "billing", title: "Billing & Revenue", description: "RA billing, milestone invoicing, and automated payment follow-ups." },
      { id: "procurement", title: "Procurement & Inventory", description: "MRP-driven PO automation and multi-site inventory tracking." },
      { id: "analytics", title: "MIS & Analytics", description: "Project profitability reports and multi-project KPI dashboards." },
    ],
    headline: "Your Entire Project Lifecycle,",
    heroStats: [
      { label: "Active Clients", value: "1000+" },
      { label: "Years of Trust", value: "30+" },
      { label: "GST Ready", value: "100%" },
    ],
    mockupImage: "/images/products/project-hero.png",
    solutions: [
      { title: "Real-time project tracking", desc: "Complete visibility across milestones, tasks, and teams." },
      { title: "Automated billing workflows", desc: "RA and milestone billing without manual effort." },
      { title: "Centralized cost control", desc: "Live budget vs actual alerts to prevent overruns." },
      { title: "Smart resource allocation", desc: "Optimize workforce and equipment across projects." },
      { title: "Multi-project analytics", desc: "Profitability and performance insights at a glance." },
    ],
    roiEstimate: "₹8–15 lakhs/year",
    implementationSteps: [
      {
        num: "01",
        title: "Process Audit",
        description:
          "Our experts map your existing project workflows, identify bottlenecks, and define clear KPIs for the ERP transition.",
        bullets: ["Workflow mapping", "KPI definition", "Gap analysis"],
        image: "/images/products/audit.png",
        bg: "linear-gradient(135deg, #071428, #0d3060)",
      },
      {
        num: "02",
        title: "Seamless Integration",
        description:
          "We configure Mx-ERP modules to fit your specific project structures, billing cycles, and compliance requirements with 100% data accuracy from day one.",
        bullets: ["Project configuration", "Billing integration", "Data migration"],
        image: "/images/products/integration.png",
        bg: "linear-gradient(135deg, #071e1c, #0a3530)",
      },
      {
        num: "03",
        title: "Go-Live & Support",
        description:
          "Dedicated training for your team followed by on-site support during the first month to ensure zero downtime.",
        bullets: ["Staff training", "On-site support", "Hypercare period"],
        image: "/images/products/support.png",
        bg: "linear-gradient(135deg, #100d28, #1a1040)",
      },
    ],
    clients: [
      { id: "pm1", name: "ACMA Projects", logo: "/images/clients/acma-logo.webp" },
      { id: "pm2", name: "AHPL Engineering", logo: "/images/clients/ahpl-logo.webp" },
      { id: "pm3", name: "AIIMS Infrastructure", logo: "/images/clients/aiims-logo.webp" },
      { id: "pm4", name: "Allahabad Builders", logo: "/images/clients/allahabad-bank-logo.webp" },
      { id: "pm5", name: "ANT Construction", logo: "/images/clients/ant-logo.webp" },
      { id: "pm6", name: "AOS Developers", logo: "/images/clients/aos-logo.webp" },
      { id: "pm7", name: "Arogyavaram EPC", logo: "/images/clients/arogyavaram-logo.webp" },
      { id: "pm8", name: "Arora Engineering", logo: "/images/clients/arora-aromatics-logo.webp" },
    ],
    industry: "Project Management",
    heroImage: "/images/products/project-hero.png",
    marqueeItems: [
      "GST Compliant",
      "Gantt Scheduling",
      "RA Billing",
      "Multi-Project",
      "Cloud Security",
      "ISO Certified",
    ],
    testimonials: [
      {
        id: 301,
        name: "Suresh Mehta",
        role: "Project Director",
        company: "Mehta Engineering Works",
        avatar: "/images/partners/1.png",
        quote:
          "ACGIL Project ERP gave us complete control over our 8 simultaneous projects. Budget overruns reduced by 30% and our billing cycle shortened from 3 weeks to 3 days.",
        rating: 5,
      },
      {
        id: 302,
        name: "Anjali Rajan",
        role: "CFO",
        company: "Rajan Infrastructure Pvt Ltd",
        avatar: "/images/partners/2.webp",
        quote:
          "The automated RA billing and milestone invoicing transformed our cash flow. We now recover payments 40% faster than before.",
        rating: 5,
      },
      {
        id: 303,
        name: "Vikram Nair",
        role: "Operations Head",
        company: "NairTech EPC",
        avatar: "/images/partners/3.webp",
        quote:
          "Resource allocation across multiple sites was a nightmare before ACGIL. Now we track every employee and machine in real time with zero conflicts.",
        rating: 5,
      },
      {
        id: 304,
        name: "Priya Reddy",
        role: "PMO Head",
        company: "Reddy Projects",
        avatar: "/images/partners/4.webp",
        quote:
          "The multi-project dashboard is exceptional. We manage 15 active projects from one screen — on budget, on time, every time.",
        rating: 5,
      },
      {
        id: 305,
        name: "Amit Joshi",
        role: "CEO",
        company: "Joshi Constructions",
        avatar: "/images/partners/5.webp",
        quote:
          "Implementing Mx-ERP was the best business decision we made. Project efficiency improved by 35% and our client satisfaction scores have never been higher.",
        rating: 5,
      },
    ],
    integrations: [
      { name: "Tally Prime", icon: "calculator", category: "Accounting" },
      { name: "Razorpay", icon: "credit-card", category: "Payments" },
      { name: "GSTN Portal", icon: "file-text", category: "Government" },
      { name: "E-way Bill", icon: "truck", category: "Government" },
      { name: "MS Project", icon: "calendar", category: "Planning" },
      { name: "Zoho Books", icon: "book-open", category: "Accounting" },
      { name: "WhatsApp Business", icon: "message-square", category: "Communication" },
      { name: "E-Invoice", icon: "file-digit", category: "Government" },
    ],
    faqs: [
      {
        q: "What is Project Management ERP Software?",
        a: "Project Management ERP Software is an integrated system that helps businesses manage project planning, execution, budgeting, resources, billing, and reporting in one platform.",
      },
      {
        q: "Who should use Project ERP Software?",
        a: "Construction companies, EPC contractors, engineering firms, and any project-based organizations benefit from using project ERP software.",
      },
      {
        q: "Does Mx-ERP support billing features?",
        a: "Yes, Mx-ERP supports RA billing, milestone billing, automated invoicing, and revenue tracking.",
      },
      {
        q: "Can it manage multiple projects simultaneously?",
        a: "Yes, Mx-ERP allows businesses to manage multiple projects with real-time tracking and centralized dashboards.",
      },
      {
        q: "How does Project ERP improve project efficiency?",
        a: "It automates workflows, improves resource utilization, reduces delays, and provides real-time project insights.",
      },
      {
        q: "Does it help control project costs?",
        a: "Yes, it provides budget tracking, cost monitoring, and alerts to prevent cost overruns.",
      },
      {
        q: "Is Project Management ERP suitable for small businesses?",
        a: "Yes, Mx-ERP is scalable and works for small, medium, and large project-based businesses.",
      },
      {
        q: "Is it cloud-based or on-premise?",
        a: "Mx-ERP offers both cloud-based and on-premise deployment options.",
      },
    ],
    industriesServed: [
      "EPC & Infrastructure Companies",
      "Civil & Structural Engineering",
      "IT & Software Projects",
      "Oil, Gas & Energy Projects",
      "Industrial Plant Engineering",
      "Interior Design & Fit-out",
      "Government & Public Works",
    ],
    comparison: [
      { feature: "Time", mxerp: "4–6 Weeks", others: "6–12 Months" },
      { feature: "Cost", mxerp: "Low", others: "High" },
      { feature: "Customization", mxerp: "High", others: "Limited" },
      { feature: "Billing", mxerp: "Built-in (RA/Milestone)", others: "Add-on" },
      { feature: "Industry Focus", mxerp: "Project-Based", others: "Generic" },
    ],
    seo: {
      title: "Best Cloud-Based Project Management ERP Software | ACGIL",
      description: "Looking for the best ERP for project management? ACGIL's cloud-based ERP simplifies budgeting, billing, scheduling, and project tracking. Book a demo today.",
      keywords:
        "project management erp software, project erp system, construction project management software, erp for project based companies, project billing software, resource management erp, cloud project erp",
      canonical: SITE_URL + "/products/project-management-erp-software",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      ogTitle: "Best Cloud-Based Project Management ERP Software | ACGIL",
      ogDescription: "Looking for the best ERP for project management? ACGIL's cloud-based ERP simplifies budgeting, billing, scheduling, and project tracking. Book a demo today.",
      ogUrl: SITE_URL + "/products/project-management-erp-software",
      ogImage: SITE_URL + "/images/project-management-erp.jpg",
      hreflang: {
        en: SITE_URL + "/products/project-management-erp-software",
        "x-default": SITE_URL + "/products/project-management-erp-software",
      },
      faqSchema: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "What is Project Management ERP Software?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Project Management ERP Software helps businesses manage planning, budgeting, execution, billing, and resources in one centralized system.",
            },
          },
          {
            "@type": "Question",
            name: "Who should use Project ERP Software?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Construction companies, EPC firms, engineering companies, and project-based businesses benefit from it.",
            },
          },
          {
            "@type": "Question",
            name: "Does Mx-ERP support billing?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, it supports RA billing, milestone billing, and automated invoicing.",
            },
          },
          {
            "@type": "Question",
            name: "Can it manage multiple projects?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, it provides real-time multi-project tracking and centralized dashboards.",
            },
          },
          {
            "@type": "Question",
            name: "Is it scalable?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, Mx-ERP is scalable for small, medium, and large businesses.",
            },
          },
        ],
      },
    },
  },
  {
    id: "tender-management",
    slug: "tender-management-software",
    category: "mx-construction",
    name: "Tender Management",
    tagline: "Built for Winning Bids. Engineered for Profitable Execution.",
    image: "/images/products/mx-erp-tender.webp",
    href: "/products/tender-management-software",
    badge: "New Launch",
    color: "#B45309",
    family: "Mx-ERP",
    keyWord: "Built",
    accentColor: "#B45309",
    trustCount: "1000+",
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    headlineThin: "Tender Management ERP",
    capHeadline: "Win More Tenders with Mx-ERP",
    headlineBold: "Tender Management Software for EPC & Construction",
    headlineAccent: "Mx-ERP by ACGIL",
    subheadline:
      "Mx-ERP by ACGIL is a powerful Tender Management Software that automates tender discovery, document analysis, bid qualification, cost estimation, and approvals — connecting every bid straight to project execution.",
    dashHeading:
      "Manage your entire tender pipeline from a single dashboard. Mx-ERP delivers automated discovery, intelligent estimates, faster approvals, and complete control from opportunity to project handover.",
    PainPointsSubheading:
      "EPC and infrastructure businesses lose profitable bids to manual tendering chaos that directly impacts growth and margins.",
    painPoints: [
      { title: "Portal Chaos", desc: "Tracking multiple tender portals manually leads to missed opportunities and last-minute rushes." },
      { title: "Deadline Slips", desc: "Missed corrigenda, extensions, and submission dates cause outright disqualification." },
      { title: "Pricing Errors", desc: "Manual BOQ estimates and guesswork margins erode bid accuracy and profitability." },
      { title: "Compliance Risk", desc: "Overlooked EMD, eligibility, and documentation clauses invite rejection and blacklisting risk." },
      { title: "Slow Approvals", desc: "Email-based evaluations and unclear ownership delay Go/No-Go decisions past the window." },
    ],
    solutionHeadline: "How Intelligent Tender Management Software Solves These Challenges",
    description:
      "Mx-ERP streamlines the tender lifecycle through automated opportunity tracking, document analysis, bid qualification, cost estimation, approval workflows, and tender-to-project integration.",
    solutionFeatures: [
      "Automated tender discovery and deadline tracking",
      "Intelligent document, BOQ, and cost analysis",
      "Scored bid qualification with Go/No-Go decisions",
      "Tender-to-project continuity for cost control",
    ],
    capdescription:
      "Advanced ERP modules designed to win profitable tenders and convert bids into controlled projects.",
    capabilities: [
      {
        title: "Automated Tender Discovery & Tracking",
        description:
          "Consolidate relevant tender opportunities from your configured portals and data sources into one trackable pipeline.",
        features: [
          "Consolidated tender pipeline",
          "Filter by industry, location & value",
          "Deadline, corrigenda & status tracking",
          "Automated alerts & reminders",
        ],
        tag: "Discovery",
        widget: "planning",
        accentColor: "#B45309",
        secondaryColor: "#059A8F",
      },
      {
        title: "Intelligent Document Analysis",
        description:
          "Extract key commercial and technical parameters from tender documents, BOQs, and eligibility conditions in minutes.",
        features: [
          "BOQ & specification extraction",
          "EMD, fees & criteria detection",
          "Structured tender summaries",
          "Risk & gap highlighting",
        ],
        tag: "Analysis",
        widget: "inventory",
        accentColor: "#059A8F",
        secondaryColor: "#B45309",
      },
      {
        title: "Bid Qualification & Go/No-Go",
        description:
          "Score every opportunity against your credentials, financials, experience, and bandwidth before committing resources.",
        features: [
          "Eligibility vs credential check",
          "Qualification gap detection",
          "Configurable scoring rules",
          "Structured Go/No-Go evaluation",
        ],
        tag: "Qualification",
        widget: "vendor",
        accentColor: "#2C78B9",
        secondaryColor: "#B45309",
      },
      {
        title: "BOQ & Cost Estimation",
        description:
          "Build ERP-backed estimates from structured BOQs, historical rates, overheads, and margins — approved before you bid.",
        features: [
          "Structured BOQ estimation",
          "Historical rate application",
          "Overheads, contingency & margins",
          "Budget & profitability checks",
        ],
        tag: "Estimation",
        widget: "billing",
        accentColor: "#B45309",
        secondaryColor: "#2C78B9",
      },
      {
        title: "Compliance, Documents & Deadline Control",
        description:
          "Run credentials, checklists, approvals, and escalations from one control tower so nothing mandatory is ever missed.",
        features: [
          "Credential & expiry tracking",
          "Tender checklists & readiness",
          "Approval hierarchies & escalations",
          "Central status dashboard",
        ],
        tag: "Compliance",
        widget: "hr",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9",
      },
      {
        title: "Tender-to-Project Integration",
        description:
          "Convert winning bids into live projects with BOQ, budgets, and commercial assumptions carried over automatically.",
        features: [
          "One-click bid-to-project conversion",
          "BOQ & budget carryover",
          "Procurement & billing linkage",
          "Estimated vs actual analytics",
        ],
        tag: "Handover",
        widget: "mis",
        accentColor: "#2C78B9",
        secondaryColor: "#B45309",
      },
    ],
    dashboardType: "project-management",
    businessImpact: [
      {
        value: "50%",
        label: "Faster Bid Cycle",
        description: "Shrink discovery-to-submission time with automation",
        color: "#00F2FE",
        icon: "Zap",
      },
      {
        value: "40%",
        label: "Less Manual Effort",
        description: "Eliminate repetitive tracking and data entry",
        color: "#7F77DD",
        icon: "TrendingDown",
      },
      {
        value: "2X",
        label: "More Bids Evaluated",
        description: "Assess twice the opportunities with the same team",
        color: "#F59E0B",
        icon: "Eye",
      },
      {
        value: "100%",
        label: "Deadline Compliance",
        description: "Never miss a submission date or mandatory clause",
        color: "#EC4899",
        icon: "TrendingUp",
      },
    ],
    workflow: [
      { id: 1, title: "Tender Discovery", step: "Step 1", icon: "order", color: "#B45309", bgColor: "bg-indigo-500", shadow: "shadow-indigo-500/20" },
      { id: 2, title: "Document Analysis", step: "Step 2", icon: "survey", color: "#B45309", bgColor: "bg-purple-500", shadow: "shadow-purple-500/20" },
      { id: 3, title: "Qualification", step: "Step 3", icon: "contract", color: "#B45309", bgColor: "bg-blue-500", shadow: "shadow-blue-500/20" },
      { id: 4, title: "Cost Estimation", step: "Step 4", icon: "planning", color: "#B45309", bgColor: "bg-emerald-500", shadow: "shadow-emerald-500/20" },
      { id: 5, title: "Approvals", step: "Step 5", icon: "finance", color: "#B45309", bgColor: "bg-teal-500", shadow: "shadow-teal-500/20" },
      { id: 6, title: "Project Handover", step: "Step 6", icon: "production", color: "#B45309", bgColor: "bg-amber-500", shadow: "shadow-amber-500/20" },
      { id: 7, title: "Billing & Control", step: "Step 7", icon: "billing", color: "#B45309", bgColor: "bg-rose-500", shadow: "shadow-rose-500/20" },
    ],
    features: [
      { id: "discovery", title: "Tender Discovery", description: "Consolidated pipeline with filters, tracking, and deadline alerts." },
      { id: "analysis", title: "Document Intelligence", description: "BOQ extraction with risks and gaps highlighted for review." },
      { id: "qualification", title: "Go/No-Go Scoring", description: "Configurable qualification scoring for confident bid decisions." },
      { id: "estimation", title: "Cost Estimation", description: "ERP-backed estimates with margins checked before approval." },
      { id: "compliance", title: "Compliance Control", description: "Credentials, checklists, approvals, and escalations in one place." },
      { id: "handover", title: "Project Handover", description: "Winning bids convert to controlled projects with cost continuity." },
    ],
    headline: "Your Entire Tender Pipeline,",
    heroStats: [
      { label: "Faster Bid Cycle", value: "50%" },
      { label: "More Bids Evaluated", value: "2X" },
      { label: "Deadline Compliance", value: "100%" },
    ],
    mockupImage: "/images/products/project-hero.png",
    solutions: [
      { title: "Automated discovery", desc: "Every relevant opportunity tracked in one pipeline, nothing missed." },
      { title: "Intelligent summaries", desc: "Structured tender briefs in minutes instead of days of reading." },
      { title: "Scored Go/No-Go", desc: "Pursue only bids you can win and execute profitably." },
      { title: "ERP-based estimates", desc: "Real cost data behind every bid price and margin." },
      { title: "Tender-to-project continuity", desc: "Estimates flow into execution, billing, and profitability tracking." },
    ],
    roiEstimate: "₹8–15 lakhs/year",
    implementationSteps: [
      {
        num: "01",
        title: "Discovery Setup",
        description:
          "We configure your tender sources, filters, alerts, and document templates to match how your bid team actually works.",
        bullets: ["Source configuration", "Filter rules", "Alert setup"],
        image: "/images/products/audit.png",
        bg: "linear-gradient(135deg, #071428, #0d3060)",
      },
      {
        num: "02",
        title: "Estimation & Approvals",
        description:
          "We map BOQ items to Mx-ERP cost heads, set pricing rules and margins, and build your technical-commercial approval matrix.",
        bullets: ["BOQ mapping", "Pricing rules", "Approval matrix"],
        image: "/images/products/integration.png",
        bg: "linear-gradient(135deg, #071e1c, #0a3530)",
      },
      {
        num: "03",
        title: "Go-Live & Support",
        description:
          "Dedicated training for your estimation and contracts team, followed by on-site support through your first live bids.",
        bullets: ["Staff training", "On-site support", "Hypercare period"],
        image: "/images/products/support.png",
        bg: "linear-gradient(135deg, #100d28, #1a1040)",
      },
    ],
    clients: [
      { id: "tm1", name: "ACMA Infra", logo: "/images/clients/acma-logo.webp" },
      { id: "tm2", name: "AHPL EPC", logo: "/images/clients/ahpl-logo.webp" },
      { id: "tm3", name: "AIIMS Projects", logo: "/images/clients/aiims-logo.webp" },
      { id: "tm4", name: "Allahabad Contractors", logo: "/images/clients/allahabad-bank-logo.webp" },
      { id: "tm5", name: "ANT Builders", logo: "/images/clients/ant-logo.webp" },
      { id: "tm6", name: "AOS Infra", logo: "/images/clients/aos-logo.webp" },
      { id: "tm7", name: "Arogyavaram EPC", logo: "/images/clients/arogyavaram-logo.webp" },
      { id: "tm8", name: "Arora Projects", logo: "/images/clients/arora-aromatics-logo.webp" },
    ],
    industry: "Construction & EPC",
    heroImage: "/images/products/project-hero.png",
    marqueeItems: [
      "Tender Discovery",
      "BOQ Estimation",
      "Go/No-Go Scoring",
      "RA Billing",
      "Cloud Security",
      "ISO Certified",
    ],
    testimonials: [
      {
        id: 501,
        name: "Rajesh Malhotra",
        role: "VP – Bids & Contracts",
        company: "Malhotra Infra Ltd",
        avatar: "/images/partners/1.png",
        quote:
          "We stopped missing tenders entirely. The discovery pipeline plus deadline escalations took our submission rate from 60% to nearly 100% in one quarter.",
        rating: 5,
      },
      {
        id: 502,
        name: "Sunita Deshmukh",
        role: "Tender Head",
        company: "Deshmukh EPC",
        avatar: "/images/partners/2.webp",
        quote:
          "Document summaries save my team two days on every major bid. Qualification gaps surface before we spend a rupee on estimation.",
        rating: 5,
      },
      {
        id: 503,
        name: "Amit Kulkarni",
        role: "Estimation Manager",
        company: "Kulkarni Constructions",
        avatar: "/images/partners/3.webp",
        quote:
          "ERP-backed rates ended our margin surprises. Estimated versus actual now converges bid after bid — our pricing confidence is completely different.",
        rating: 5,
      },
      {
        id: 504,
        name: "Prakash Iyer",
        role: "Director",
        company: "Iyer Infrastructure",
        avatar: "/images/partners/4.webp",
        quote:
          "Go/No-Go scoring stopped us from chasing three loss-making tenders this year alone. That single discipline paid for the implementation.",
        rating: 5,
      },
      {
        id: 505,
        name: "Neha Agarwal",
        role: "CEO",
        company: "Agarwal Buildcon",
        avatar: "/images/partners/5.webp",
        quote:
          "Winning bids flow straight into execution with budgets intact. Tender-to-project continuity is the feature our auditors praise the most.",
        rating: 5,
      },
    ],
    integrations: [
      { name: "Tender Portals", icon: "cloud", category: "Sourcing" },
      { name: "DSC / e-Sign", icon: "briefcase", category: "Compliance" },
      { name: "Tally Prime", icon: "layers", category: "Accounting" },
      { name: "WhatsApp Business", icon: "message-square", category: "Communication" },
      { name: "MS Project", icon: "activity", category: "Planning" },
      { name: "E-Invoice", icon: "server", category: "Government" },
      { name: "Document Vault", icon: "hard-drive", category: "Storage" },
      { name: "Zoho Books", icon: "database", category: "Accounting" },
    ],
    faqs: [
      {
        q: "What is Tender Management Software?",
        a: "Tender Management Software helps EPC and construction businesses discover opportunities, analyze documents, qualify bids, estimate costs, manage approvals, and convert wins into projects — all in one system.",
      },
      {
        q: "Does it automatically download tenders from all portals?",
        a: "It consolidates opportunities from your configured portals and data sources. Coverage depends on portal accessibility, document formats, and your configured business rules.",
      },
      {
        q: "How does BOQ cost estimation work?",
        a: "BOQ items are extracted, structured, and mapped to Mx-ERP materials, labour, and cost heads, using historical rates to compute costs, overheads, contingencies, and bid margins.",
      },
      {
        q: "What is Go/No-Go analysis?",
        a: "Go/No-Go scoring compares tender eligibility against your credentials, financials, and bandwidth using configurable rules, so management bids only on winnable, profitable work.",
      },
      {
        q: "Does it track compliance documents and expiries?",
        a: "Yes, it maintains a central repository with validity and expiry reminders, tender-specific checklists, and full version control.",
      },
      {
        q: "What happens after winning a bid?",
        a: "Winning bids convert into Mx-ERP project records with BOQ quantities, budgets, and commercial assumptions carried into planning, procurement, and RA billing.",
      },
      {
        q: "Is it suitable for small contractors?",
        a: "Yes, Mx-ERP Tender Management scales from single-team contractors to large EPC groups.",
      },
      {
        q: "Is AI analysis reliable for final submission?",
        a: "AI-generated analysis and recommendations accelerate review but should always be validated by authorised personnel before final bid submission.",
      },
    ],
    industriesServed: [
      "EPC Contractors",
      "Roads & Infrastructure Builders",
      "Oil, Gas & Energy Projects",
      "Industrial Plant Contractors",
      "Government & PSU Contractors",
      "Real Estate Developers",
      "MEP Contractors",
    ],
    comparison: [
      { feature: "Time", mxerp: "4–6 Weeks", others: "6–12 Months" },
      { feature: "Cost", mxerp: "Low", others: "High" },
      { feature: "Customization", mxerp: "High", others: "Limited" },
      { feature: "Bid Handover", mxerp: "Built-in (Tender-to-Project)", others: "Manual" },
      { feature: "Industry Focus", mxerp: "EPC Tendering", others: "Generic" },
    ],
    seo: {
      title: "Tender Management Software for EPC & Construction | ACGIL",
      description: "ACGIL's tender management software automates tender discovery, BOQ estimation, bid qualification & approvals for EPC contractors. Book a free demo.",
      keywords:
        "tender management software, epc tender management, construction tender software, bid management software, boq estimation software, tender cost estimation, go no-go analysis, tender tracking software",
      canonical: SITE_URL + "/products/tender-management-software",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      ogTitle: "Tender Management Software for EPC & Construction | ACGIL",
      ogDescription: "ACGIL's tender management software automates tender discovery, BOQ estimation, bid qualification & approvals for EPC contractors. Book a free demo.",
      ogUrl: SITE_URL + "/products/tender-management-software",
      ogImage: SITE_URL + "/images/tender-management-erp.jpg",
      hreflang: {
        en: SITE_URL + "/products/tender-management-software",
        "x-default": SITE_URL + "/products/tender-management-software",
      },
      faqSchema: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "What is Tender Management Software?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Tender Management Software helps EPC businesses discover, qualify, estimate, approve, and convert tenders into projects in one system.",
            },
          },
          {
            "@type": "Question",
            name: "Does it work with all tender portals?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "It works with configured portals and data sources; coverage depends on portal accessibility and formats.",
            },
          },
          {
            "@type": "Question",
            name: "How does Go/No-Go scoring work?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Configurable rules compare eligibility against credentials, financials, and bandwidth for a structured decision.",
            },
          },
          {
            "@type": "Question",
            name: "Does it integrate with project execution?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, winning bids convert to Mx-ERP projects with budgets, procurement, and RA billing linked.",
            },
          },
          {
            "@type": "Question",
            name: "Is it scalable?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, it scales from single-team contractors to large EPC groups.",
            },
          },
        ],
      },
    },
  },
  {
    id: "ra-billing-software",
    slug: "ra-billing-erp-software",
    category: "mx-construction",
    name: "RA Billing Software",
    tagline: "Built for Construction & Projects. Engineered for Faster Payments.",
    image: "/images/products/mx-erp-ra-billing.webp",
    href: "/products/ra-billing-erp-software",
    badge: "Most Popular",
    color: "#059A8F",
    family: "Mx-ERP",
    keyWord: "Built",
    accentColor: "#059A8F",
    trustCount: "1000+",
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
     headlineThin: "RA Billing Software",
     capHeadline: "Accelerate RA Billing with Mx-ERP",
     headlineBold: "Running Account Billing Software for Construction & Projects",
    headlineAccent: "Mx-ERP by ACGIL",
    subheadline:
      "Mx-ERP by ACGIL is a powerful RA Billing Software designed to automate running account billing, manage project-based invoicing, and ensure accurate payment tracking — all in one unified platform.",
    dashHeading:
      "Manage project billing, client invoices, work progress, and payments from a single dashboard. Mx-ERP delivers real-time visibility into billing status, approvals, and cash flow.",
    PainPointsSubheading:
      "Construction and project-based businesses face multiple billing challenges that directly impact revenue and cash flow.",
    painPoints: [
      { title: "Manual Billing Errors", desc: "Error-prone manual calculations leading to disputes and revenue leakage." },
      { title: "Invoice Delays", desc: "Slow invoice generation causing payment delays and cash flow pressure." },
      { title: "Progress Tracking Gaps", desc: "Difficulty linking work completion to billing with no real-time visibility." },
      { title: "Payment Disputes", desc: "Lack of billing transparency leading to client disagreements and delays." },
      { title: "Compliance Risk", desc: "Manual GST calculations and non-audit-ready records creating compliance exposure." },
    ],
    solutionHeadline: "How RA Billing Software Solves These Challenges",
    description:
      "Mx-ERP automates and streamlines the entire billing process.",
    solutionFeatures: [
      "Automated RA bill generation based on work progress",
      "Progress-based and BOQ-linked billing",
      "Real-time invoice and payment tracking",
      "Centralized billing system with GST compliance",
    ],
    capdescription:
      "Advanced billing modules designed to eliminate errors, accelerate invoicing, and improve cash flow.",
    capabilities: [
      {
        title: "RA Bill Generation",
        description:
          "Generate running account bills automatically based on work progress and BOQ with multi-level approval workflows.",
        features: [
          "Work-progress-based billing",
          "BOQ-based billing",
          "Automated calculations",
          "Multi-level approvals",
        ],
        tag: "Billing",
        widget: "billing",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9",
      },
      {
        title: "Contract & BOQ Management",
        description:
          "Manage Bill of Quantities, contracts, rate analysis, and variation orders in one centralized system.",
        features: [
          "Bill of Quantity (BOQ) tracking",
          "Contract-based billing",
          "Rate analysis",
          "Variation order management",
        ],
        tag: "Contracts",
        widget: "vendor",
        accentColor: "#2C78B9",
        secondaryColor: "#059A8F",
      },
      {
        title: "Invoice & Payment Tracking",
        description:
          "Track every invoice, payment, and outstanding amount in real time with complete client-wise billing history.",
        features: [
          "Automated invoicing",
          "Payment tracking",
          "Outstanding reports",
          "Client-wise billing history",
        ],
        tag: "Finance",
        widget: "finance",
        accentColor: "#7F77DD",
        secondaryColor: "#059A8F",
      },
      {
        title: "GST & Tax Compliance",
        description:
          "Stay fully compliant with GST-ready billing, automated tax calculations, and audit-ready documentation.",
        features: [
          "GST-ready billing",
          "Tax calculations",
          "Compliance reports",
          "Audit-ready data",
        ],
        tag: "Compliance",
        widget: "gst",
        accentColor: "#059A8F",
        secondaryColor: "#7F77DD",
      },
      {
        title: "Integration with Project Management",
        description:
          "Link billing directly to project progress with site-wise tracking and real-time cost vs billing comparison.",
        features: [
          "Link billing with project progress",
          "Site-wise billing tracking",
          "Cost vs billing comparison",
          "Real-time updates",
        ],
        tag: "Projects",
        widget: "planning",
        accentColor: "#2C78B9",
        secondaryColor: "#7F77DD",
      },
      {
        title: "MIS & Reporting",
        description:
          "Make faster decisions with detailed billing reports, revenue tracking, and profitability dashboards.",
        features: [
          "Billing reports",
          "Revenue tracking",
          "Profitability analysis",
          "Real-time dashboards",
        ],
        tag: "Analytics",
        widget: "mis",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9",
      },
    ],
    dashboardType: "ra-billing",
    businessImpact: [
      {
        value: "50%",
        label: "Billing Errors",
        description: "Reduce manual billing mistakes with automation",
        color: "#00F2FE",
        icon: "TrendingDown",
      },
      {
        value: "40%",
        label: "Faster Invoicing",
        description: "Generate and send invoices in minutes not days",
        color: "#7F77DD",
        icon: "Zap",
      },
      {
        value: "30%",
        label: "Cash Flow",
        description: "Improve collections with real-time payment tracking",
        color: "#F59E0B",
        icon: "TrendingUp",
      },
      {
        value: "2X",
        label: "Faster Payment Cycle",
        description: "Accelerate the entire billing-to-payment timeline",
        color: "#EC4899",
        icon: "Zap",
      },
    ],
    workflow: [
      { id: 1, title: "Work Progress", step: "Step 1", icon: "production", color: "#059A8F", bgColor: "bg-teal-500", shadow: "shadow-teal-500/20" },
      { id: 2, title: "BOQ Linking", step: "Step 2", icon: "inventory", color: "#059A8F", bgColor: "bg-blue-500", shadow: "shadow-blue-500/20" },
      { id: 3, title: "RA Bill", step: "Step 3", icon: "billing", color: "#059A8F", bgColor: "bg-indigo-500", shadow: "shadow-indigo-500/20" },
      { id: 4, title: "Invoice", step: "Step 4", icon: "finance", color: "#059A8F", bgColor: "bg-emerald-500", shadow: "shadow-emerald-500/20" },
      { id: 5, title: "Payment", step: "Step 5", icon: "finance", color: "#059A8F", bgColor: "bg-amber-500", shadow: "shadow-amber-500/20" },
      { id: 6, title: "Accounting", step: "Step 6", icon: "finance", color: "#059A8F", bgColor: "bg-purple-500", shadow: "shadow-purple-500/20" },
    ],
    features: [
      { id: "ra-bill", title: "RA Bill Generation", description: "Work-progress and BOQ-based billing with automated calculations and multi-level approvals." },
      { id: "boq", title: "Contract & BOQ Management", description: "Centralized BOQ tracking, rate analysis, and variation order management." },
      { id: "invoice", title: "Invoice & Payment Tracking", description: "Real-time payment tracking, outstanding reports, and client-wise billing history." },
      { id: "gst", title: "GST & Tax Compliance", description: "GST-ready billing with automated tax calculations and audit-ready documentation." },
      { id: "project-link", title: "Project Integration", description: "Billing linked to project progress with site-wise tracking and cost vs billing comparison." },
      { id: "mis", title: "MIS & Reporting", description: "Billing reports, revenue tracking, and profitability dashboards in real time." },
    ],
    headline: "Your Entire Billing Process,",
    heroStats: [
      { label: "Active Clients", value: "1000+" },
      { label: "Years of Trust", value: "30+" },
      { label: "GST Ready", value: "100%" },
    ],
    mockupImage: "/images/products/ra-billing-hero.png",
    solutions: [
      { title: "Automated RA billing", desc: "Generate running account bills in minutes based on work progress." },
      { title: "BOQ-linked accuracy", desc: "Eliminate calculation errors with BOQ-based automated billing." },
      { title: "Real-time payment tracking", desc: "Know exactly where every invoice stands at all times." },
      { title: "Built-in GST compliance", desc: "Stay audit-ready with automated GST calculations and reports." },
      { title: "Project-billing integration", desc: "Link every bill to site progress for complete billing transparency." },
    ],
    roiEstimate: "₹8–15 lakhs/year",
    implementationSteps: [
      {
        num: "01",
        title: "Process Audit",
        description:
          "Our experts map your existing billing workflows, contract structures, and BOQ formats to design the optimal ERP configuration.",
        bullets: ["Workflow mapping", "BOQ structure review", "Gap analysis"],
        image: "/images/products/audit.png",
        bg: "linear-gradient(135deg, #071428, #0d3060)",
      },
      {
        num: "02",
        title: "Seamless Integration",
        description:
          "We configure RA billing modules, GST settings, and project linkages to fit your exact requirements with 100% data accuracy from day one.",
        bullets: ["BOQ configuration", "GST integration", "Data migration"],
        image: "/images/products/integration.png",
        bg: "linear-gradient(135deg, #071e1c, #0a3530)",
      },
      {
        num: "03",
        title: "Go-Live & Support",
        description:
          "Dedicated training for your billing and finance teams followed by on-site support during the first month to ensure zero downtime.",
        bullets: ["Staff training", "On-site support", "Hypercare period"],
        image: "/images/products/support.png",
        bg: "linear-gradient(135deg, #100d28, #1a1040)",
      },
    ],
    clients: [
      { id: "rb1", name: "ACMA Projects", logo: "/images/clients/acma-logo.webp" },
      { id: "rb2", name: "AHPL Engineering", logo: "/images/clients/ahpl-logo.webp" },
      { id: "rb3", name: "AIIMS Infrastructure", logo: "/images/clients/aiims-logo.webp" },
      { id: "rb4", name: "Allahabad Builders", logo: "/images/clients/allahabad-bank-logo.webp" },
      { id: "rb5", name: "ANT Construction", logo: "/images/clients/ant-logo.webp" },
      { id: "rb6", name: "AOS Developers", logo: "/images/clients/aos-logo.webp" },
      { id: "rb7", name: "Arogyavaram EPC", logo: "/images/clients/arogyavaram-logo.webp" },
      { id: "rb8", name: "Arora Engineering", logo: "/images/clients/arora-aromatics-logo.webp" },
    ],
    industry: "Construction & Projects",
    heroImage: "/images/products/ra-billing-hero.png",
    marqueeItems: [
      "GST Compliant",
      "BOQ Billing",
      "RA Bills",
      "Progress Billing",
      "Cloud Security",
      "ISO Certified",
    ],
    testimonials: [
      {
        id: 306,
        name: "Ramesh Iyer",
        role: "Finance Head",
        company: "Iyer Construction Ltd",
        avatar: "/images/partners/1.png",
        quote:
          "ACGIL RA Billing Software cut our invoicing time by 40%. What used to take a week now takes a few hours. Our payment cycle has improved dramatically.",
        rating: 5,
      },
      {
        id: 307,
        name: "Sunita Verma",
        role: "CFO",
        company: "Verma EPC",
        avatar: "/images/partners/2.webp",
        quote:
          "The BOQ-linked billing eliminated 90% of our billing disputes with clients. Automated calculations mean zero manual errors and complete client confidence.",
        rating: 5,
      },
      {
        id: 308,
        name: "Arvind Patel",
        role: "Project Director",
        company: "Patel Infrastructure",
        avatar: "/images/partners/3.webp",
        quote:
          "Managing RA bills across 12 sites simultaneously was a nightmare before Mx-ERP. Now we have real-time visibility into every bill, approval, and payment.",
        rating: 5,
      },
      {
        id: 309,
        name: "Meena Shah",
        role: "Accounts Manager",
        company: "Shah Contractors",
        avatar: "/images/partners/4.webp",
        quote:
          "The GST compliance features alone saved us 3 days of work every month. Audit-ready reports are generated automatically — no more last-minute scrambles.",
        rating: 5,
      },
      {
        id: 310,
        name: "Deepak Nair",
        role: "MD",
        company: "Nair Civil Works",
        avatar: "/images/partners/5.webp",
        quote:
          "Cash flow improved by 30% in the first quarter. Real-time payment tracking lets us follow up proactively instead of chasing overdue invoices blindly.",
        rating: 5,
      },
    ],
    integrations: [
      { name: "Tally Prime", icon: "calculator", category: "Accounting" },
      { name: "Razorpay", icon: "credit-card", category: "Payments" },
      { name: "GSTN Portal", icon: "file-text", category: "Government" },
      { name: "E-way Bill", icon: "truck", category: "Government" },
      { name: "MS Project", icon: "calendar", category: "Planning" },
      { name: "E-Invoice", icon: "file-digit", category: "Government" },
      { name: "WhatsApp Business", icon: "message-square", category: "Communication" },
      { name: "Zoho Books", icon: "book-open", category: "Accounting" },
    ],
    faqs: [
      {
        q: "What is RA Billing Software?",
        a: "RA Billing Software helps businesses generate invoices based on project progress and completed work using running account billing.",
      },
      {
        q: "Who should use RA Billing Software?",
        a: "Construction companies, contractors, EPC firms, and project-based businesses benefit from RA billing systems.",
      },
      {
        q: "What is BOQ in RA billing?",
        a: "BOQ (Bill of Quantity) is a document that lists project items, quantities, and rates used for billing calculations.",
      },
      {
        q: "Does it support GST billing?",
        a: "Yes, it includes GST billing, tax calculations, and compliance reports.",
      },
      {
        q: "Can it track payments and outstanding invoices?",
        a: "Yes, it provides real-time payment tracking and outstanding reports.",
      },
      {
        q: "Does it integrate with project management?",
        a: "Yes, it integrates with project progress for accurate and transparent billing.",
      },
      {
        q: "Is it suitable for small contractors?",
        a: "Yes, Mx-ERP is scalable for small to large businesses.",
      },
      {
        q: "Is it cloud-based or on-premise?",
        a: "Mx-ERP supports both cloud and on-premise deployment.",
      },
    ],
    industriesServed: [
      "Civil & Structural Contractors",
      "EPC & Infrastructure Companies",
      "Building & Real Estate Developers",
      "MEP & Electrical Contractors",
      "Road & Highway Projects",
      "Industrial Plant Construction",
      "Government & Public Works Contractors",
    ],
    comparison: [
      { feature: "Accuracy", mxerp: "High — Automated", others: "Error-prone" },
      { feature: "Speed", mxerp: "Fast", others: "Slow" },
      { feature: "Automation", mxerp: "Yes", others: "No" },
      { feature: "Tracking", mxerp: "Real-time", others: "Limited" },
      { feature: "GST Compliance", mxerp: "Built-in", others: "Risky/Manual" }
    ],
    seo: {
      title: "RA Bill ERP & Tracking Software for Construction Billing | ACGIL",
      description: "ACGIL's RA bill ERP software automates running account billing for construction. Track RA bills, measurements, and payments easily. Book a free demo.",
      keywords:
        "ra billing software, running account billing software, boq billing software, construction billing software, project billing system, gst billing software, contractor billing software",
      canonical: SITE_URL + "/products/ra-billing-erp-software",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      ogTitle: "RA Bill ERP & Tracking Software for Construction Billing | ACGIL",
      ogDescription: "ACGIL's RA bill ERP software automates running account billing for construction. Track RA bills, measurements, and payments easily. Book a free demo.",
      ogUrl: SITE_URL + "/products/ra-billing-erp-software",
      ogImage: SITE_URL + "/images/ra-billing.jpg",
      hreflang: {
        en: SITE_URL + "/products/ra-billing-erp-software",
        "x-default": SITE_URL + "/products/ra-billing-erp-software",
      },
      faqSchema: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "What is RA Billing Software?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "RA Billing Software helps generate invoices based on project progress and completed work.",
            },
          },
          {
            "@type": "Question",
            name: "What is BOQ billing?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "BOQ billing is based on bill of quantities and rates defined for project work.",
            },
          },
          {
            "@type": "Question",
            name: "Does it support GST?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, it includes GST billing and compliance features.",
            },
          },
          {
            "@type": "Question",
            name: "Can it track payments and outstanding invoices?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, it provides real-time payment tracking and outstanding invoice reports.",
            },
          },
          {
            "@type": "Question",
            name: "Is it scalable for small contractors?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, Mx-ERP is scalable for small, medium, and large project businesses.",
            },
          },
        ],
      },
    },
  },
  {
    id: "contract-management-erp-software",
    slug: "contract-management-erp-software",
    category: "mx-erp",
    name: "Contract Management ERP Software",
    tagline: "Built for Contract-Driven Businesses. Engineered for Full Lifecycle Control.",
    image: "/images/products/mx-erp-contract.webp",
    href: "/products/contract-management-erp-software",
    badge: "Most Popular",
    color: "#2C78B9",
    family: "Mx-ERP",
    keyWord: "Built",
    accentColor: "#2C78B9",
    trustCount: "1000+",
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
     headlineThin: "Contract Management ERP",
     capHeadline: "Control Contracts Better with Mx-ERP",
     headlineBold: "ERP Software for Contract Lifecycle Management",
    headlineAccent: "Mx-ERP by ACGIL",
    subheadline:
      "Mx-ERP by ACGIL is a powerful Contract Management ERP Software designed to manage contracts, track obligations, automate billing, and control risks — all in one unified platform.",
    dashHeading:
      "Manage contracts from creation to execution, billing, and renewal from a single dashboard. Get real-time visibility into contract value, timelines, compliance, and performance.",
    PainPointsSubheading:
      "Businesses handling contracts face operational and financial risks due to manual processes and lack of control.",
    painPoints: [
      { title: "Missed Deadlines", desc: "Contract mismanagement causing missed renewals, expiries, and obligation failures." },
      { title: "Revenue Leakage", desc: "Poor tracking of contract milestones and billing leading to unrecovered revenue." },
      { title: "Manual Billing Errors", desc: "Incorrect invoicing due to disconnected billing and contract data." },
      { title: "Compliance & Legal Risk", desc: "Lack of audit trails and approval workflows exposing businesses to legal risks." },
      { title: "Zero Visibility", desc: "No centralized view across all active contracts, values, and performance metrics." },
    ],
    solutionHeadline: "How Contract Management ERP Software Solves These Challenges",
    description:
      "Mx-ERP digitizes and automates your entire contract process from creation to closure.",
    solutionFeatures: [
      "Centralized contract repository with version control",
      "Automated alerts and reminders for renewals and expiries",
      "Real-time contract tracking across all stages",
      "Integrated billing, finance, and compliance management",
    ],
    capdescription:
      "Advanced contract lifecycle modules designed to reduce risk, recover revenue, and accelerate execution.",
    capabilities: [
      {
        title: "Contract Creation & Management",
        description:
          "Draft, store, and manage all contracts in one centralized repository with version control and template management.",
        features: [
          "Contract drafting and storage",
          "Version control",
          "Template management",
          "Document centralization",
        ],
        tag: "Contracts",
        widget: "planning",
        accentColor: "#2C78B9",
        secondaryColor: "#059A8F",
      },
      {
        title: "Contract Lifecycle Management (CLM)",
        description:
          "Track every contract from initiation to closure with automated renewal tracking, expiry alerts, and workflow automation.",
        features: [
          "Contract initiation to closure",
          "Renewal tracking",
          "Expiry alerts",
          "Workflow automation",
        ],
        tag: "Lifecycle",
        widget: "production",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9",
      },
      {
        title: "Billing & Revenue Tracking",
        description:
          "Automate contract-based and milestone billing with integrated revenue recognition and real-time payment tracking.",
        features: [
          "Contract-based billing",
          "Milestone billing",
          "Revenue recognition",
          "Payment tracking",
        ],
        tag: "Finance",
        widget: "billing",
        accentColor: "#7F77DD",
        secondaryColor: "#059A8F",
      },
      {
        title: "Compliance & Risk Management",
        description:
          "Ensure full compliance with audit trails, legal risk controls, and multi-level approval workflows for every contract.",
        features: [
          "Compliance monitoring",
          "Audit trails",
          "Legal risk control",
          "Approval workflows",
        ],
        tag: "Compliance",
        widget: "gst",
        accentColor: "#059A8F",
        secondaryColor: "#7F77DD",
      },
      {
        title: "Vendor & Client Management",
        description:
          "Track vendor and client contracts with performance monitoring and relationship insights in one unified view.",
        features: [
          "Vendor contract tracking",
          "Client agreement management",
          "Performance tracking",
          "Relationship insights",
        ],
        tag: "Relationships",
        widget: "vendor",
        accentColor: "#2C78B9",
        secondaryColor: "#7F77DD",
      },
      {
        title: "MIS & Business Intelligence",
        description:
          "Make faster decisions with contract performance reports, revenue dashboards, and real-time KPI analytics.",
        features: [
          "Contract performance reports",
          "Revenue dashboards",
          "KPI tracking",
          "Real-time analytics",
        ],
        tag: "Analytics",
        widget: "mis",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9",
      },
    ],
    dashboardType: "contract-management",
    businessImpact: [
      {
        value: "40%",
        label: "Contract Errors",
        description: "Reduce mistakes with automated workflows and version control",
        color: "#00F2FE",
        icon: "TrendingDown",
      },
      {
        value: "30%",
        label: "Revenue Visibility",
        description: "Improve revenue tracking across all active contracts",
        color: "#7F77DD",
        icon: "TrendingUp",
      },
      {
        value: "35%",
        label: "Process Efficiency",
        description: "Faster contract execution with automated workflows",
        color: "#F59E0B",
        icon: "TrendingUp",
      },
      {
        value: "2X",
        label: "Faster Execution",
        description: "Accelerate contract approvals and billing cycles",
        color: "#EC4899",
        icon: "Zap",
      },
    ],
    workflow: [
      { id: 1, title: "Contract Creation", step: "Step 1", icon: "order", color: "#2C78B9", bgColor: "bg-indigo-500", shadow: "shadow-indigo-500/20" },
      { id: 2, title: "Approval", step: "Step 2", icon: "production", color: "#2C78B9", bgColor: "bg-purple-500", shadow: "shadow-purple-500/20" },
      { id: 3, title: "Execution", step: "Step 3", icon: "inventory", color: "#2C78B9", bgColor: "bg-blue-500", shadow: "shadow-blue-500/20" },
      { id: 4, title: "Billing", step: "Step 4", icon: "billing", color: "#2C78B9", bgColor: "bg-emerald-500", shadow: "shadow-emerald-500/20" },
      { id: 5, title: "Payment", step: "Step 5", icon: "finance", color: "#2C78B9", bgColor: "bg-amber-500", shadow: "shadow-amber-500/20" },
      { id: 6, title: "Renewal", step: "Step 6", icon: "reports", color: "#2C78B9", bgColor: "bg-teal-500", shadow: "shadow-teal-500/20" },
    ],
    features: [
      { id: "creation", title: "Contract Creation & Management", description: "Centralized drafting, storage, and version control with template management." },
      { id: "clm", title: "Contract Lifecycle Management", description: "End-to-end tracking from initiation to closure with renewal alerts and workflow automation." },
      { id: "billing", title: "Billing & Revenue Tracking", description: "Contract-based and milestone billing with integrated revenue recognition." },
      { id: "compliance", title: "Compliance & Risk Management", description: "Audit trails, legal risk controls, and multi-level approval workflows." },
      { id: "vendor-client", title: "Vendor & Client Management", description: "Unified tracking of vendor and client contracts with performance insights." },
      { id: "mis", title: "MIS & Business Intelligence", description: "Contract performance reports, revenue dashboards, and real-time KPI analytics." },
    ],
    headline: "Your Entire Contract Lifecycle,",
    heroStats: [
      { label: "Active Clients", value: "1000+" },
      { label: "Years of Trust", value: "30+" },
      { label: "GST Ready", value: "100%" },
    ],
    mockupImage: "/images/products/contract-erp-hero.png",
    solutions: [
      { title: "Centralized repository", desc: "All contracts in one place with version control and instant access." },
      { title: "Automated alerts", desc: "Never miss a renewal, expiry, or milestone with proactive reminders." },
      { title: "Integrated billing", desc: "Contract-linked invoicing with zero revenue leakage." },
      { title: "Compliance control", desc: "Audit-ready trails and approval workflows to eliminate legal risk." },
      { title: "Real-time analytics", desc: "Contract performance and revenue dashboards for smarter decisions." },
    ],
    roiEstimate: "₹8–15 lakhs/year",
    implementationSteps: [
      {
        num: "01",
        title: "Process Audit",
        description:
          "Our experts map your existing contract workflows, billing structures, and compliance requirements to design the optimal ERP configuration.",
        bullets: ["Workflow mapping", "Contract template review", "Gap analysis"],
        image: "/images/products/audit.png",
        bg: "linear-gradient(135deg, #071428, #0d3060)",
      },
      {
        num: "02",
        title: "Seamless Integration",
        description:
          "We configure contract lifecycle, billing, compliance, and vendor management modules to fit your exact business needs with 100% data accuracy from day one.",
        bullets: ["CLM configuration", "Billing integration", "Data migration"],
        image: "/images/products/integration.png",
        bg: "linear-gradient(135deg, #071e1c, #0a3530)",
      },
      {
        num: "03",
        title: "Go-Live & Support",
        description:
          "Dedicated training for your contracts, finance, and legal teams followed by on-site support during the first month to ensure zero downtime.",
        bullets: ["Staff training", "On-site support", "Hypercare period"],
        image: "/images/products/support.png",
        bg: "linear-gradient(135deg, #100d28, #1a1040)",
      },
    ],
    clients: [
      { id: "cm1", name: "ACMA Contracts", logo: "/images/clients/acma-logo.webp" },
      { id: "cm2", name: "AHPL Engineering", logo: "/images/clients/ahpl-logo.webp" },
      { id: "cm3", name: "AIIMS Projects", logo: "/images/clients/aiims-logo.webp" },
      { id: "cm4", name: "Allahabad Industries", logo: "/images/clients/allahabad-bank-logo.webp" },
      { id: "cm5", name: "ANT Construction", logo: "/images/clients/ant-logo.webp" },
      { id: "cm6", name: "AOS Services", logo: "/images/clients/aos-logo.webp" },
      { id: "cm7", name: "Arogyavaram EPC", logo: "/images/clients/arogyavaram-logo.webp" },
      { id: "cm8", name: "Arora Enterprises", logo: "/images/clients/arora-aromatics-logo.webp" },
    ],
    industry: "Contract Management",
    heroImage: "/images/products/contract-erp-hero.png",
    marqueeItems: [
      "GST Compliant",
      "CLM Ready",
      "Milestone Billing",
      "Audit Trails",
      "Cloud Security",
      "ISO Certified",
    ],
    testimonials: [
      {
        id: 321,
        name: "Suresh Kapoor",
        role: "Legal Head",
        company: "Kapoor Infrastructure",
        avatar: "/images/partners/1.png",
        quote:
          "Mx-ERP Contract Management eliminated our contract mismanagement issues overnight. We no longer miss renewals and our audit trails are always ready for compliance reviews.",
        rating: 5,
      },
      {
        id: 322,
        name: "Anita Mehta",
        role: "CFO",
        company: "Mehta EPC Group",
        avatar: "/images/partners/2.webp",
        quote:
          "Revenue leakage from unbilled milestones dropped to zero. The integrated billing module ensures every contract obligation is invoiced on time — we recovered ₹22 lakhs in the first year.",
        rating: 5,
      },
      {
        id: 323,
        name: "Rajiv Sharma",
        role: "Operations Director",
        company: "Sharma Services Ltd",
        avatar: "/images/partners/3.webp",
        quote:
          "Managing 200+ vendor and client contracts manually was a constant risk. With Mx-ERP, we have real-time visibility into every contract value, deadline, and compliance status.",
        rating: 5,
      },
      {
        id: 324,
        name: "Priya Nair",
        role: "Contract Manager",
        company: "Nair Manufacturing",
        avatar: "/images/partners/4.webp",
        quote:
          "Contract execution time reduced by 2X. Automated approval workflows mean no more chasing signatures and the version control has eliminated disputes over document versions.",
        rating: 5,
      },
      {
        id: 325,
        name: "Deepak Verma",
        role: "CEO",
        company: "Verma Trading Group",
        avatar: "/images/partners/5.webp",
        quote:
          "Process efficiency improved by 35% in the first quarter. The MIS dashboards give us instant visibility into contract performance and revenue that used to take days to compile.",
        rating: 5,
      },
    ],
    integrations: [
      { name: "Tally Prime", icon: "calculator", category: "Accounting" },
      { name: "Razorpay", icon: "credit-card", category: "Payments" },
      { name: "GSTN Portal", icon: "file-text", category: "Government" },
      { name: "E-Invoice", icon: "file-digit", category: "Government" },
      { name: "WhatsApp Business", icon: "message-square", category: "Communication" },
      { name: "MS Project", icon: "calendar", category: "Planning" },
      { name: "Zoho Books", icon: "book-open", category: "Accounting" },
      { name: "E-way Bill", icon: "truck", category: "Government" },
    ],
    faqs: [
      {
        q: "What is Contract Management ERP Software?",
        a: "Contract Management ERP Software helps businesses manage the entire lifecycle of contracts, including creation, execution, billing, compliance, and renewal.",
      },
      {
        q: "Who should use Contract ERP Software?",
        a: "Businesses in construction, EPC, manufacturing, services, and trading that handle multiple contracts benefit from it.",
      },
      {
        q: "Does it support contract-based billing?",
        a: "Yes, it supports milestone billing, recurring billing, and revenue tracking.",
      },
      {
        q: "Can it track contract renewals and expiry?",
        a: "Yes, it provides automated alerts and reminders for renewals and expiries.",
      },
      {
        q: "How does it reduce risks?",
        a: "It ensures compliance, maintains audit trails, and minimizes manual errors through workflow automation.",
      },
      {
        q: "Is it scalable for growing businesses?",
        a: "Yes, Mx-ERP is scalable and adaptable to business growth across industries.",
      },
      {
        q: "Does it integrate with finance and ERP modules?",
        a: "Yes, it integrates with finance, billing, procurement, and project modules.",
      },
      {
        q: "Is it cloud-based or on-premise?",
        a: "Mx-ERP offers both cloud-based and on-premise deployment options.",
      },
    ],
    industriesServed: [
      "Construction & EPC Companies",
      "Manufacturing & Engineering",
      "IT & Software Services",
      "Facility Management",
      "Government & Public Sector",
      "Trading & Distribution",
      "Professional Services & Consulting",
    ],
    comparison: [
      { feature: "Visibility", mxerp: "Real-time", others: "Limited" },
      { feature: "Automation", mxerp: "High", others: "None" },
      { feature: "Risk", mxerp: "Low", others: "High" },
      { feature: "Billing", mxerp: "Integrated", others: "Separate" },
      { feature: "Compliance", mxerp: "Built-in", others: "Risky/Manual" },
    ],
    seo: {
      title: "Contract Management ERP Software | Contract Lifecycle Management - Acgil",
      description: "Manage contracts efficiently with ACGIL’s Contract Management ERP Software for contract creation, tracking, approvals, compliance, renewals, and lifecycle management.",
      keywords:
        "contract management erp software, contract lifecycle management system, clm software, contract billing software, contract tracking system, erp for contracts, compliance management software",
      canonical: SITE_URL + "/products/contract-management-erp-software",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      ogTitle: "Contract Management ERP Software | Contract Lifecycle Management - Acgil",
      ogDescription: "Manage contracts efficiently with ACGIL’s Contract Management ERP Software for contract creation, tracking, approvals, compliance, renewals, and lifecycle management.",
      ogUrl: SITE_URL + "/products/contract-management-erp-software",
      ogImage: SITE_URL + "/images/contract-erp.jpg",
      hreflang: {
        en: SITE_URL + "/products/contract-management-erp-software",
        "x-default": SITE_URL + "/products/contract-management-erp-software",
      },
      faqSchema: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "What is Contract Management ERP Software?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Contract Management ERP Software helps manage contract lifecycle including creation, billing, compliance, and renewal.",
            },
          },
          {
            "@type": "Question",
            name: "Does it support billing?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, it supports contract-based and milestone billing.",
            },
          },
          {
            "@type": "Question",
            name: "Can it track contract renewals?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, it provides automated alerts for renewals and expiry.",
            },
          },
          {
            "@type": "Question",
            name: "How does it reduce compliance risk?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "It maintains audit trails, enforces approval workflows, and monitors compliance across all contracts.",
            },
          },
          {
            "@type": "Question",
            name: "Is it scalable for growing businesses?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, Mx-ERP is scalable for small, medium, and large enterprises across industries.",
            },
          },
        ],
      },
    },
  },
  {
    id: "quantity-surveyor-erp-software",
    slug: "quantity-surveyor-erp-software",
    category: "mx-construction",
    name: "Quantity Surveyor ERP Software",
    tagline: "Built for Construction Cost Control. Engineered for Project Profitability.",
    image: "/images/products/mx-erp-qs.webp",
    href: "/products/quantity-surveyor-erp-software",
    badge: "Most Popular",
    color: "#059A8F",
    family: "Mx-ERP",
    keyWord: "Built",
    accentColor: "#059A8F",
    trustCount: "1000+",
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
     headlineThin: "Quantity Surveyor ERP",
     capHeadline: "Precision Quantity Surveying with Mx-ERP",
     headlineBold: "BOQ Software for Construction",
    headlineAccent: "Mx-ERP by ACGIL",
    subheadline:
      "Mx-ERP by ACGIL is a powerful Quantity Surveyor ERP Software designed to manage BOQ, cost estimation, budgeting, and project cost control — all in one unified platform.",
    dashHeading:
      "Manage BOQ, project costs, contracts, billing, and budgets from a single dashboard. Mx-ERP delivers real-time visibility into cost tracking, estimation accuracy, and profitability.",
    PainPointsSubheading:
      "Construction and project-based businesses face multiple cost and estimation challenges that impact profitability and project success.",
    painPoints: [
      { title: "BOQ Calculation Errors", desc: "Manual quantity calculations leading to costly mistakes and project disputes." },
      { title: "Inaccurate Cost Estimation", desc: "Poor estimation causing budget overruns before the project even begins." },
      { title: "Cost Tracking Gaps", desc: "No real-time visibility into actual vs budgeted costs across project stages." },
      { title: "Budget Overruns", desc: "Uncontrolled expenditure due to lack of alerts and cost variance monitoring." },
      { title: "Zero Cost Visibility", desc: "Disconnected data across BOQ, billing, and finance causing profitability blind spots." },
    ],
    solutionHeadline: "How Quantity Surveyor ERP Software Solves These Challenges",
    description:
      "Mx-ERP automates and streamlines the entire cost management process for construction and project businesses.",
    solutionFeatures: [
      "Automated BOQ generation with version control",
      "Accurate cost estimation and budget forecasting",
      "Real-time cost tracking with variance alerts",
      "Centralized cost management system with RA billing integration",
    ],
    capdescription:
      "Advanced QS modules designed to eliminate estimation errors, control costs, and maximize project profitability.",
    capabilities: [
      {
        title: "BOQ Management",
        description:
          "Automate quantity calculations, create and revise BOQs with version control, and track item-wise costs with complete accuracy.",
        features: [
          "Automated quantity calculations",
          "BOQ creation and revisions",
          "Version control",
          "Item-wise cost tracking",
        ],
        tag: "BOQ",
        widget: "planning",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9",
      },
      {
        title: "Cost Estimation & Budgeting",
        description:
          "Estimate material and labor costs accurately with budget planning, cost forecasting, and continuous estimation accuracy improvement.",
        features: [
          "Material & labor cost estimation",
          "Budget planning",
          "Cost forecasting",
          "Estimation accuracy improvement",
        ],
        tag: "Estimation",
        widget: "finance",
        accentColor: "#2C78B9",
        secondaryColor: "#059A8F",
      },
      {
        title: "RA Billing Integration",
        description:
          "Link BOQ directly to progress-based RA billing with automated calculations and real-time payment tracking.",
        features: [
          "Progress-based billing",
          "BOQ-linked billing",
          "Automated calculations",
          "Payment tracking",
        ],
        tag: "Billing",
        widget: "billing",
        accentColor: "#7F77DD",
        secondaryColor: "#059A8F",
      },
      {
        title: "Contract & Vendor Management",
        description:
          "Track subcontractors, control contract-based costs, and manage rate analysis and variation orders in one system.",
        features: [
          "Subcontractor tracking",
          "Contract-based cost control",
          "Rate analysis",
          "Variation order management",
        ],
        tag: "Contracts",
        widget: "vendor",
        accentColor: "#059A8F",
        secondaryColor: "#7F77DD",
      },
      {
        title: "Project Cost Control",
        description:
          "Monitor budget vs actual costs in real time with variance alerts and profitability analysis at every project stage.",
        features: [
          "Budget vs actual comparison",
          "Cost variance alerts",
          "Real-time cost tracking",
          "Profitability analysis",
        ],
        tag: "Cost Control",
        widget: "production",
        accentColor: "#2C78B9",
        secondaryColor: "#7F77DD",
      },
      {
        title: "MIS & Reporting",
        description:
          "Make faster decisions with cost reports, budget dashboards, and real-time profitability insights.",
        features: [
          "Cost reports",
          "Budget reports",
          "Profitability insights",
          "Real-time dashboards",
        ],
        tag: "Analytics",
        widget: "mis",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9",
      },
    ],
    dashboardType: "quantity-surveyor",
    businessImpact: [
      {
        value: "50%",
        label: "Estimation Errors",
        description: "Reduce BOQ and cost calculation mistakes with automation",
        color: "#00F2FE",
        icon: "TrendingDown",
      },
      {
        value: "40%",
        label: "Faster BOQ Prep",
        description: "Prepare accurate BOQs in a fraction of the time",
        color: "#7F77DD",
        icon: "Zap",
      },
      {
        value: "30%",
        label: "Cost Control",
        description: "Improve cost control efficiency across all projects",
        color: "#F59E0B",
        icon: "TrendingUp",
      },
      {
        value: "2X",
        label: "Project Profitability",
        description: "Better margins through tighter cost management",
        color: "#EC4899",
        icon: "TrendingUp",
      },
    ],
    workflow: [
      { id: 1, title: "BOQ", step: "Step 1", icon: "boq", color: "#059A8F", bgColor: "bg-teal-500", shadow: "shadow-teal-500/20" },
      { id: 2, title: "Cost Estimation", step: "Step 2", icon: "finance", color: "#059A8F", bgColor: "bg-blue-500", shadow: "shadow-blue-500/20" },
      { id: 3, title: "Budget", step: "Step 3", icon: "inventory", color: "#059A8F", bgColor: "bg-indigo-500", shadow: "shadow-indigo-500/20" },
      { id: 4, title: "RA Billing", step: "Step 4", icon: "billing", color: "#059A8F", bgColor: "bg-emerald-500", shadow: "shadow-emerald-500/20" },
      { id: 5, title: "Payment", step: "Step 5", icon: "finance", color: "#059A8F", bgColor: "bg-amber-500", shadow: "shadow-amber-500/20" },
      { id: 6, title: "Cost Analysis", step: "Step 6", icon: "reports", color: "#059A8F", bgColor: "bg-purple-500", shadow: "shadow-purple-500/20" },
    ],
    features: [
      { id: "boq", title: "BOQ Management", description: "Automated quantity calculations with BOQ creation, revisions, and version control." },
      { id: "estimation", title: "Cost Estimation & Budgeting", description: "Material and labor cost estimation with budget planning and cost forecasting." },
      { id: "ra-billing", title: "RA Billing Integration", description: "BOQ-linked progress-based billing with automated calculations and payment tracking." },
      { id: "contracts", title: "Contract & Vendor Management", description: "Subcontractor tracking, rate analysis, and variation order management." },
      { id: "cost-control", title: "Project Cost Control", description: "Real-time budget vs actual tracking with cost variance alerts and profitability analysis." },
      { id: "mis", title: "MIS & Reporting", description: "Cost reports, budget dashboards, and real-time profitability insights." },
    ],
    headline: "Your Entire Cost & BOQ Process,",
    heroStats: [
      { label: "Active Clients", value: "1000+" },
      { label: "Years of Trust", value: "30+" },
      { label: "GST Ready", value: "100%" },
    ],
    mockupImage: "/images/products/qs-erp-hero.png",
    solutions: [
      { title: "Automated BOQ", desc: "Generate accurate BOQs in minutes with zero manual calculation errors." },
      { title: "Precise cost estimation", desc: "Material and labor forecasting tools to keep projects on budget from day one." },
      { title: "RA billing integration", desc: "BOQ-linked billing ensures every completed item is invoiced accurately." },
      { title: "Real-time cost control", desc: "Live budget vs actual tracking with instant variance alerts." },
      { title: "Profitability analytics", desc: "Project-level profitability insights to make smarter financial decisions." },
    ],
    roiEstimate: "₹8–15 lakhs/year",
    implementationSteps: [
      {
        num: "01",
        title: "Process Audit",
        description:
          "Our experts map your existing BOQ structures, cost estimation workflows, and billing processes to design the optimal configuration.",
        bullets: ["BOQ structure review", "Estimation workflow mapping", "Gap analysis"],
        image: "/images/products/audit.png",
        bg: "linear-gradient(135deg, #071428, #0d3060)",
      },
      {
        num: "02",
        title: "Seamless Integration",
        description:
          "We configure BOQ management, cost estimation, RA billing, and contract modules to fit your exact project requirements with 100% data accuracy from day one.",
        bullets: ["BOQ configuration", "RA billing integration", "Data migration"],
        image: "/images/products/integration.png",
        bg: "linear-gradient(135deg, #071e1c, #0a3530)",
      },
      {
        num: "03",
        title: "Go-Live & Support",
        description:
          "Dedicated training for your QS, finance, and project teams followed by on-site support during the first month to ensure zero downtime.",
        bullets: ["Staff training", "On-site support", "Hypercare period"],
        image: "/images/products/support.png",
        bg: "linear-gradient(135deg, #100d28, #1a1040)",
      },
    ],
    clients: [
      { id: "qs1", name: "ACMA Projects", logo: "/images/clients/acma-logo.webp" },
      { id: "qs2", name: "AHPL Engineering", logo: "/images/clients/ahpl-logo.webp" },
      { id: "qs3", name: "AIIMS Infrastructure", logo: "/images/clients/aiims-logo.webp" },
      { id: "qs4", name: "Allahabad Builders", logo: "/images/clients/allahabad-bank-logo.webp" },
      { id: "qs5", name: "ANT Construction", logo: "/images/clients/ant-logo.webp" },
      { id: "qs6", name: "AOS Developers", logo: "/images/clients/aos-logo.webp" },
      { id: "qs7", name: "Arogyavaram EPC", logo: "/images/clients/arogyavaram-logo.webp" },
      { id: "qs8", name: "Arora Engineering", logo: "/images/clients/arora-aromatics-logo.webp" },
    ],
    industry: "Construction & Quantity Surveying",
    heroImage: "/images/products/qs-erp-hero.png",
    marqueeItems: [
      "GST Compliant",
      "BOQ Automation",
      "RA Billing",
      "Cost Estimation",
      "Cloud Security",
      "ISO Certified",
    ],
    testimonials: [
      {
        id: 326,
        name: "Arun Mehta",
        role: "Chief Quantity Surveyor",
        company: "Mehta Infrastructure",
        avatar: "/images/partners/1.png",
        quote:
          "Mx-ERP reduced our BOQ preparation time by 40%. What used to take 3 days now takes half a day with zero calculation errors. Our estimation accuracy has never been better.",
        rating: 5,
      },
      {
        id: 327,
        name: "Sunita Iyer",
        role: "Project Finance Head",
        company: "Iyer Constructions",
        avatar: "/images/partners/2.webp",
        quote:
          "The real-time budget vs actual tracking with variance alerts transformed our cost control. We identified and stopped a potential ₹35 lakh overrun in the first month of using Mx-ERP.",
        rating: 5,
      },
      {
        id: 328,
        name: "Deepak Sharma",
        role: "Operations Director",
        company: "Sharma EPC",
        avatar: "/images/partners/3.webp",
        quote:
          "BOQ-linked RA billing eliminated all our invoicing disputes with clients. Every bill is traceable to a specific BOQ item and progress percentage — complete transparency.",
        rating: 5,
      },
      {
        id: 329,
        name: "Kavita Nair",
        role: "Contract Manager",
        company: "Nair Civil Works",
        avatar: "/images/partners/4.webp",
        quote:
          "Subcontractor cost tracking and variation order management are handled seamlessly. We've reduced subcontractor billing disputes by 80% since implementing Mx-ERP.",
        rating: 5,
      },
      {
        id: 330,
        name: "Rajiv Patel",
        role: "MD",
        company: "Patel Builders",
        avatar: "/images/partners/5.webp",
        quote:
          "Project profitability improved by 2X after implementing proper cost controls. The MIS dashboards give our management team instant visibility into cost performance across all sites.",
        rating: 5,
      },
    ],
    integrations: [
      { name: "Tally Prime", icon: "calculator", category: "Accounting" },
      { name: "AutoCAD", icon: "layout", category: "Design & Estimation" },
      { name: "MS Project", icon: "calendar", category: "Planning" },
      { name: "GSTN Portal", icon: "file-text", category: "Government" },
      { name: "E-way Bill", icon: "truck", category: "Government" },
      { name: "Razorpay", icon: "credit-card", category: "Payments" },
      { name: "E-Invoice", icon: "file-digit", category: "Government" },
      { name: "WhatsApp Business", icon: "message-square", category: "Communication" },
    ],
    faqs: [
      {
        q: "What is Quantity Surveyor ERP Software?",
        a: "It helps manage BOQ, cost estimation, budgeting, and project cost control efficiently in one centralized platform.",
      },
      {
        q: "What is BOQ in construction?",
        a: "BOQ (Bill of Quantity) lists materials, labor, and costs required for a construction project and forms the basis for billing and cost control.",
      },
      {
        q: "Can it handle RA billing?",
        a: "Yes, it supports progress-based RA billing directly linked with BOQ for accurate and transparent invoicing.",
      },
      {
        q: "Does it help in cost estimation?",
        a: "Yes, it provides accurate cost estimation and forecasting tools for material, labor, and equipment costs.",
      },
      {
        q: "Can it track project costs in real time?",
        a: "Yes, it offers real-time cost tracking, budget vs actual comparison, and cost variance alerts.",
      },
      {
        q: "Is it suitable for large projects?",
        a: "Yes, Mx-ERP is scalable for projects of all sizes from small contractors to large EPC companies.",
      },
      {
        q: "Does it support variation order management?",
        a: "Yes, it tracks variation orders, rate revisions, and contract amendments with full audit trails.",
      },
      {
        q: "Is it cloud-based or on-premise?",
        a: "Mx-ERP supports both cloud-based and on-premise deployment options.",
      },
    ],
    industriesServed: [
      "Civil & Structural Engineering",
      "EPC & Infrastructure Companies",
      "Building & Real Estate Developers",
      "Road & Highway Projects",
      "MEP & Electrical Contractors",
      "Industrial Plant Construction",
      "Government & Public Works",
    ],
    comparison: [
      { feature: "Accuracy", mxerp: "High — Automated", others: "Error-prone" },
      { feature: "Speed", mxerp: "Fast", others: "Slow" },
      { feature: "Automation", mxerp: "Yes", others: "No" },
      { feature: "Tracking", mxerp: "Real-time", others: "Limited" },
      { feature: "Cost Control", mxerp: "Strong", others: "Weak" }
    ],
    seo: {
      title: "BOQ Estimation ERP Software for Construction | BOQ Cost Analysis Software",
      description: "Automate BOQ analysis, estimation and cost control with Mx-ERP BOQ software for construction. Accurate takeoff, faster bids. Request a free demo.",
      keywords:
        "quantity surveyor erp software, boq software, cost estimation software construction, quantity surveying software, project cost control erp, construction cost management software",
      canonical: SITE_URL + "/products/quantity-surveyor-erp-software",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      ogTitle: "BOQ Estimation ERP Software for Construction | BOQ Cost Analysis Software",
      ogDescription: "Automate BOQ analysis, estimation and cost control with Mx-ERP BOQ software for construction. Accurate takeoff, faster bids. Request a free demo.",
      ogUrl: SITE_URL + "/products/quantity-surveyor-erp-software",
      ogImage: SITE_URL + "/images/quantity-surveyor-erp.jpg",
      hreflang: {
        en: SITE_URL + "/products/quantity-surveyor-erp-software",
        "x-default": SITE_URL + "/products/quantity-surveyor-erp-software",
      },
      faqSchema: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "What is Quantity Surveyor ERP Software?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "It helps manage BOQ, cost estimation, budgeting, and project cost control efficiently.",
            },
          },
          {
            "@type": "Question",
            name: "What is BOQ in construction?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "BOQ lists materials, labor, and costs required for a construction project.",
            },
          },
          {
            "@type": "Question",
            name: "Can it handle RA billing?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, it supports progress-based RA billing linked with BOQ.",
            },
          },
          {
            "@type": "Question",
            name: "Does it help in cost estimation?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, it provides accurate cost estimation and forecasting tools.",
            },
          },
          {
            "@type": "Question",
            name: "Is it suitable for large projects?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, it is scalable for projects of all sizes.",
            },
          },
        ],
      },
    },
  },
  {
    id: "construction-epc",
    slug: "construction-epc-erp-software",
    category: "mx-construction",
    name: "Construction EPC",
    href: "/products/construction-epc-erp-software",
    seo: {
      title: "EPC & MEP Construction ERP Software | Intelligent tender and estimation Software",
      description: "ACGIL's EPC & MEP construction ERP software simplifies intelligent tender and estimation, procurement, and project costing. Book a free demo today.",
      canonical: SITE_URL + "/products/construction-epc-erp-software",
      robots: "index, follow",
      ogTitle: "EPC & MEP Construction ERP Software | Intelligent tender and estimation Software",
      ogDescription: "ACGIL's EPC & MEP construction ERP software simplifies intelligent tender and estimation, procurement, and project costing. Book a free demo today.",
      ogUrl: SITE_URL + "/products/construction-epc-erp-software",
    },
    family: "Mx-ERP",
    headlineThin: "Construction ERP",
    tagline: "Built for Every Project — From Estimation to Handover",
    headlineBold: "Construction EPC ERP Software",
    headlineAccent: "Every Project",
    subheadline: "Mx-ERP by ACGIL is a powerful Construction EPC ERP Software designed to give you complete control over engineering, procurement, construction, billing, and resource management, all in one unified platform.",
    subtitle: "From estimation to handover — ACGIL Mx-ERP gives construction companies total control over costs, timelines, and compliance.",
    challengesSubheadline: "From estimation to handover — ACGIL Mx-ERP gives construction companies total control over costs, timelines, and compliance.",
    accentColor: "#059A8F",
    dashboardType: "construction",
    stats: [
      { value: "340+", label: "PROJECTS DELIVERED" },
      { value: "₹500Cr+", label: "PROJECT VALUE MANAGED" },
      { value: "100%", label: "GST COMPLIANT" }
    ],
    workflow: [
      { id: 1, title: "Project Initiation", step: "Step 1", icon: "project", color: "#059A8F", bgColor: "bg-teal-500", shadow: "shadow-teal-500/20" },
      { id: 2, title: "BOQ & Estimation", step: "Step 2", icon: "boq", color: "#059A8F", bgColor: "bg-blue-500", shadow: "shadow-blue-500/20" },
      { id: 3, title: "Procurement", step: "Step 3", icon: "vendor", color: "#059A8F", bgColor: "bg-indigo-500", shadow: "shadow-indigo-500/20" },
      { id: 4, title: "Execution", step: "Step 4", icon: "site", color: "#059A8F", bgColor: "bg-emerald-500", shadow: "shadow-emerald-500/20" },
      { id: 5, title: "Cost Control", step: "Step 5", icon: "cost", color: "#059A8F", bgColor: "bg-amber-500", shadow: "shadow-amber-500/20" },
      { id: 6, title: "Billing & RA", step: "Step 6", icon: "billing", color: "#059A8F", bgColor: "bg-rose-500", shadow: "shadow-rose-500/20" },
      { id: 7, title: "Handover", step: "Step 7", icon: "delivery", color: "#059A8F", bgColor: "bg-fuchsia-500", shadow: "shadow-fuchsia-500/20" },
      { id: 8, title: "Compliance", step: "Step 8", icon: "gst", color: "#059A8F", bgColor: "bg-purple-500", shadow: "shadow-purple-500/20" },
    ],
    steps: [
      { num: "01", title: "Map Your Project Structure", description: "We configure BOQ, WBS, and cost centers matching your exact project hierarchy.", bullets: ["WBS mapping", "Cost centers", "BOQ import"], duration: "Week 1-2" },
      { num: "02", title: "Configure Approvals & Billing", description: "Set up RA billing cycles, sub-contractor approvals, and retention workflows.", bullets: ["RA billing", "Approval matrix", "Retention setup"], duration: "Week 2-5" },
      { num: "03", title: "Go Live on Site", description: "Mobile-ready for site engineers. Real-time updates from anywhere.", bullets: ["Mobile app", "Site training", "Live sync"], duration: "Week 6-8" }
    ],
    features: [
      { id: "project", title: "Project Planning", description: "Detailed WBS and milestone tracking.", componentType: "planning", colSpan: 2 },
      { id: "boq", title: "BOQ Management", description: "Centralized BOQ with version control.", componentType: "boq", colSpan: 1 },
      { id: "billing", title: "RA Billing", description: "Automated running account bills.", componentType: "billing", colSpan: 1 },
      { id: "vendor", title: "Sub-contractor Portal", description: "Manage vendor bills and retention.", componentType: "vendor", colSpan: 1 },
      { id: "cost", title: "Cost Control", description: "Real-time budget vs actual tracking.", componentType: "cost", colSpan: 2 },
      { id: "mobile", title: "Site Mobile App", description: "Daily progress reporting from site.", componentType: "mobile", colSpan: 1 },
      { id: "doc", title: "Document Control", description: "Centralized drawing and contract storage.", componentType: "doc", colSpan: 1 }
    ],
    capabilities: [
      {
        title: "Project Planning & BOQ",
        description: "Detailed BOQ with material, labour, and equipment costing",
        features: [
          "Multi-level WBS and milestone tracking",
          "BOQ import with version control",
          "Resource and equipment scheduling",
          "Material quantity estimation"
        ],
        tag: "Planning",
        widget: "planning",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9"
      },
      {
        title: "RA Billing & Payments",
        description: "Running account billing with retention and milestone tracking",
        features: [
          "Automated running account bill generation",
          "Configurable retention rules (5–10%)",
          "Milestone-based payment tracking",
          "Subcontractor payment certificates"
        ],
        tag: "Finance",
        widget: "billing",
        accentColor: "#2C78B9",
        secondaryColor: "#059A8F"
      },
      {
        title: "Sub-contractor Management",
        description: "PO, work orders, and payment certificates for all subs",
        features: [
          "Work order issuance and tracking",
          "Material issuance from site store",
          "Daily progress recording by sub",
          "Retention and deduction management"
        ],
        tag: "Vendors",
        widget: "vendor",
        accentColor: "#7F77DD",
        secondaryColor: "#059A8F"
      },
      {
        title: "Site Progress Tracking",
        description: "Daily progress reports and photo documentation",
        features: [
          "Mobile app for offline site reporting",
          "Photo evidence at each milestone",
          "Labour and equipment attendance",
          "Real-time HO dashboard updates"
        ],
        tag: "Execution",
        widget: "site",
        accentColor: "#059A8F",
        secondaryColor: "#7F77DD"
      },
      {
        title: "Cost Control & Forecasting",
        description: "Budget vs actual with EAC forecasting",
        features: [
          "Real-time budget vs actual at WBS level",
          "EAC and ETC forecasting",
          "Cost variance alerts and reports",
          "Profitability tracking per project"
        ],
        tag: "Finance",
        widget: "cost",
        accentColor: "#2C78B9",
        secondaryColor: "#7F77DD"
      },
      {
        title: "GST & Compliance",
        description: "Construction-specific GST with reverse charge handling",
        features: [
          "Reverse charge mechanism support",
          "Composite scheme calculations",
          "E-way bill for material movement",
          "Audit-ready statutory documentation"
        ],
        tag: "Compliance",
        widget: "gst",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9"
      }
    ],
    industriesServed: [
      "Residential & Commercial Buildings",
      "Roads & Infrastructure",
      "Industrial & Factory Construction",
      "MEP & Electrical Contractors",
      "Real Estate Developers",
      "Government EPC Projects",
      "Interior Fit-out Companies"
    ],
    faqs: [
      {
        q: "How does Mx-ERP handle RA billing and retention?",
        a: "Our RA billing module automatically calculates running account bills based on work progress, with configurable retention rules (typically 5-10%) that are automatically deducted and tracked for release."
      },
      {
        q: "Can site engineers use the system on mobile without internet?",
        a: "Yes, our mobile app works completely offline. Site engineers can log daily progress, upload photos, and record material consumption. Data syncs automatically when connectivity is restored."
      },
      {
        q: "How do you manage sub-contractor work orders and payments?",
        a: "The sub-contractor portal allows you to create work orders, track material issuance, record daily progress, and generate payment certificates with retention tracking - all integrated with your main project accounting."
      },
      {
        q: "Does it support AutoCAD and engineering drawing management?",
        a: "Yes, drawings can be uploaded and version-controlled with automatic notifications to relevant teams when revisions are made. You can link drawings to specific WBS elements and BOQ items."
      },
      {
        q: "How does cost tracking work at the WBS level?",
        a: "Each WBS node has its own budget, actuals, and variance tracking. You can drill down from project level to individual activities to see exactly where costs are deviating from plan."
      },
      {
        q: "Can it handle both building construction and infrastructure projects?",
        a: "Absolutely. The system is configured for both building projects (residential, commercial) and infrastructure (roads, bridges, utilities) with different WBS structures and reporting formats."
      },
      {
        q: "How does GST compliance work for construction projects?",
        a: "The system handles reverse charge mechanism, composite scheme calculations, and generates GST reports specific to construction. It also manages e-way bills for material movement between sites."
      }
    ],
    painPoints: [
      { title: "Budget Overruns", desc: "No real-time tracking of expenses vs budgets leading to cost leakages of 15-20% on average." },
      { title: "Material Wastage", desc: "Excess procurement and lack of site inventory control causing 10-15% material waste." },
      { title: "Manual RA Billing", desc: "Weeks of delay in processing contractor bills and reconciliation causing cash flow issues." },
      { title: "Zero Site Visibility", desc: "Relying on phone calls for daily progress updates from remote sites leading to delays." },
      { title: "Compliance Risks", desc: "Missing statutory documentation and GST reconciliation issues during audits." },
    ],
    solutions: [
      { title: "Real-time cost control", desc: "Live budget vs actual tracking at WBS level prevents overspending with automated alerts." },
      { title: "Smart procurement", desc: "Material indents linked to BOQ ensure zero excess procurement and optimal stock levels." },
      { title: "Automated RA billing", desc: "Process complex contractor bills with retention in minutes, not weeks." },
      { title: "Site mobile app", desc: "Digital daily progress reports with photo evidence from every site in real-time." },
      { title: "Built-in compliance", desc: "Automated GST filing and centralized document control for audit readiness." },
    ],
    ctaHeadline: "Ready to Transform Your Construction Operations?",
    ctaSubtext: "Join 340+ construction companies building the future with Mx-ERP.",
    clients: [
      { id: "c1", name: "Honeywell Engineering", logo: "/images/clients/acma-logo.webp" },
      { id: "c2", name: "AHPL Infrastructure", logo: "/images/clients/ahpl-logo.webp" },
      { id: "c3", name: "AIIMS Projects", logo: "/images/clients/aiims-logo.webp" },
      { id: "c4", name: "Allahabad Builders", logo: "/images/clients/allahabad-bank-logo.webp" },
      { id: "c5", name: "ANT Construction", logo: "/images/clients/ant-logo.webp" },
      { id: "c6", name: "AOS Developers", logo: "/images/clients/aos-logo.webp" },
      { id: "c7", name: "Arogyavaram EPC", logo: "/images/clients/arogyavaram-logo.webp" },
      { id: "c8", name: "Arora Engineering", logo: "/images/clients/arora-aromatics-logo.webp" }
    ],
    testimonials: [
      {
        id: 201,
        name: "Ramesh Chandra",
        role: "Project Director",
        company: "DLF Ltd",
        avatar: "/images/partners/6.webp",
        quote: "ACGIL Construction ERP gave us complete visibility across our 12 projects. RA billing that used to take 10 days now happens in 2 hours, and our project managers finally have real-time data.",
        rating: 5
      },
      {
        id: 202,
        name: "Sunita Reddy",
        role: "Finance Head",
        company: "Prestige Group",
        avatar: "/images/partners/7.webp",
        quote: "The cost control features are exceptional. We reduced budget overruns by 25% and the GST compliance module saved us 3 days every month in reconciliation work.",
        rating: 5
      },
      {
        id: 203,
        name: "Amit Patel",
        role: "Construction Manager",
        company: "Sobha Developers",
        avatar: "/images/partners/8.webp",
        quote: "Site reporting has never been easier. Our engineers use the mobile app to update progress with photos, and HO gets real-time updates. No more chasing site teams for reports.",
        rating: 5
      }
    ],
    businessImpact: [
      {
        value: "20%",
        label: "Cost Savings",
        description: "Reduction in project costs through real-time budget tracking",
        color: "#00F2FE",
        icon: "TrendingDown"
      },
      {
        value: "35%",
        label: "Project Speed",
        description: "Faster project completion with optimized scheduling",
        color: "#7F77DD",
        icon: "TrendingUp"
      },
      {
        value: "50%",
        label: "Billing Speed",
        description: "Faster RA billing and payment processing",
        color: "#F59E0B",
        icon: "Zap"
      },
      {
        value: "100%",
        label: "Compliance",
        description: "Complete statutory compliance and documentation",
        color: "#EC4899",
        icon: "Eye"
      }
    ],
    integrations: [
      { name: "AutoCAD", icon: "layout", category: "Design" },
      { name: "MS Project", icon: "calendar", category: "Planning" },
      { name: "Tally Prime", icon: "calculator", category: "Accounting" },
      { name: "GSTN Portal", icon: "file-text", category: "Government" },
      { name: "Razorpay", icon: "credit-card", category: "Payments" },
      { name: "WhatsApp Business", icon: "message-square", category: "Communication" },
    ]
  },
  {
    id: "distributors-erp",
    slug: "distributors-erp-software",
    category: "mx-erp",
    name: "Distributors ERP",
    tagline: "Built for Speed. Engineered for Growth.",
    href: "/products/distributors-erp-software",
    family: "Mx-ERP",
     headlineThin: "Distributors ERP",
     capHeadline: "Power Distribution with Mx-ERP",
     headlineBold: "ERP Software for Distributors Industry",
    headlineAccent: "Mx-ERP by ACGIL",
    subheadline: "Gain real-time visibility, eliminate operational bottlenecks, and scale your distribution business faster with data-driven insights.",
    headline: "Your Entire Distribution Business,",
    dashHeading: "Mx-ERP by ACGIL is a powerful Distributors ERP Software designed to streamline inventory, order management, supply chain, and GST compliance — all from one centralized platform.",
    challengesSubheadline: "Distribution businesses struggle with operational inefficiencies that directly impact profitability and growth.",
    PainPointsSubheading: "Distribution businesses struggle with operational inefficiencies that directly impact profitability and growth.",
    accentColor: "#2C78B9",
    dashboardType: "distributors",
    stats: [
      { value: "50,000+", label: "RETAILERS REACHED" },
      { value: "100%", label: "CLAIM RECOVERY" },
      { value: "3x", label: "SALES VELOCITY" }
    ],
    steps: [
      { num: "01", title: "Network Setup", description: "Map your principal companies, stockists, and retailer hierarchy.", bullets: ["Hierarchy mapping", "Principal setup", "Retailer DB"], duration: "Week 1" },
      { num: "02", title: "Route Planning", description: "Configure beats and routes for your sales team.", bullets: ["Beat mapping", "GPS setup", "Targets"], duration: "Week 2" },
      { num: "03", title: "Field Rollout", description: "Deploy the mobile app to your sales reps for live order taking.", bullets: ["App training", "Live tracking", "Order sync"], duration: "Week 3" }
    ],
    features: [
      { id: "inventory", title: "Inventory Management", description: "Multi-warehouse inventory tracking, batch & serial number management, and barcode integration." },
      { id: "sales", title: "Sales & Order Management", description: "Order processing automation, real-time tracking, and multi-channel sales management." },
      { id: "procurement", title: "Procurement & Vendor Management", description: "Automated purchase orders, vendor performance tracking, and GRN matching." },
      { id: "finance", title: "Finance & GST Management", description: "GST-ready billing & compliance, credit management, and financial reporting." },
      { id: "warehouse", title: "Warehouse & Logistics Management", description: "Warehouse bin management, dispatch tracking, and route optimization." },
      { id: "analytics", title: "MIS & Business Intelligence", description: "150+ reports & dashboards, KPI tracking, and multi-location insights." },
    ],
    capdescription: "Everything You Need to Run Distribution Smarter",
    capabilities: [
      {
        title: "Inventory Management",
        description: "Gain full control over inventory with real-time tracking across warehouses and distribution centers.",
        features: [
          "Multi-warehouse inventory tracking",
          "Batch & serial number management",
          "Barcode & RFID integration",
          "Auto stock alerts & replenishment"
        ],
        tag: "Inventory",
        widget: "inventory",
        accentColor: "#2C78B9",
        secondaryColor: "#059A8F"
      },
      {
        title: "Sales & Order Management",
        description: "Streamline your entire sales cycle from quotation to order fulfillment.",
        features: [
          "Order processing automation",
          "Real-time order tracking",
          "Multi-channel sales management",
          "Pricing & discount management"
        ],
        tag: "Sales",
        widget: "sales",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9"
      },
      {
        title: "Procurement & Vendor Management",
        description: "Simplify purchasing and vendor coordination with automated workflows.",
        features: [
          "Automated purchase orders",
          "Vendor performance tracking",
          "GRN & invoice matching",
          "Approval workflows"
        ],
        tag: "Procurement",
        widget: "vendor",
        accentColor: "#7F77DD",
        secondaryColor: "#059A8F"
      },
      {
        title: "Finance & GST Management",
        description: "Manage your financial operations with GST-ready billing and compliance.",
        features: [
          "GST-ready billing & compliance",
          "Accounts payable & receivable",
          "Credit management",
          "Financial reporting"
        ],
        tag: "Finance",
        widget: "finance",
        accentColor: "#059A8F",
        secondaryColor: "#7F77DD"
      },
      {
        title: "Warehouse & Logistics Management",
        description: "Optimize warehouse space and shipping logistics for faster dispatch.",
        features: [
          "Warehouse bin management",
          "Dispatch & delivery tracking",
          "Route optimization",
          "Real-time shipment visibility"
        ],
        tag: "Warehouse",
        widget: "warehouse",
        accentColor: "#2C78B9",
        secondaryColor: "#7F77DD"
      },
      {
        title: "MIS & Business Intelligence",
        description: "Get real-time insights with detailed reports and analytics dashboards.",
        features: [
          "150+ reports & dashboards",
          "KPI tracking",
          "Forecasting & analytics",
          "Multi-location insights"
        ],
        tag: "Analytics",
        widget: "mis",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9"
      }
    ],
    industriesServed: [
      "FMCG Distributors",
      "Pharma Distributors",
      "Electronics & Electrical Traders",
      "Wholesale Traders",
      "Retail Chain Suppliers",
      "Import & Export Businesses",
      "Automotive & Auto Components"
    ],
    integrations: [
      { name: "SAP/Oracle Sync", icon: "database", category: "Principal ERP Sync" },
      { name: "Oracle Cloud", icon: "cloud", category: "Enterprise" },
      { name: "Tally Prime", icon: "calculator", category: "Accounting" },
      { name: "WhatsApp Cloud", icon: "message-square", category: "Alerts & Notifications" },
      { name: "Google Maps", icon: "map", category: "Beat Optimization" },
      { name: "Razorpay/PayU", icon: "credit-card", category: "Payment Collection" },
      { name: "Delhivery", icon: "package", category: "Logistics" }
    ],
    painPoints: [
      { title: "Stock Imbalance", desc: "Overstocking or stock shortages due to poor demand forecasting." },
      { title: "Order Processing Delays", desc: "Manual processes leading to errors and delayed deliveries." },
      { title: "Complex Supply Chain", desc: "Difficulty managing suppliers, warehouses, and logistics." },
      { title: "GST & Compliance Issues", desc: "Errors in billing, taxation, and compliance reporting." },
      { title: "Lack of Real-Time Visibility", desc: "No centralized data for inventory, sales, and financial insights." },
    ],
    solutionHeadline: "How Distributors ERP Software Solves This",
    description: "Mx-ERP connects your entire distribution ecosystem into one intelligent system.",
    solutionFeatures: [
      "Real-time inventory tracking across all warehouses",
      "Automated order-to-delivery workflows",
      "Centralized control over procurement and sales",
      "Advanced analytics for smarter decision-making"
    ],
    faqs: [
      {
        q: "What is Distributors ERP Software?",
        a: "Distributors ERP Software helps manage inventory, orders, logistics, billing, and finance in one centralized system to improve efficiency and profitability."
      },
      {
        q: "Why do distributors need ERP software?",
        a: "It simplifies supply chain operations, reduces errors, improves inventory visibility, and enhances overall business performance."
      },
      {
        q: "Is Mx-ERP suitable for growing distribution businesses?",
        a: "Yes, it is scalable and supports both small distributors and large enterprises."
      },
      {
        q: "Does it support GST billing?",
        a: "Yes, it includes GST billing, compliance, and reporting features."
      },
      {
        q: "Can it manage multiple warehouses?",
        a: "Yes, it provides real-time multi-location inventory tracking."
      }
    ],
    ctaHeadline: "Supercharge Your Distribution Network",
    ctaSubtext: "Join top distributors streamlining their supply chain with Mx-ERP.",
    clients: [
      { id: "d1", name: "ACMA Distributors", logo: "/images/clients/acma-logo.webp" },
      { id: "d2", name: "AHPL Pharma Dist.", logo: "/images/clients/ahpl-logo.webp" },
      { id: "d3", name: "AIIMS Supply Chain", logo: "/images/clients/aiims-logo.webp" },
      { id: "d4", name: "Allahabad Traders", logo: "/images/clients/allahabad-bank-logo.webp" },
      { id: "d5", name: "ANT Networks", logo: "/images/clients/ant-logo.webp" },
      { id: "d6", name: "AOS Distribution", logo: "/images/clients/aos-logo.webp" },
      { id: "d7", name: "Arogyavaram Medical", logo: "/images/clients/arogyavaram-logo.webp" },
      { id: "d8", name: "Arora FMCG Dist.", logo: "/images/clients/arora-aromatics-logo.webp" }
    ],
    testimonials: [
      {
        id: 204,
        name: "Kiran Desai",
        role: "Managing Director",
        company: "Desai Distributors",
        avatar: "/images/partners/9.webp",
        quote: "ACGIL Distributors ERP transformed our 50-year old distribution business. Claim recovery improved from 60% to 100%, and we eliminated the monthly stock reconciliation headaches.",
        rating: 5
      },
      {
        id: 205,
        name: "Meera Nair",
        role: "Operations Head",
        company: "Kerala Pharma Distributors",
        avatar: "/images/partners/10.webp",
        quote: "The batch tracking and expiry alerts saved us ₹18 lakhs in expired stock last year. The pharma compliance features are exactly what we needed for DPCO adherence.",
        rating: 5
      },
      {
        id: 206,
        name: "Suresh Kumar",
        role: "Sales Manager",
        company: "South India FMCG Network",
        avatar: "/images/partners/11.webp",
        quote: "Our 150 salesmen now use the mobile app for order taking. Beat adherence improved by 40% and we have complete visibility into their daily activities.",
        rating: 5
      }
    ],
    businessImpact: [
      {
        value: "35%",
        label: "Inventory Accuracy",
        description: "Reduce losses and improve stock control",
        color: "#00F2FE",
        icon: "TrendingUp"
      },
      {
        value: "30%",
        label: "Faster Order Fulfillment",
        description: "Speed up deliveries and customer satisfaction",
        color: "#7F77DD",
        icon: "TrendingUp"
      },
      {
        value: "45%",
        label: "Operational Visibility",
        description: "Real-time insights across supply chain",
        color: "#F59E0B",
        icon: "Eye"
      },
      {
        value: "2X",
        label: "Faster Decision-Making",
        description: "Data-driven growth strategies",
        color: "#EC4899",
        icon: "Zap"
      }
    ],
    workflow: [
      { id: 1, title: "Order Placement", step: "Step 1", icon: "order", color: "#2C78B9", bgColor: "bg-indigo-500", shadow: "shadow-indigo-500/20" },
      { id: 2, title: "Inventory Allocation", step: "Step 2", icon: "inventory", color: "#2C78B9", bgColor: "bg-purple-500", shadow: "shadow-purple-500/20" },
      { id: 3, title: "Dispatch & Logistics", step: "Step 3", icon: "truck", color: "#2C78B9", bgColor: "bg-blue-500", shadow: "shadow-blue-500/20" },
      { id: 4, title: "Delivery & Billing", step: "Step 4", icon: "billing", color: "#2C78B9", bgColor: "bg-emerald-500", shadow: "shadow-emerald-500/20" },
      { id: 5, title: "Payment", step: "Step 5", icon: "finance", color: "#2C78B9", bgColor: "bg-teal-500", shadow: "shadow-teal-500/20" },
    ],
    comparison: [
      { feature: "Implementation Time", mxerp: "4–6 Weeks", others: "6–12 Months" },
      { feature: "Cost", mxerp: "Low", others: "High" },
      { feature: "Customization", mxerp: "High", others: "Limited" },
      { feature: "GST Compliance", mxerp: "Built-in", others: "Add-on" },
      { feature: "Industry Focus", mxerp: "Distribution-Specific", others: "Generic" },
      { feature: "Deployment", mxerp: "Cloud + On-Premise", others: "Cloud Only" },
      { feature: "Support", mxerp: "24/7 India Support", others: "Limited" }
    ],
    seo: {
      title: "Distributors ERP Software | Distribution Management ERP - Acgil",
      description: "Optimize distribution operations with ACGIL’s ERP software for distributors, supporting inventory, sales, purchasing, order processing, finance, and distributor management.",
      keywords: "distributors erp software, erp for distributors, distribution management software, global distribution erp, inventory management erp, supply chain erp, gst erp software, cloud erp for distributors",
      canonical: SITE_URL + "/products/distributors-erp-software",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      ogTitle: "Distributors ERP Software | Distribution Management ERP - Acgil",
      ogDescription: "Optimize distribution operations with ACGIL’s ERP software for distributors, supporting inventory, sales, purchasing, order processing, finance, and distributor management.",
      ogUrl: SITE_URL + "/products/distributors-erp-software",
      ogImage: SITE_URL + "/images/distributors-erp.jpg",
      hreflang: {
        "en": SITE_URL + "/products/distributors-erp-software",
        "x-default": SITE_URL + "/products/distributors-erp-software"
      },
      faqSchema: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What is Distributors ERP Software?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Distributors ERP Software helps manage inventory, orders, logistics, billing, and finance in one centralized system to improve efficiency and profitability."
            }
          },
          {
            "@type": "Question",
            "name": "Why do distributors need ERP software?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "It simplifies supply chain operations, reduces errors, improves inventory visibility, and enhances overall business performance."
            }
          },
          {
            "@type": "Question",
            "name": "Is Mx-ERP suitable for growing distribution businesses?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, it is scalable and supports both small distributors and large enterprises."
            }
          },
          {
            "@type": "Question",
            "name": "Does it support GST billing?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, it includes GST billing, compliance, and reporting features."
            }
          },
          {
            "@type": "Question",
            "name": "Can it manage multiple warehouses?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, it provides real-time multi-location inventory tracking."
            }
          }
        ]
      }
    }
  },
  {
    id: "retail-erp",
    slug: "retail-erp-software",
    category: "mx-erp",
    name: "Retail ERP",
    href: "/products/retail-erp-software",
    family: "Mx-ERP",
    headlineThin: "Retail ERP",
    tagline: "Built for Every Store - From Single Outlets to 50+ Store Chains",
    headlineBold: "Retail ERP",
    headlineAccent: "Built for Every Store",
    subtitle: "Unified POS, inventory, and loyalty management for single stores to 50+ outlet retail chains.",
    challengesSubheadline: "Unified POS, inventory, and loyalty management for single stores to 50+ outlet retail chains.",
    accentColor: "#7F77DD",
    dashboardType: "retail",
    stats: [
      { value: "1,200+", label: "STORES POWERED" },
      { value: "99.9%", label: "UPTIME" },
      { value: "3 Sec", label: "BILLING SPEED" }
    ],
    steps: [
      { num: "01", title: "Central Setup", description: "Configure your head office, central warehouse, and store hierarchy.", bullets: ["HO setup", "SKU master", "Pricing"], duration: "Week 1" },
      { num: "02", title: "Hardware Integration", description: "Connect barcode scanners, receipt printers, and weigh scales.", bullets: ["Peripherals", "Network setup", "Testing"], duration: "Week 2" },
      { num: "03", title: "Store Rollout", description: "Train cashiers and go live store-by-store with centralized control.", bullets: ["Cashier training", "Live billing", "Sync check"], duration: "Week 3" }
    ],
    features: [
      { id: "pos", title: "Fast POS", description: "Keyboard-friendly fast billing interface.", componentType: "pos", colSpan: 2 },
      { id: "multi", title: "Multi-Store", description: "Real-time sync across all retail outlets.", componentType: "multi", colSpan: 1 },
      { id: "loyalty", title: "Loyalty Program", description: "Points, tiers, and targeted SMS campaigns.", componentType: "loyalty", colSpan: 1 },
      { id: "inventory", title: "Store Inventory", description: "Auto-replenishment from central warehouse.", componentType: "inventory", colSpan: 1 },
      { id: "omni", title: "Omnichannel", description: "Sync offline stock with e-commerce store.", componentType: "omni", colSpan: 2 },
      { id: "promo", title: "Promotions", description: "BOGO, happy hours, and dynamic pricing.", componentType: "promo", colSpan: 1 },
      { id: "audit", title: "Stock Audit", description: "Mobile-based physical stock verification.", componentType: "audit", colSpan: 1 }
    ],
    capabilities: [
      {
        title: "POS Terminal",
        description: "Fast billing with barcode, touch, and mobile support",
        features: [
          "Keyboard-friendly fast billing",
          "Offline billing capability",
          "Hold and recall bills",
          "Integration with weigh scales"
        ],
        tag: "Billing",
        widget: "pos",
        accentColor: "#7F77DD",
        secondaryColor: "#059A8F"
      },
      {
        title: "Multi-store Management",
        description: "Centralized stock and pricing across all outlets",
        features: [
          "Real-time stock visibility",
          "Automated store replenishment",
          "Inter-branch transfers",
          "Centralized price updates"
        ],
        tag: "Operations",
        widget: "multi",
        accentColor: "#059A8F",
        secondaryColor: "#7F77DD"
      },
      {
        title: "Loyalty & CRM",
        description: "Customer points, birthday offers, SMS campaigns",
        features: [
          "Tier-based loyalty points",
          "Automated birthday/anniversary SMS",
          "Targeted promotional campaigns",
          "Customer purchase history"
        ],
        tag: "Marketing",
        widget: "crm",
        accentColor: "#2C78B9",
        secondaryColor: "#7F77DD"
      },
      {
        title: "Franchise Management",
        description: "Royalty calculation and performance dashboards",
        features: [
          "Automated royalty billing",
          "Franchise profitability reports",
          "Standardized HO processes",
          "Consolidated franchise dashboard"
        ],
        tag: "Expansion",
        widget: "franchise",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9"
      },
      {
        title: "E-commerce Sync",
        description: "Real-time sync with Shopify, WooCommerce, Flipkart",
        features: [
          "Omnichannel inventory sync",
          "Centralized order processing",
          "Unified customer database",
          "Marketplace integrations"
        ],
        tag: "Omnichannel",
        widget: "ecom",
        accentColor: "#7F77DD",
        secondaryColor: "#2C78B9"
      },
      {
        title: "GST Billing",
        description: "GSTIN-wise billing across all stores",
        features: [
          "State-wise GST handling",
          "E-invoicing integration",
          "GSTR-1, 2, 3B ready reports",
          "HSN/SAC code management"
        ],
        tag: "Compliance",
        widget: "gst",
        accentColor: "#2C78B9",
        secondaryColor: "#059A8F"
      }
    ],
    industriesServed: [
      "Supermarkets & Grocery Stores",
      "Apparel & Footwear Chains",
      "Electronics & Appliance Retail",
      "Pharmacy & Health Stores",
      "Jewelry & Lifestyle Boutiques",
      "FMCG Multi-Brand Outlets",
      "Furniture & Home Decor"
    ],
    painPoints: [
      { title: "Slow Billing", desc: "Long queues during peak hours due to laggy POS systems." },
      { title: "Stock Pilferage", desc: "Unexplained inventory shrinkages and lack of store audits." },
      { title: "Siloed Stores", desc: "Inability to track stock or sales across multiple outlets." },
      { title: "Static Pricing", desc: "Difficulty in pushing centralized price updates and promotions." },
      { title: "Low Loyalty", desc: "No data-driven way to track and reward repeat customers." },
    ],
    solutions: [
      { title: "Fast POS", desc: "Keyboard-friendly interface for 3-second billing even offline." },
      { title: "Digital Audits", desc: "Mobile-based physical stock verification to catch discrepancies." },
      { title: "Multi-Store Sync", desc: "Centralized HO control over all retail outlets and warehouses." },
      { title: "Dynamic Pricing", desc: "Push price changes and BOGO offers to all stores instantly." },
      { title: "CRM & Loyalty", desc: "Integrated loyalty points and SMS campaigns for better retention." },
    ],
    faqs: [
      {
        q: "How does the offline mode work for POS billing?",
        a: "The POS system continues to function completely offline. All transactions are stored locally and automatically sync with the central server when internet connectivity is restored, ensuring zero downtime."
      },
      {
        q: "Can I push price changes to all stores from head office?",
        a: "Yes, our centralized pricing engine allows you to update prices, apply promotions, and manage discounts across all stores instantly. Changes reflect immediately on all POS terminals."
      },
      {
        q: "Does it support size/color matrix for fashion retail?",
        a: "Full matrix support for apparel, footwear, and fashion accessories with variant-level inventory tracking, barcode generation, and seasonal stock management."
      },
      {
        q: "How are returns and exchanges handled across multiple stores?",
        a: "Cross-store returns are seamless - customers can return at any location and receive credit notes or exchanges. The system automatically updates inventory across the chain."
      },
      {
        q: "Can I track individual cashier performance?",
        a: "Yes, detailed shift-wise reports show transaction counts, void patterns, cancellations, and sales performance for each cashier with exportable analytics."
      },
      {
        q: "How does auto-replenishment work for stores?",
        a: "The system monitors stock levels at each store and automatically generates purchase orders or transfer indents based on minimum stock rules and sales velocity."
      },
      {
        q: "Is franchise management included?",
        a: "Yes, complete franchise module with royalty calculations, performance dashboards, and automated reporting for franchise owners."
      }
    ],
    ctaHeadline: "Scale Your Retail Chain Faster",
    ctaSubtext: "Join 1200+ stores delivering seamless checkout experiences.",
    clients: [
      { id: "r1", name: "Acma Supermart", logo: "/images/clients/acma-logo.webp" },
      { id: "r2", name: "AHPL Retail", logo: "/images/clients/ahpl-logo.webp" },
      { id: "r3", name: "AIIMS Pharmacy", logo: "/images/clients/aiims-logo.webp" },
      { id: "r4", name: "Allahabad Stores", logo: "/images/clients/allahabad-bank-logo.webp" },
      { id: "r5", name: "ANT Boutiques", logo: "/images/clients/ant-logo.webp" },
      { id: "r6", name: "AOS Electronics", logo: "/images/clients/aos-logo.webp" },
      { id: "r7", name: "Arogyavaram Health", logo: "/images/clients/arogyavaram-logo.webp" },
      { id: "r8", name: "Arora Apparels", logo: "/images/clients/arora-aromatics-logo.webp" }
    ],
    testimonials: [
      {
        id: 207,
        name: "Arjun Malhotra",
        role: "IT Director",
        company: "Reliance Retail",
        avatar: "/images/partners/12.webp",
        quote: "ACGIL Retail ERP handles our 800+ stores with ease. The offline capability is crucial for our locations with unreliable internet, and the 3-second billing speed keeps queues moving even during peak hours.",
        rating: 5
      },
      {
        id: 208,
        name: "Pooja Verma",
        role: "Operations Manager",
        company: "Future Group",
        avatar: "/images/partners/13.webp",
        quote: "The loyalty program integration increased our repeat customer rate by 35%. The mobile audit feature helped us reduce shrinkage from 3.2% to 1.8% across our stores.",
        rating: 5
      },
      {
        id: 209,
        name: "Ravi Shankar",
        role: "Store Owner",
        company: "Fashion Forward",
        avatar: "/images/partners/14.webp",
        quote: "As a multi-brand retailer with 15 stores, the centralized control and real-time inventory sync has been a game-changer. We finally have visibility into what's selling where.",
        rating: 5
      }
    ],
    businessImpact: [
      {
        value: "3 Sec",
        label: "Billing Speed",
        description: "Ultra-fast checkout even during peak hours",
        color: "#00F2FE",
        icon: "Zap"
      },
      {
        value: "99.9%",
        label: "Uptime",
        description: "Reliable POS system with offline capability",
        color: "#7F77DD",
        icon: "Eye"
      },
      {
        value: "40%",
        label: "Shrinkage",
        description: "Reduced inventory pilferage with digital audits",
        color: "#F59E0B",
        icon: "TrendingDown"
      },
      {
        value: "30%",
        label: "Customer Retention",
        description: "Improved loyalty with integrated CRM",
        color: "#EC4899",
        icon: "TrendingUp"
      }
    ],
    integrations: [
      { name: "Pine Labs", icon: "credit-card", category: "POS" },
      { name: "Paytm", icon: "wallet", category: "Payments" },
      { name: "Shiprocket", icon: "rocket", category: "Logistics" },
      { name: "Tally Prime", icon: "calculator", category: "Accounting" },
      { name: "Mailchimp", icon: "mail", category: "Marketing" },
      { name: "WhatsApp Business", icon: "message-square", category: "CRM" },
    ],
    seo: {
  title: "Retail ERP Software | Retail Business Management Solution - Acgil",
  description: "Manage retail operations with ACGIL’s Retail ERP Software for inventory, sales, purchasing, billing, customer management, and day-to-day business processes.",
  keywords: "retail erp software, erp for retail business, retail management software, pos erp software, inventory management retail erp, gst billing software, multi store retail software, cloud retail erp",
  canonical: SITE_URL + "/products/retail-erp-software",
  robots: "index, follow, max-snippet:-1, max-image-preview:large",
  ogTitle: "Retail ERP Software | Retail Business Management Solution - Acgil",
  ogDescription: "Manage retail operations with ACGIL’s Retail ERP Software for inventory, sales, purchasing, billing, customer management, and day-to-day business processes.",
  ogUrl: SITE_URL + "/products/retail-erp-software",
  ogImage: SITE_URL + "/images/retail-erp.jpg",
  hreflang: {
    "en": SITE_URL + "/products/retail-erp-software",
    "x-default": SITE_URL + "/products/retail-erp-software"
  },
  faqSchema: {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is Retail ERP Software?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Retail ERP Software helps retailers manage inventory, billing, sales, POS, customer data, and finance in one centralized system for better efficiency."
        }
      },
      {
        "@type": "Question",
        "name": "Why do retailers need ERP software?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "It improves inventory control, simplifies billing, enhances customer experience, and helps streamline retail operations across stores."
        }
      },
      {
        "@type": "Question",
        "name": "Is Mx-ERP suitable for multi-store retail businesses?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, Mx-ERP supports multi-store operations with centralized control, real-time stock visibility, and unified reporting."
        }
      },
      {
        "@type": "Question",
        "name": "Does it support GST billing?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, it includes GST billing, tax compliance, invoicing, and reporting features."
        }
      },
      {
        "@type": "Question",
        "name": "Can it integrate with POS systems?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, Mx-ERP integrates with POS systems to ensure seamless billing, sales tracking, and inventory synchronization."
        }
      }
    ]
  }
}
  },
  {
    id: "buying-house",
    slug: "buying-house-erp-software",
    category: "mx-erp",
    name: "Buying House ERP",
    tagline: "Built for Global Sourcing. Engineered for Growth.",
    href: "/products/buying-house-erp-software",
    family: "Mx-ERP",
     headlineThin: "Buying House ERP",
     capHeadline: "Simplify Buying House Operations with Mx-ERP",
     headlineBold: "Buying House ERP Software",
    headlineAccent: "Mx-ERP by ACGIL",
    subheadline: "Gain real-time visibility, reduce delays, and streamline your sourcing and merchandising operations globally",
    headline: "Your Entire Buying House Operations,",
    dashHeading: "Mx-ERP by ACGIL is a powerful Buying House ERP Software designed to manage vendor coordination, order tracking, production follow-ups, quality checks, and export documentation — all in one centralized system.",
    challengesSubheadline: "Buying houses operate in complex environments with multiple stakeholders.",
    PainPointsSubheading: "Buying houses operate in complex environments with multiple stakeholders.",
    accentColor: "#059A8F",
    dashboardType: "buying-house",
    stats: [
      { value: "10M+", label: "GARMENTS SHIPPED" },
      { value: "0", label: "MISSED DEADLINES" },
      { value: "100%", label: "QC COMPLIANCE" }
    ],
    steps: [
      { num: "01", title: "Buyer Configuration", description: "Set up buyer profiles, tech packs, and compliance standards.", bullets: ["Buyer master", "Tech packs", "AQL standards"], duration: "Week 1" },
      { num: "02", title: "Vendor Onboarding", description: "Map factories, their capacities, and compliance certifications.", bullets: ["Factory profiles", "Capacity mapping", "Compliance checks"], duration: "Week 2" },
      { num: "03", title: "Order Pipeline Live", description: "Start tracking sampling, production, and shipments in real-time.", bullets: ["TNA calendar", "Live tracking", "Alerts setup"], duration: "Week 3" }
    ],
    features: [
      { id: "vendor", title: "Vendor Management", description: "Supplier database and vendor performance tracking." },
      { id: "order", title: "Order Management", description: "End-to-end order lifecycle tracking and timeline monitoring." },
      { id: "production", title: "Production Tracking", description: "Real-time production status updates and milestone tracking." },
      { id: "qc", title: "Quality Control (QC)", description: "Inspection tracking, quality reports, and compliance checks." },
      { id: "docs", title: "Documentation & Export Management", description: "Automated invoice, packing list, and export document generation." },
      { id: "reporting", title: "MIS & Reporting", description: "Real-time dashboards, performance reports, and business insights." },
    ],
    capdescription: "Everything You Need to Manage Buying House Operations",
    capabilities: [
      {
        title: "Vendor Management",
        description: "Manage vendors, track supplier performance, and centralize communication for better sourcing.",
        features: [
          "Supplier database",
          "Vendor performance tracking",
          "Communication tracking"
        ],
        tag: "Vendors",
        widget: "vendor",
        accentColor: "#2C78B9",
        secondaryColor: "#059A8F"
      },
      {
        title: "Order Management",
        description: "Follow orders through their entire lifecycle with automated timeline monitoring and PO management.",
        features: [
          "Order lifecycle tracking",
          "PO management",
          "Timeline monitoring"
        ],
        tag: "Orders",
        widget: "planning",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9"
      },
      {
        title: "Production Tracking",
        description: "Get real-time production status updates and automated alerts for milestone delays.",
        features: [
          "Production status updates",
          "Milestone tracking",
          "Delay alerts"
        ],
        tag: "Production",
        widget: "production",
        accentColor: "#7F77DD",
        secondaryColor: "#059A8F"
      },
      {
        title: "Quality Control (QC)",
        description: "Maintain high standards with inspection tracking, automated quality reports, and compliance checks.",
        features: [
          "Inspection tracking",
          "Quality reports",
          "Compliance checks"
        ],
        tag: "Quality",
        widget: "gst",
        accentColor: "#059A8F",
        secondaryColor: "#7F77DD"
      },
      {
        title: "Documentation & Export Management",
        description: "Auto-generate commercial invoices, packing lists, and other regulatory export paperwork with ease.",
        features: [
          "Invoice & packing list",
          "Export documentation",
          "Compliance management"
        ],
        tag: "Logistics",
        widget: "billing",
        accentColor: "#2C78B9",
        secondaryColor: "#7F77DD"
      },
      {
        title: "MIS & Reporting",
        description: "Visualize operations on real-time dashboards with performance metrics and business growth insights.",
        features: [
          "Real-time dashboards",
          "Performance reports",
          "Business insights"
        ],
        tag: "Analytics",
        widget: "mis",
        accentColor: "#7F77DD",
        secondaryColor: "#059A8F"
      }
    ],
    industriesServed: [
      "Apparel & Garment Buying",
      "Home Furnishing Sourcing",
      "Footwear & Accessories",
      "Handicrafts & Decor",
      "Hardgoods Buying Houses",
      "Leather Goods Sourcing",
      "Textile Export Agencies"
    ],
    painPoints: [
      { title: "Vendor Coordination Issues", desc: "Managing multiple suppliers leads to delays and miscommunication." },
      { title: "Order Tracking Gaps", desc: "No real-time visibility across order lifecycle." },
      { title: "Quality Control Problems", desc: "Manual QC processes increase rejection rates." },
      { title: "Documentation Errors", desc: "Export and compliance paperwork mistakes cause delays." },
      { title: "Lack of Centralized Data", desc: "No single system for orders, vendors, and production." },
    ],
    solutionHeadline: "How Buying House ERP Software Solves This",
    description: "Mx-ERP connects your entire sourcing and production workflow into one system.",
    solutionFeatures: [
      "Real-time order tracking",
      "Vendor & supplier management",
      "Quality control automation",
      "Centralized documentation system"
    ],
    faqs: [
      {
        q: "What is Buying House ERP Software?",
        a: "Buying House ERP Software helps manage vendors, orders, production, quality checks, and export documentation in one system."
      },
      {
        q: "Why do buying houses need ERP software?",
        a: "It improves coordination, reduces errors, and provides real-time visibility across sourcing operations."
      },
      {
        q: "Can it manage multiple vendors?",
        a: "Yes, it supports vendor tracking, performance monitoring, and communication."
      },
      {
        q: "Does it support quality control?",
        a: "Yes, it includes QC tracking, inspection reports, and compliance features."
      },
      {
        q: "Is it suitable for export businesses?",
        a: "Yes, it supports export documentation and global sourcing workflows."
      }
    ],
    ctaHeadline: "Streamline Your Sourcing Operations",
    ctaSubtext: "Join top buying houses delivering on-time, every time.",
    clients: [
      { id: "bh1", name: "ACMA Sourcing", logo: "/images/clients/acma-logo.webp" },
      { id: "bh2", name: "AHPL Buying", logo: "/images/clients/ahpl-logo.webp" },
      { id: "bh3", name: "AIIMS Exports", logo: "/images/clients/aiims-logo.webp" },
      { id: "bh4", name: "Allahabad Textiles", logo: "/images/clients/allahabad-bank-logo.webp" },
      { id: "bh5", name: "ANT Sourcing", logo: "/images/clients/ant-logo.webp" },
      { id: "bh6", name: "AOS Garments", logo: "/images/clients/aos-logo.webp" },
      { id: "bh7", name: "Arogyavaram Exports", logo: "/images/clients/arogyavaram-logo.webp" },
      { id: "bh8", name: "Arora Buying House", logo: "/images/clients/arora-aromatics-logo.webp" }
    ],
    testimonials: [
      {
        id: 210,
        name: "Neha Agarwal",
        role: "Sourcing Director",
        company: "Global Fashions Ltd",
        avatar: "/images/partners/15.webp",
        quote: "ACGIL Buying House ERP eliminated our TNA tracking nightmares. We went from 30% late deliveries to zero missed deadlines in 6 months. The factory portal alone saved us 15 hours per week in follow-ups.",
        rating: 5
      },
      {
        id: 211,
        name: "Rajiv Mehta",
        role: "Managing Partner",
        company: "Indus Sourcing",
        avatar: "/images/partners/16.webp",
        quote: "The sample tracking and tech pack management features are exceptional. Our sample approval cycle reduced from 21 days to 7 days, and we have complete version control of all specifications.",
        rating: 5
      },
      {
        id: 212,
        name: "Anita Sharma",
        role: "Export Manager",
        company: "Texport Industries",
        avatar: "/images/partners/17.webp",
        quote: "Documentation that used to take 2 days now happens in 2 hours. The auto-generation of shipping documents with complete accuracy has eliminated errors and delays in our export shipments.",
        rating: 5
      }
    ],
    businessImpact: [
      {
        value: "40%",
        label: "Operational Efficiency",
        description: "Automate workflows and reduce manual work",
        color: "#00F2FE",
        icon: "TrendingUp"
      },
      {
        value: "35%",
        label: "Faster Order Execution",
        description: "Improve delivery timelines",
        color: "#7F77DD",
        icon: "TrendingUp"
      },
      {
        value: "50%",
        label: "Visibility",
        description: "Real-time tracking across vendors & orders",
        color: "#F59E0B",
        icon: "Eye"
      },
      {
        value: "2X",
        label: "Faster Decisions",
        description: "Data-driven business growth",
        color: "#EC4899",
        icon: "Zap"
      }
    ],
    workflow: [
      { id: 1, title: "Buyer Order", step: "Step 1", icon: "order", color: "#2C78B9", bgColor: "bg-indigo-500", shadow: "shadow-indigo-500/20" },
      { id: 2, title: "Vendor Allocation", step: "Step 2", icon: "vendor", color: "#2C78B9", bgColor: "bg-purple-500", shadow: "shadow-purple-500/20" },
      { id: 3, title: "Production Tracking", step: "Step 3", icon: "production", color: "#2C78B9", bgColor: "bg-blue-500", shadow: "shadow-blue-500/20" },
      { id: 4, title: "Quality Inspection", step: "Step 4", icon: "qc", color: "#2C78B9", bgColor: "bg-emerald-500", shadow: "shadow-emerald-500/20" },
      { id: 5, title: "Shipment & Documentation", step: "Step 5", icon: "ship", color: "#2C78B9", bgColor: "bg-teal-500", shadow: "shadow-teal-500/20" }
    ],
    comparison: [
      { feature: "Vendor Management", mxerp: "Advanced", others: "Limited" },
      { feature: "Order Tracking", mxerp: "Real-time", others: "Manual" },
      { feature: "QC Management", mxerp: "Built-in", others: "External" },
      { feature: "Documentation", mxerp: "Automated", others: "Manual" },
      { feature: "Visibility", mxerp: "High", others: "Low" }
    ],
    seo: {
      title: "Buying House ERP Software | Apparel Buying House Management - Acgil",
      description: "Streamline buying house operations with ACGIL’s ERP software for apparel businesses, covering order management, sourcing, production tracking, inventory, costing, and reporting.",
      keywords: "buying house erp software, sourcing erp software, vendor management erp, export management software, apparel buying house erp, qc management software, cloud erp for buying house",
      canonical: SITE_URL + "/products/buying-house-erp-software",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      ogTitle: "Buying House ERP Software | Apparel Buying House Management - Acgil",
      ogDescription: "Streamline buying house operations with ACGIL’s ERP software for apparel businesses, covering order management, sourcing, production tracking, inventory, costing, and reporting.",
      ogUrl: SITE_URL + "/products/buying-house-erp-software",
      ogImage: SITE_URL + "/images/buying-house-erp.jpg",
      hreflang: {
        "en": SITE_URL + "/products/buying-house-erp-software",
        "x-default": SITE_URL + "/products/buying-house-erp-software"
      },
      faqSchema: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What is Buying House ERP Software?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Buying House ERP Software helps manage vendors, orders, production, quality checks, and export documentation in one system."
            }
          },
          {
            "@type": "Question",
            "name": "Why do buying houses need ERP software?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "It improves coordination, reduces errors, and provides real-time visibility across sourcing operations."
            }
          },
          {
            "@type": "Question",
            "name": "Can it manage multiple vendors?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, it supports vendor tracking and performance monitoring."
            }
          },
          {
            "@type": "Question",
            "name": "Does it support quality control?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, it includes QC tracking, inspection reports, and compliance features."
            }
          },
          {
            "@type": "Question",
            "name": "Is it suitable for export businesses?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, it supports export documentation and global sourcing workflows."
            }
          }
        ]
      }
    }
  },
  {
    id: "ecommerce",
    slug: "ecommerce-erp-software",
    category: "mx-erp",
    name: "E-commerce ERP",
    tagline: "Built for omnichannel retail. Engineered for growth.",
    href: "/products/ecommerce-erp-software",
    family: "Mx-ERP",
     headlineThin: "E-commerce ERP",
     capHeadline: "Run E-commerce Smarter with Mx-ERP",
     headlineBold: "E-commerce ERP Software",
    headlineAccent: "Mx-ERP by ACGIL",
    subheadline: "Get real-time visibility, automate operations, and scale your online business faster across multiple channels like Amazon, Flipkart, Shopify, and more.",
    headline: "Your Entire E-commerce Business,",
    dashHeading: "Mx-ERP by ACGIL is a powerful E-commerce ERP Software designed to manage inventory, orders, marketplaces, payments, and logistics — all in one centralized platform.",
    challengesSubheadline: "E-commerce businesses deal with complex operations that impact growth and customer experience",
    PainPointsSubheading: "E-commerce businesses deal with complex operations that impact growth and customer experience.",
    accentColor: "#2C78B9",
    dashboardType: "ecommerce",
    stats: [
      { value: "5M+", label: "ORDERS SYNCED" },
      { value: "99.9%", label: "INVENTORY ACCURACY" },
      { value: "24/7", label: "AUTO-SYNC" }
    ],
    steps: [
      { num: "01", title: "Channel Integration", description: "Connect your seller accounts via API in minutes.", bullets: ["Amazon sync", "Shopify connect", "API setup"], duration: "Day 1" },
      { num: "02", title: "Inventory Master", description: "Centralize your stock count to prevent overselling.", bullets: ["Stock mapping", "Buffer rules", "Warehouse config"], duration: "Day 2" },
      { num: "03", title: "Automated Fulfillment", description: "Set up auto-fetching of orders and shipping labels.", bullets: ["Order routing", "Label generation", "Dispatch setup"], duration: "Day 3" }
    ],
    features: [
      { id: "multi", title: "Multi-channel Integration", description: "Connect marketplaces, web stores, and POS systems in real-time." },
      { id: "sync", title: "Automated Order Processing", description: "Fetch orders from all channels and route them automatically." },
      { id: "inventory", title: "Inventory Sync & Management", description: "Real-time inventory updates across channels to prevent overselling." },
      { id: "ship", title: "Shipping & Logistics Integration", description: "Print shipping labels and generate courier details automatically." },
      { id: "returns", title: "Return & RTO Management", description: "Dedicated workflow for returns, RTOs, and inventory restocking." },
      { id: "reporting", title: "Financial & Tax Compliance", description: "Automated GST invoicing, e-way bills, and payment reconciliation." },
    ],
    capdescription: "Everything You Need to Manage E-commerce Operations",
    capabilities: [
      {
        title: "Inventory Management",
        description: "Manage inventory efficiently across marketplaces and warehouses with complete real-time visibility.",
        features: [
          "Real-time stock sync across marketplaces",
          "Multi-warehouse management",
          "SKU & product catalog management",
          "Auto stock alerts"
        ],
        tag: "Inventory",
        widget: "inventory",
        accentColor: "#2C78B9",
        secondaryColor: "#059A8F"
      },
      {
        title: "Order Management",
        description: "Automate and streamline your complete order processing workflow from one centralized dashboard.",
        features: [
          "Centralized order dashboard",
          "Automated order processing",
          "Bulk order handling",
          "Order tracking"
        ],
        tag: "Orders",
        widget: "orders",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9"
      },
      {
        title: "Marketplace Integration",
        description: "Connect and manage all marketplaces and sales channels from one platform.",
        features: [
          "Amazon, Flipkart, Shopify integration",
          "Multi-channel sales tracking",
          "Product listing management",
          "Pricing & discount automation"
        ],
        tag: "Channels",
        widget: "planning",
        accentColor: "#7F77DD",
        secondaryColor: "#059A8F"
      },
      {
        title: "Returns & Refund Management",
        description: "Simplify return workflows and refund handling with automated tracking and analysis.",
        features: [
          "RTO management",
          "Automated refund processing",
          "Return tracking",
          "Loss analysis"
        ],
        tag: "Returns",
        widget: "returns",
        accentColor: "#059A8F",
        secondaryColor: "#7F77DD"
      },
      {
        title: "Finance & GST Management",
        description: "Manage GST compliance, reconciliation, and financial reporting from one system.",
        features: [
          "GST billing & invoicing",
          "Payment reconciliation",
          "Profit & loss tracking",
          "Financial reports"
        ],
        tag: "Finance",
        widget: "finance",
        accentColor: "#2C78B9",
        secondaryColor: "#7F77DD"
      },
      {
        title: "Logistics & Shipping",
        description: "Optimize shipping operations and delivery management with integrated logistics tools.",
        features: [
          "Courier integrations",
          "Shipping label generation",
          "Delivery tracking",
          "Route optimization"
        ],
        tag: "Logistics",
        widget: "allocation",
        accentColor: "#7F77DD",
        secondaryColor: "#059A8F"
      },
      {
        title: "MIS & Business Intelligence",
        description: "Make data-driven business decisions with advanced dashboards and analytics.",
        features: [
          "Real-time dashboards",
          "Sales analytics",
          "Demand forecasting",
          "Performance insights"
        ],
        tag: "Analytics",
        widget: "mis",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9"
      }
    ],
    integrations: [
      { name: "Amazon Seller API", category: "Marketplace Sync", icon: "shopping-bag" },
      { name: "Shiprocket", category: "Logistics & Fulfilment", icon: "truck" },
      { name: "Razorpay", category: "Payment Gateway", icon: "credit-card" },
      { name: "GST/IRP Portal", category: "E-invoicing Compliance", icon: "file-text" },
      { name: "Shopify / WooCommerce", category: "Storefront Integration", icon: "globe" }
    ],
    industriesServed: [
      "D2C Brands & Startups",
      "Electronics & Gadgets",
      "Fashion & Apparel E-tailers",
      "Health & Beauty Cosmetics",
      "Home & Kitchen Appliances",
      "FMCG Online Retailers",
      "Omnichannel Retail Brands"
    ],
    painPoints: [
      {
        title: "Inventory Mismatch",
        desc: "Overselling or stockouts due to poor synchronization across platforms."
      },
      {
        title: "Order Management Chaos",
        desc: "Manual handling leads to delays, errors, and cancellations."
      },
      {
        title: "Multi-Channel Complexity",
        desc: "Difficulty managing multiple marketplaces and websites."
      },
      {
        title: "Returns & Refund Issues",
        desc: "Handling returns (RTO) and refunds manually causes losses."
      },
      {
        title: "Lack of Real-Time Insights",
        desc: "No centralized dashboard for sales, orders, and profitability."
      }
    ],
    solutionHeadline: "How E-commerce ERP Software Solves This",
    description: "Mx-ERP integrates all your e-commerce operations into one intelligent system.",
    solutionFeatures: [
      "Real-time inventory sync across all platforms",
      "Automated order processing & fulfillment",
      "Centralized multi-channel management",
      "Advanced analytics for growth decisions"
    ],
    faqs: [
      {
        q: "What is E-commerce ERP Software?",
        a: "E-commerce ERP Software is an integrated system that helps online businesses manage inventory, orders, marketplaces, logistics, and finance in one platform."
      },
      {
        q: "Why do e-commerce businesses need ERP software?",
        a: "It automates operations, reduces manual errors, synchronizes inventory across channels, and improves overall efficiency and profitability."
      },
      {
        q: "Can E-commerce ERP integrate with marketplaces?",
        a: "Yes, it integrates with platforms like Amazon, Flipkart, Shopify, and more for centralized management."
      },
      {
        q: "Does it support returns and refunds management?",
        a: "Yes, it includes RTO tracking, return management, and automated refund processing."
      },
      {
        q: "Is it suitable for growing online businesses?",
        a: "Yes, it is scalable and supports startups, D2C brands, and large e-commerce enterprises."
      }
    ],
    ctaHeadline: "Scale Your E-commerce Empire",
    ctaSubtext: "Join top D2C brands automating their back-office with Mx-ERP.",
    clients: [
      { id: "e1", name: "ACMA D2C", logo: "/images/clients/acma-logo.webp" },
      { id: "e2", name: "AHPL Online", logo: "/images/clients/ahpl-logo.webp" },
      { id: "e3", name: "AIIMS Care", logo: "/images/clients/aiims-logo.webp" },
      { id: "e4", name: "Allahabad Ecom", logo: "/images/clients/allahabad-bank-logo.webp" },
      { id: "e5", name: "ANT Retail", logo: "/images/clients/ant-logo.webp" },
      { id: "e6", name: "AOS Digital", logo: "/images/clients/aos-logo.webp" },
      { id: "e7", name: "Arogyavaram Cart", logo: "/images/clients/arogyavaram-logo.webp" },
      { id: "e8", name: "Arora Cosmetics", logo: "/images/clients/arora-aromatics-logo.webp" }
    ],
    testimonials: [
      {
        id: 213,
        name: "Kabeer Biswas",
        role: "CEO",
        company: "D2C Brand",
        avatar: "/images/partners/18.webp",
        quote: "ACGIL E-commerce ERP solved our biggest headache - overselling. We went from 12-15 cancellations per day to zero. The inventory sync is so fast that we can now confidently run flash sales without worry.",
        rating: 5
      },
      {
        id: 214,
        name: "Priya Sharma",
        role: "Operations Head",
        company: "GlowSkin Cosmetics",
        avatar: "/images/partners/19.webp",
        quote: "The payment reconciliation feature alone saved us ₹4.2 lakhs in the first quarter. We finally have visibility into all the hidden charges and can dispute incorrect deductions with evidence.",
        rating: 5
      },
      {
        id: 215,
        name: "Rohit Khurana",
        role: "Founder",
        company: "FitGear Apparel",
        avatar: "/images/partners/20.webp",
        quote: "Shipping cost optimization reduced our logistics expenses by 28%. The system automatically selects the best courier for each order based on destination and package size.",
        rating: 5
      }
    ],
    businessImpact: [
      {
        value: "40%",
        label: "Inventory Accuracy",
        description: "Reduce overselling and stockouts",
        color: "#00F2FE",
        icon: "TrendingUp"
      },
      {
        value: "35%",
        label: "Faster Order Processing",
        description: "Improve delivery speed and customer experience",
        color: "#7F77DD",
        icon: "Zap"
      },
      {
        value: "50%",
        label: "Operational Efficiency",
        description: "Automate repetitive tasks",
        color: "#F59E0B",
        icon: "TrendingUp"
      },
      {
        value: "2X",
        label: "Faster Growth Decisions",
        description: "Data-driven scaling",
        color: "#EC4899",
        icon: "Eye"
      }
    ],
    workflow: [
      {
        id: 1,
        title: "Order Capture",
        step: "Step 1",
        icon: "order",
        color: "#2C78B9",
        bgColor: "bg-indigo-500",
        shadow: "shadow-indigo-500/20"
      },
      {
        id: 2,
        title: "Inventory Sync",
        step: "Step 2",
        icon: "sync",
        color: "#2C78B9",
        bgColor: "bg-purple-500",
        shadow: "shadow-purple-500/20"
      },
      {
        id: 3,
        title: "Order Processing",
        step: "Step 3",
        icon: "inventory",
        color: "#2C78B9",
        bgColor: "bg-blue-500",
        shadow: "shadow-blue-500/20"
      },
      {
        id: 4,
        title: "Packaging & Dispatch",
        step: "Step 4",
        icon: "truck",
        color: "#2C78B9",
        bgColor: "bg-emerald-500",
        shadow: "shadow-emerald-500/20"
      },
      {
        id: 5,
        title: "Delivery & Payment",
        step: "Step 5",
        icon: "returns",
        color: "#2C78B9",
        bgColor: "bg-teal-500",
        shadow: "shadow-teal-500/20"
      }
    ],
    comparison: [
      {
        feature: "Multi-Channel Integration",
        mxerp: "Yes",
        others: "Limited"
      },
      {
        feature: "Automation",
        mxerp: "High",
        others: "Low"
      },
      {
        feature: "Real-Time Sync",
        mxerp: "Yes",
        others: "No"
      },
      {
        feature: "Returns Management",
        mxerp: "Built-in",
        others: "Manual"
      },
      {
        feature: "Scalability",
        mxerp: "High",
        others: "Limited"
      },
      {
        feature: "Implementation Time",
        mxerp: "4–6 Weeks",
        others: "6–12 Months"
      }
    ],
    seo: {
      title: "Ecommerce ERP Software | Ecommerce Business Management - Acgil",
      description: "Ecommerce ERP software for online businesses with integrated order processing, inventory, sales, purchasing, customer data, accounting, and business reporting.",
      keywords: "e-commerce erp software, omnichannel erp software, inventory sync erp, order management software, multi-channel erp, shipping integration software, cloud erp for e-commerce",
      canonical: SITE_URL + "/products/ecommerce-erp-software",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      ogTitle: "Ecommerce ERP Software | Ecommerce Business Management - Acgil",
      ogDescription: "Ecommerce ERP software for online businesses with integrated order processing, inventory, sales, purchasing, customer data, accounting, and business reporting.",
      ogUrl: SITE_URL + "/products/ecommerce-erp-software",
      ogImage: SITE_URL + "/images/ecommerce-erp.jpg",
      hreflang: {
        "en": SITE_URL + "/products/ecommerce-erp-software",
        "x-default": SITE_URL + "/products/ecommerce-erp-software"
      },
      faqSchema: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "What is E-commerce ERP Software?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "E-commerce ERP Software helps manage orders, inventory, shipping, returns, and billing across all sales channels in one centralized dashboard."
            }
          },
          {
            "@type": "Question",
            "name": "Why do e-commerce brands need ERP software?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "It automates processes, prevents overselling, reduces shipping costs, and provides real-time business visibility."
            }
          },
          {
            "@type": "Question",
            "name": "Does it support Shopify and WooCommerce?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, it provides direct API integrations for Shopify, WooCommerce, and other major storefronts."
            }
          },
          {
            "@type": "Question",
            "name": "Can it sync with marketplaces like Amazon?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, it integrates with major marketplaces like Amazon and Flipkart for real-time order and stock updates."
            }
          },
          {
            "@type": "Question",
            "name": "Does it manage returns and RTOs?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes, it has dedicated workflows to manage customer returns, RTO tracking, and stock restocking."
            }
          }
        ]
      }
    }
  },
  {
    id: "hospital-management-erp-software",
    slug: "hospital-management-erp-software",
    category: "electra-his",
    name: "Hospital Management Software",
    tagline: "Built for Healthcare. Engineered for Better Patient Care.",
    image: "/images/products/mx-erp-hospital.webp",
    href: "/products/hospital-management-erp-software",
    badge: "Most Popular",
    color: "#059A8F",
    family: "Electra-HIS",
    keyWord: "Built",
    accentColor: "#059A8F",
    trustCount: "1000+",
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
     headlineThin: "Hospital Management Software",
     capHeadline: "Hospital Management System with Electra-HIS",
     headlineBold: "Hospital Management Information System",
    headlineAccent: "Electra-HIS by ACGIL",
    subheadline:
      "Electra-HIS by ACGIL is a powerful Hospital Management Software (HMIS) designed to manage patient records, billing, pharmacy, inventory, and hospital operations — all in one unified platform.",
    dashHeading:
      "Manage patients, appointments, billing, pharmacy, inventory, and staff from a single dashboard. Electra-HIS delivers real-time visibility into hospital performance, operations, and patient care.",
    PainPointsSubheading:
      "Hospitals and healthcare institutions face multiple operational challenges that impact efficiency and patient satisfaction.",
    painPoints: [
      { title: "Manual Record Errors", desc: "Patient data entry mistakes leading to care delays and compliance risks." },
      { title: "Billing Delays", desc: "Slow invoice generation and discharge processing affecting cash flow and patient experience." },
      { title: "Pharmacy Gaps", desc: "Poor inventory tracking causing stockouts, expired medicines, and revenue leakage." },
      { title: "Zero Real-Time Data", desc: "No centralized visibility into beds, staff, and operations across departments." },
      { title: "Siloed Staff Management", desc: "Inefficient scheduling and coordination impacting care quality and productivity." },
    ],
    solutionHeadline: "How Electra-HIS Solves These Challenges",
    description:
      "Electra-HIS automates and streamlines the entire hospital management process.",
    solutionFeatures: [
      "Digital patient record management (EMR/EHR)",
      "Automated billing and invoicing",
      "Real-time inventory and pharmacy tracking",
      "Centralized hospital management system",
    ],
    capdescription:
      "Advanced ERP modules designed to streamline hospital operations, improve patient care, and maximize efficiency.",
    capabilities: [
      {
        title: "Patient Management System",
        description:
          "Digitize patient records, automate appointments, and manage admission and discharge with complete patient history tracking.",
        features: [
          "Digital patient records (EMR/EHR)",
          "Appointment scheduling",
          "Admission & discharge management",
          "Patient history tracking",
        ],
        tag: "Patients",
        widget: "production",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9",
      },
      {
        title: "Billing & Financial Management",
        description:
          "Automate hospital billing, manage insurance and TPA claims, and ensure GST-compliant invoicing with real-time payment tracking.",
        features: [
          "Automated hospital billing",
          "Insurance & TPA management",
          "GST-compliant invoicing",
          "Payment tracking",
        ],
        tag: "Finance",
        widget: "billing",
        accentColor: "#2C78B9",
        secondaryColor: "#059A8F",
      },
      {
        title: "Pharmacy Management",
        description:
          "Track medicine stock in real time with batch and expiry management, prescription integration, and automated low-stock alerts.",
        features: [
          "Medicine stock tracking",
          "Batch & expiry management",
          "Prescription integration",
          "Automated alerts",
        ],
        tag: "Pharmacy",
        widget: "inventory",
        accentColor: "#7F77DD",
        secondaryColor: "#059A8F",
      },
      {
        title: "Inventory & Supply Chain",
        description:
          "Manage hospital inventory end-to-end with real-time tracking, vendor management, and automated stock replenishment.",
        features: [
          "Real-time inventory tracking",
          "Vendor management",
          "Auto stock replenishment",
          "Purchase management",
        ],
        tag: "Inventory",
        widget: "vendor",
        accentColor: "#059A8F",
        secondaryColor: "#7F77DD",
      },
      {
        title: "Doctor & Staff Management",
        description:
          "Streamline staff scheduling, payroll, and performance tracking with role-based access across all departments.",
        features: [
          "Staff scheduling",
          "Payroll integration",
          "Performance tracking",
          "Role-based access",
        ],
        tag: "HR",
        widget: "hr",
        accentColor: "#2C78B9",
        secondaryColor: "#7F77DD",
      },
      {
        title: "MIS & Reporting",
        description:
          "Make faster, data-driven decisions with hospital performance reports, revenue tracking, and real-time dashboards.",
        features: [
          "Hospital performance reports",
          "Revenue tracking",
          "Patient analytics",
          "Real-time dashboards",
        ],
        tag: "Analytics",
        widget: "mis",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9",
      },
    ],
    dashboardType: "hospital-erp",
    businessImpact: [
      {
        value: "50%",
        label: "Manual Errors",
        description: "Reduce errors with automated digital records",
        color: "#00F2FE",
        icon: "TrendingDown",
      },
      {
        value: "40%",
        label: "Faster Billing",
        description: "Accelerate discharge and invoicing processes",
        color: "#7F77DD",
        icon: "Zap",
      },
      {
        value: "30%",
        label: "Operational Efficiency",
        description: "Improve hospital-wide productivity and coordination",
        color: "#F59E0B",
        icon: "TrendingUp",
      },
      {
        value: "2X",
        label: "Better Patient Management",
        description: "Deliver faster, more accurate patient care",
        color: "#EC4899",
        icon: "Eye",
      },
    ],
    workflow: [
      { id: 1, title: "Patient Registration", step: "Step 1", icon: "registration", color: "#059A8F", bgColor: "bg-teal-500", shadow: "shadow-teal-500/20" },
      { id: 2, title: "Consultation", step: "Step 2", icon: "opd", color: "#059A8F", bgColor: "bg-blue-500", shadow: "shadow-blue-500/20" },
      { id: 3, title: "Diagnosis", step: "Step 3", icon: "lab", color: "#059A8F", bgColor: "bg-indigo-500", shadow: "shadow-indigo-500/20" },
      { id: 4, title: "Billing", step: "Step 4", icon: "finance", color: "#059A8F", bgColor: "bg-emerald-500", shadow: "shadow-emerald-500/20" },
      { id: 5, title: "Pharmacy", step: "Step 5", icon: "pharmacy", color: "#059A8F", bgColor: "bg-amber-500", shadow: "shadow-amber-500/20" },
      { id: 6, title: "Discharge", step: "Step 6", icon: "delivery", color: "#059A8F", bgColor: "bg-rose-500", shadow: "shadow-rose-500/20" },
      { id: 7, title: "Reports", step: "Step 7", icon: "reports", color: "#059A8F", bgColor: "bg-purple-500", shadow: "shadow-purple-500/20" },
    ],
    features: [
      { id: "patient", title: "Patient Management", description: "Digital EMR/EHR with appointment scheduling, admission, and discharge management." },
      { id: "billing", title: "Billing & Finance", description: "Automated hospital billing with TPA, insurance, and GST-compliant invoicing." },
      { id: "pharmacy", title: "Pharmacy Management", description: "Medicine stock tracking, batch & expiry management, and prescription integration." },
      { id: "inventory", title: "Inventory & Supply Chain", description: "Real-time inventory tracking with vendor management and auto replenishment." },
      { id: "hr", title: "Doctor & Staff Management", description: "Staff scheduling, payroll integration, and role-based access control." },
      { id: "mis", title: "MIS & Reporting", description: "Hospital performance reports, revenue tracking, and real-time dashboards." },
    ],
    headline: "Your Hospital Information System,",
    heroStats: [
      { label: "Active Clients", value: "1000+" },
      { label: "Years of Trust", value: "30+" },
      { label: "GST Ready", value: "100%" },
    ],
    mockupImage: "/images/products/hospital-erp-hero.png",
    solutions: [
      { title: "Digital patient records", desc: "EMR/EHR-based records accessible instantly across all departments." },
      { title: "Automated billing", desc: "Faster discharge with GST-compliant, insurance-ready invoicing." },
      { title: "Pharmacy control", desc: "Zero stockouts and expired medicine elimination with real-time tracking." },
      { title: "Centralized operations", desc: "Complete hospital control from one unified platform." },
      { title: "Real-time analytics", desc: "Performance dashboards and reports for faster management decisions." },
    ],
    roiEstimate: "₹8–15 lakhs/year",
    implementationSteps: [
      {
        num: "01",
        title: "Process Audit",
        description:
          "Our experts map your hospital workflows, department structures, and billing requirements to design the optimal configuration.",
        bullets: ["Workflow mapping", "Department setup", "Gap analysis"],
        image: "/images/products/audit.png",
        bg: "linear-gradient(135deg, #071428, #0d3060)",
      },
      {
        num: "02",
        title: "Seamless Integration",
        description:
          "We configure patient management, billing, pharmacy, and inventory modules to fit your hospital's exact needs with 100% data accuracy from day one.",
        bullets: ["EMR configuration", "Billing integration", "Data migration"],
        image: "/images/products/integration.png",
        bg: "linear-gradient(135deg, #071e1c, #0a3530)",
      },
      {
        num: "03",
        title: "Go-Live & Support",
        description:
          "Dedicated training for doctors, nurses, billing, and admin staff followed by on-site support during the first month to ensure zero downtime.",
        bullets: ["Staff training", "On-site support", "Hypercare period"],
        image: "/images/products/support.png",
        bg: "linear-gradient(135deg, #100d28, #1a1040)",
      },
    ],
    clients: [
      { id: "hm1", name: "ACMA Healthcare", logo: "/images/clients/acma-logo.webp" },
      { id: "hm2", name: "AHPL Hospitals", logo: "/images/clients/ahpl-logo.webp" },
      { id: "hm3", name: "AIIMS Research", logo: "/images/clients/aiims-logo.webp" },
      { id: "hm4", name: "Allahabad Medical", logo: "/images/clients/allahabad-bank-logo.webp" },
      { id: "hm5", name: "ANT Clinics", logo: "/images/clients/ant-logo.webp" },
      { id: "hm6", name: "AOS Diagnostics", logo: "/images/clients/aos-logo.webp" },
      { id: "hm7", name: "Arogyavaram Hospital", logo: "/images/clients/arogyavaram-logo.webp" },
      { id: "hm8", name: "Arora Health", logo: "/images/clients/arora-aromatics-logo.webp" },
    ],
    industry: "Healthcare",
    challengesIndustry: "Hospital Information System",
    testimonialsIndustry: "Hospital Management Software",
    heroImage: "/images/products/hospital-erp-hero.png",
    marqueeItems: [
      "GST Compliant",
      "EMR/EHR Ready",
      "TPA Billing",
      "Pharmacy Tracking",
      "Cloud Security",
      "ISO Certified",
    ],
    testimonials: [
      {
        id: 311,
        name: "Dr. Anil Sharma",
        role: "Medical Director",
        company: "Sharma Multi-Specialty Hospital",
        avatar: "/images/partners/1.png",
        quote:
          "Electra-HIS transformed our 200-bed hospital. Billing errors reduced by 50% and our discharge process went from 2 hours to 30 minutes. The real-time dashboards are a game changer for management decisions.",
        rating: 5,
      },
      {
        id: 312,
        name: "Rekha Nair",
        role: "CFO",
        company: "Nair Healthcare Group",
        avatar: "/images/partners/2.webp",
        quote:
          "The automated TPA and insurance billing saved us ₹18 lakhs in the first year. GST compliance is now fully automated — no more monthly reconciliation nightmares.",
        rating: 5,
      },
      {
        id: 313,
        name: "Dr. Suresh Patel",
        role: "Operations Head",
        company: "Patel Clinic Network",
        avatar: "/images/partners/3.webp",
        quote:
          "Managing pharmacy inventory across 5 branches was impossible before Electra-HIS. Now we have complete batch-level tracking with zero expired medicine losses.",
        rating: 5,
      },
      {
        id: 314,
        name: "Meena Verma",
        role: "Hospital Administrator",
        company: "Verma City Hospital",
        avatar: "/images/partners/4.webp",
        quote:
          "Staff scheduling and payroll that used to take 3 days now happens automatically. Our doctors and nurses spend more time on patients and less time on paperwork.",
        rating: 5,
      },
      {
        id: 315,
        name: "Dr. Rakesh Iyer",
        role: "CEO",
        company: "Iyer Health Institute",
        avatar: "/images/partners/5.webp",
        quote:
          "Operational efficiency improved by 30% in the first quarter. The patient management module alone has improved our OPD throughput by 40%. Highly recommended.",
        rating: 5,
      },
    ],
    integrations: [
      { name: "Tally Prime", icon: "calculator", category: "Accounting" },
      { name: "Razorpay", icon: "credit-card", category: "Payments" },
      { name: "GSTN Portal", icon: "file-text", category: "Government" },
      { name: "Ayushman Bharat / PMJAY", icon: "shield", category: "Government Health Scheme" },
      { name: "TPA / Insurance Portals", icon: "credit-card", category: "Insurance" },
      { name: "WhatsApp Business", icon: "message-square", category: "Communication" },
      { name: "E-Invoice", icon: "file-digit", category: "Government" },
      { name: "Zoho Books", icon: "book-open", category: "Accounting" },
    ],
    faqs: [
      {
        q: "What is Hospital Management Software (HMIS)?",
        a: "It is a system that helps manage patient records, billing, pharmacy, and hospital operations efficiently in one centralized platform.",
      },
      {
        q: "What is HMIS?",
        a: "HMIS (Hospital Management Information System) is software used to digitize and manage all hospital processes from patient registration to discharge.",
      },
      {
        q: "Does it support GST billing?",
        a: "Yes, it includes GST billing, automated tax calculations, and compliance reports.",
      },
      {
        q: "Can it manage pharmacy operations?",
        a: "Yes, it provides real-time inventory tracking, batch and expiry management, and prescription-based billing.",
      },
      {
        q: "Is it suitable for small clinics?",
        a: "Yes, Electra-HIS is scalable and works for small clinics, multi-specialty hospitals, and large healthcare groups.",
      },
      {
        q: "Can it manage patient records digitally?",
        a: "Yes, it supports EMR/EHR for complete digital patient records accessible across all departments.",
      },
      {
        q: "Does it handle insurance and TPA billing?",
        a: "Yes, it includes TPA management, insurance claim tracking, and cashless billing workflows.",
      },
      {
        q: "Is it cloud-based or on-premise?",
        a: "Electra-HIS supports both cloud-based and on-premise deployment options.",
      },
    ],
    industriesServed: [
      "Multi-Specialty Hospitals",
      "Single Specialty Clinics",
      "Nursing Homes & Day Care Centers",
      "Diagnostic & Imaging Centers",
      "Pharmacy Chains",
      "Ayurvedic & Wellness Centers",
      "Corporate Hospital Groups",
    ],
    comparison: [
      { feature: "Accuracy", mxerp: "High — Automated", others: "Error-prone" },
      { feature: "Speed", mxerp: "Fast", others: "Slow" },
      { feature: "Automation", mxerp: "Yes", others: "No" },
      { feature: "Tracking", mxerp: "Real-time", others: "Limited" },
      { feature: "Efficiency", mxerp: "High", others: "Low" }
    ],
    detailSections: [
      {
        id: "hospital-benefits",
        eyebrow: "Why Electra-HIS",
        title: "Benefits of Acgil Hospital Management Software",
        description:
          "Running a hospital means balancing finances, staff, stock, and compliance every single day. Acgil ERP gives management one connected view of the entire organization, so decisions are based on real numbers instead of guesswork.",
        layout: "benefit-grid",
        items: [
          {
            title: "Stronger Revenue Control",
            desc: "Automate billing, insurance and TPA claims, and collections tracking. Spot pending payments and missed charges early, and stop revenue leakage before it grows.",
            icon: "revenue",
          },
          {
            title: "Smarter Inventory and Purchasing",
            desc: "Track medicines, consumables, and equipment across stores in real time. Expiry alerts and purchase planning reduce wastage and prevent costly stock-outs.",
            icon: "inventory",
          },
          {
            title: "Simplified HR and Payroll",
            desc: "Manage staff attendance, shifts, leaves, and salary processing from one place. Less paperwork for the HR team and fewer payroll errors.",
            icon: "hr",
          },
          {
            title: "Integrated Accounts and Finance",
            desc: "Link every department's transactions directly to accounts. Get accurate ledgers, expense tracking, and profit reports without manual reconciliation.",
            icon: "accounts",
          },
          {
            title: "Multi-Branch Visibility",
            desc: "Monitor performance across all locations from a single dashboard. Compare departments, standardize processes, and keep every branch aligned.",
            icon: "branches",
          },
          {
            title: "Security and Compliance Readiness",
            desc: "Role-based access, audit trails, and secure data storage protect sensitive information and help you prepare for NABH and other regulatory audits.",
            icon: "compliance",
          },
        ],
      },
      {
        id: "hospital-opd-ipd",
        eyebrow: "OPD / IPD Suite",
        title: "Core Modules of Acgil Hospital Management Software",
        description:
          "From patient registration and appointments to inpatient care, billing, and discharge, Acgil Hospital Management Software helps hospitals manage essential healthcare operations through integrated modules, improving coordination among departments and supporting efficient patient care.",
        layout: "module-list",
        items: [
          {
            title: "OPD Management Module",
            desc: "Manage patient registration, appointment scheduling, doctor consultations, and outpatient records.",
            icon: "consultation",
          },
          {
            title: "IPD Management Module",
            desc: "Handle patient admission, bed and ward allocation, treatment records, and discharge management.",
            icon: "patient",
          },
          {
            title: "Pharmacy Management Module",
            desc: "Manage medicine inventory, prescriptions, medicine sales, stock levels, and expiry tracking.",
            icon: "pharmacy",
          },
          {
            title: "Hospital Billing Management Module",
            desc: "Manage patient billing, consultation charges, treatment expenses, payment records, and invoices.",
            icon: "billing",
          },
          {
            title: "Laboratory Management Module",
            desc: "Manage laboratory test requests, sample records, diagnostic results, and lab reports.",
            icon: "lab",
          },
          {
            title: "Inventory Management Module",
            desc: "Track medical supplies, hospital consumables, stock movements, purchases, and store inventory.",
            icon: "inventory",
          },
          {
            title: "Reports & Analytics Module",
            desc: "Generate hospital reports covering patient activity, revenue, billing, and operational performance.",
            icon: "reports",
          },
        ],
      },
    ],
    seo: {
      title: "Hospital Management Information System & Software | ACGIL",
      description: "Explore hospital management software, a powerful hospital management system and HMIS that connects clinical, admin, and financial data for better patient care.",
      keywords:
        "hospital management software, hospital management information system, hmis software, hospital information system, healthcare software, hospital billing software, patient management system, pharmacy management software",
      canonical: SITE_URL + "/products/hospital-management-erp-software",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      ogTitle: "Hospital Management Information System & Software | ACGIL",
      ogDescription: "Explore hospital management software, a powerful hospital management system and HMIS that connects clinical, admin, and financial data for better patient care.",
      ogUrl: SITE_URL + "/products/hospital-management-erp-software",
      ogImage: SITE_URL + "/images/hospital-erp.jpg",
      hreflang: {
        en: SITE_URL + "/products/hospital-management-erp-software",
        "x-default": SITE_URL + "/products/hospital-management-erp-software",
      },
      faqSchema: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "What is Hospital Management Software (HMIS)?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Hospital Management Software (HMIS) helps manage patient records, billing, pharmacy, and hospital operations in one system.",
            },
          },
          {
            "@type": "Question",
            name: "What is HMIS?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "HMIS stands for Hospital Management Information System used to digitize hospital processes.",
            },
          },
          {
            "@type": "Question",
            name: "Does it support GST billing?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, it includes GST billing and compliance features.",
            },
          },
          {
            "@type": "Question",
            name: "Can it manage pharmacy operations?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, it provides inventory tracking and prescription-based billing.",
            },
          },
          {
            "@type": "Question",
            name: "Is it scalable for small clinics?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, Electra-HIS is scalable for clinics, hospitals, and large healthcare groups.",
            },
          },
        ],
      },
    },
  },
  {
    id: "lab-management-erp-software",
    slug: "lab-management-erp-software",
    category: "electra-his",
    name: "Lab Management ERP Software",
    tagline: "Built for Laboratories & Diagnostics. Engineered for Accuracy.",
    image: "/images/products/mx-erp-lab.webp",
    href: "/products/lab-management-erp-software",
    badge: "Most Popular",
    color: "#7F77DD",
    family: "Electra-HIS",
    keyWord: "Built",
    accentColor: "#7F77DD",
    trustCount: "1000+",
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
     headlineThin: "Lab Management ERP",
     capHeadline: "Transform Laboratory Management with Mx-ERP",
     headlineBold: "Laboratory Information Management System (LIMS)",
    headlineAccent: "Electra-HIS by ACGIL",
    subheadline:
      "Electra-HIS by ACGIL is a powerful Lab Management ERP Software designed to manage sample tracking, reporting, billing, inventory, and laboratory operations — all in one unified platform.",
    dashHeading:
      "Manage samples, reports, billing, inventory, and lab staff from a single dashboard. Electra-HIS delivers real-time visibility into lab performance, workflows, and diagnostics accuracy.",
    PainPointsSubheading:
      "Labs and diagnostic centers face multiple operational challenges that impact efficiency, accuracy, and turnaround time.",
    painPoints: [
      { title: "Sample Tracking Errors", desc: "Manual sample handling causing mix-ups, losses, and repeat collections." },
      { title: "Report Delays", desc: "Slow report generation impacting patient experience and lab credibility." },
      { title: "Billing Inaccuracies", desc: "Manual billing errors leading to revenue leakage and compliance risks." },
      { title: "Inventory Mismanagement", desc: "Reagent stockouts and expired consumables disrupting daily operations." },
      { title: "Zero Real-Time Visibility", desc: "No centralized data on samples, staff, or lab performance across shifts." },
    ],
    solutionHeadline: "How Lab Management ERP Software Solves These Challenges",
    description:
      "Mx-ERP automates and streamlines the entire laboratory management process.",
    solutionFeatures: [
      "Digital sample tracking system",
      "Automated report generation",
      "Automated report generation",
      "Real-time inventory monitoring",
      "Centralized lab management system",
    ],
    capdescription:
      "Advanced LIMS modules designed to eliminate errors, accelerate reporting, and maximize lab efficiency.",
    capabilities: [
      {
        title: "Sample Management System",
        description:
          "Track every sample with barcode accuracy from collection to processing, with complete test allocation and history.",
        features: [
          "Barcode-based sample tracking",
          "Sample collection & processing",
          "Test allocation & workflow",
          "Sample history tracking",
        ],
        tag: "Samples",
        widget: "production",
        accentColor: "#7F77DD",
        secondaryColor: "#059A8F",
      },
      {
        title: "Report Management",
        description:
          "Generate reports automatically with digital signatures, custom formats, and fast digital delivery to patients and doctors.",
        features: [
          "Automated report generation",
          "Digital signatures",
          "Custom report formats",
          "Quick report delivery",
        ],
        tag: "Reports",
        widget: "mis",
        accentColor: "#059A8F",
        secondaryColor: "#7F77DD",
      },
      {
        title: "Billing & Financial Management",
        description:
          "Automate lab billing with GST-compliant invoicing, real-time payment tracking, and insurance integration.",
        features: [
          "Automated lab billing",
          "GST-compliant invoices",
          "Payment tracking",
          "Insurance integration",
        ],
        tag: "Finance",
        widget: "billing",
        accentColor: "#2C78B9",
        secondaryColor: "#7F77DD",
      },
      {
        title: "Inventory Management",
        description:
          "Track reagents and consumables in real time with batch and expiry management and automated reorder alerts.",
        features: [
          "Reagent & consumable tracking",
          "Batch & expiry management",
          "Auto stock alerts",
          "Purchase management",
        ],
        tag: "Inventory",
        widget: "inventory",
        accentColor: "#7F77DD",
        secondaryColor: "#2C78B9",
      },
      {
        title: "Staff & Lab Management",
        description:
          "Manage technicians, shift schedules, and performance with role-based access across all lab departments.",
        features: [
          "Technician management",
          "Role-based access",
          "Shift scheduling",
          "Performance tracking",
        ],
        tag: "HR",
        widget: "hr",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9",
      },
      {
        title: "MIS & Reporting",
        description:
          "Make faster decisions with lab performance reports, revenue analytics, and real-time dashboards.",
        features: [
          "Lab performance reports",
          "Revenue analytics",
          "Test-wise reporting",
          "Real-time dashboards",
        ],
        tag: "Analytics",
        widget: "mis",
        accentColor: "#2C78B9",
        secondaryColor: "#059A8F",
      },
    ],
    dashboardType: "lab-erp",
    businessImpact: [
      {
        value: "60%",
        label: "Manual Errors",
        description: "Reduce errors with barcode tracking and automation",
        color: "#00F2FE",
        icon: "TrendingDown",
      },
      {
        value: "45%",
        label: "Faster Reports",
        description: "Generate and deliver reports in minutes not hours",
        color: "#7F77DD",
        icon: "Zap",
      },
      {
        value: "35%",
        label: "Operational Efficiency",
        description: "Improve lab-wide productivity and workflow speed",
        color: "#F59E0B",
        icon: "TrendingUp",
      },
      {
        value: "2X",
        label: "Better Sample Tracking",
        description: "Full traceability from collection to report delivery",
        color: "#EC4899",
        icon: "Eye",
      },
    ],
    workflow: [
      { id: 1, title: "Sample Collection", step: "Step 1", icon: "registration", color: "#7F77DD", bgColor: "bg-indigo-500", shadow: "shadow-indigo-500/20" },
      { id: 2, title: "Testing", step: "Step 2", icon: "workflow", color: "#7F77DD", bgColor: "bg-purple-500", shadow: "shadow-purple-500/20" },
      { id: 3, title: "Analysis", step: "Step 3", icon: "equipment", color: "#7F77DD", bgColor: "bg-blue-500", shadow: "shadow-blue-500/20" },
      { id: 4, title: "Report Generation", step: "Step 4", icon: "reports", color: "#7F77DD", bgColor: "bg-emerald-500", shadow: "shadow-emerald-500/20" },
      { id: 5, title: "Billing", step: "Step 5", icon: "finance", color: "#7F77DD", bgColor: "bg-amber-500", shadow: "shadow-amber-500/20" },
      { id: 6, title: "Delivery", step: "Step 6", icon: "delivery", color: "#7F77DD", bgColor: "bg-teal-500", shadow: "shadow-teal-500/20" },
    ],
    features: [
      { id: "sample", title: "Sample Management", description: "Barcode-based sample tracking from collection through processing with full history." },
      { id: "reports", title: "Report Management", description: "Automated report generation with digital signatures and custom format delivery." },
      { id: "billing", title: "Billing & Finance", description: "Automated GST-compliant lab billing with payment tracking and insurance integration." },
      { id: "inventory", title: "Inventory Management", description: "Real-time reagent and consumable tracking with batch, expiry, and auto-reorder alerts." },
      { id: "staff", title: "Staff & Lab Management", description: "Technician scheduling, role-based access, and performance tracking across shifts." },
      { id: "mis", title: "MIS & Reporting", description: "Lab performance dashboards, revenue analytics, and test-wise reporting in real time." },
    ],
    headline: "Your Entire Lab Operations,",
    heroStats: [
      { label: "Active Clients", value: "1000+" },
      { label: "Years of Trust", value: "30+" },
      { label: "GST Ready", value: "100%" },
    ],
    mockupImage: "/images/products/lab-erp-hero.png",
    solutions: [
      { title: "Digital sample tracking", desc: "Barcode-based accuracy from collection to report with zero mix-ups." },
      { title: "Automated reporting", desc: "Reports generated and delivered digitally in minutes, not hours." },
      { title: "Integrated billing", desc: "GST-compliant invoicing with insurance support and real-time payment tracking." },
      { title: "Inventory control", desc: "Zero reagent stockouts and expired consumable elimination with auto alerts." },
      { title: "Real-time analytics", desc: "Lab performance and revenue dashboards for faster management decisions." },
    ],
    roiEstimate: "₹8–15 lakhs/year",
    implementationSteps: [
      {
        num: "01",
        title: "Process Audit",
        description:
          "Our experts map your existing lab workflows, test panels, and billing structures to design the optimal LIMS configuration.",
        bullets: ["Workflow mapping", "Test panel setup", "Gap analysis"],
        image: "/images/products/audit.png",
        bg: "linear-gradient(135deg, #071428, #0d3060)",
      },
      {
        num: "02",
        title: "Seamless Integration",
        description:
          "We configure sample management, reporting, billing, and inventory modules to fit your lab's exact requirements with 100% data accuracy from day one.",
        bullets: ["LIMS configuration", "Analyzer integration", "Data migration"],
        image: "/images/products/integration.png",
        bg: "linear-gradient(135deg, #071e1c, #0a3530)",
      },
      {
        num: "03",
        title: "Go-Live & Support",
        description:
          "Dedicated training for lab technicians, billing staff, and administrators followed by on-site support during the first month to ensure zero downtime.",
        bullets: ["Staff training", "On-site support", "Hypercare period"],
        image: "/images/products/support.png",
        bg: "linear-gradient(135deg, #100d28, #1a1040)",
      },
    ],
    clients: [
      { id: "lm1", name: "ACMA Diagnostics", logo: "/images/clients/acma-logo.webp" },
      { id: "lm2", name: "AHPL Labs", logo: "/images/clients/ahpl-logo.webp" },
      { id: "lm3", name: "AIIMS Pathology", logo: "/images/clients/aiims-logo.webp" },
      { id: "lm4", name: "Allahabad Testing", logo: "/images/clients/allahabad-bank-logo.webp" },
      { id: "lm5", name: "ANT Labs", logo: "/images/clients/ant-logo.webp" },
      { id: "lm6", name: "AOS Diagnostics", logo: "/images/clients/aos-logo.webp" },
      { id: "lm7", name: "Arogyavaram Labs", logo: "/images/clients/arogyavaram-logo.webp" },
      { id: "lm8", name: "Arora Pathology", logo: "/images/clients/arora-aromatics-logo.webp" },
    ],
    industry: "Laboratory & Diagnostics",
    heroImage: "/images/products/lab-erp-hero.png",
    marqueeItems: [
      "GST Compliant",
      "Barcode Tracking",
      "Auto Reports",
      "LIMS Ready",
      "Cloud Security",
      "ISO Certified",
    ],
    testimonials: [
      {
        id: 316,
        name: "Dr. Priya Menon",
        role: "Lab Director",
        company: "Menon Diagnostics",
        avatar: "/images/partners/1.png",
        quote:
          "Mx-ERP LIMS reduced our manual errors by 60%. Barcode-based sample tracking eliminated mix-ups completely and our report turnaround time dropped from 6 hours to 90 minutes.",
        rating: 5,
      },
      {
        id: 317,
        name: "Rajesh Iyer",
        role: "Operations Head",
        company: "Iyer Pathology Network",
        avatar: "/images/partners/2.webp",
        quote:
          "The automated report generation with digital signatures has transformed our patient experience. Reports are delivered via WhatsApp instantly after authorization — patients love it.",
        rating: 5,
      },
      {
        id: 318,
        name: "Anita Sharma",
        role: "Finance Manager",
        company: "Sharma Diagnostic Centre",
        avatar: "/images/partners/3.webp",
        quote:
          "GST billing and insurance integration saved us 2 days of work every month. Billing accuracy improved to 99.8% and we have complete real-time payment visibility.",
        rating: 5,
      },
      {
        id: 319,
        name: "Dr. Sunil Verma",
        role: "CEO",
        company: "Verma Lab Chain",
        avatar: "/images/partners/4.webp",
        quote:
          "Managing reagent inventory across 8 collection centers was impossible manually. Now we have real-time stock visibility with zero expired consumable losses.",
        rating: 5,
      },
      {
        id: 320,
        name: "Kavita Nair",
        role: "Lab Manager",
        company: "Nair Clinical Labs",
        avatar: "/images/partners/5.webp",
        quote:
          "Operational efficiency improved by 35% in the first quarter. Staff scheduling and shift management through the system has significantly improved our team productivity.",
        rating: 5,
      },
    ],
    integrations: [
      { name: "Tally Prime", icon: "calculator", category: "Accounting" },
      { name: "Razorpay", icon: "credit-card", category: "Payments" },
      { name: "GSTN Portal", icon: "file-text", category: "Government" },
      { name: "WhatsApp Business API", icon: "message-square", category: "Report Delivery" },
      { name: "Lab Analyzer APIs (ASTM)", icon: "cpu", category: "Diagnostics Integration" },
      { name: "NABL Portal", icon: "shield-check", category: "Accreditation" },
      { name: "E-Invoice", icon: "file-digit", category: "Government" },
      { name: "Insurance / TPA Portals", icon: "credit-card", category: "Insurance" },
    ],
    faqs: [
      {
        q: "What is Lab Management ERP Software?",
        a: "It is a system that helps manage samples, reports, billing, and lab operations efficiently in one centralized platform.",
      },
      {
        q: "What is LIMS?",
        a: "LIMS (Laboratory Information Management System) is software used to manage laboratory workflows digitally from sample collection to report delivery.",
      },
      {
        q: "Does it support automated reporting?",
        a: "Yes, it generates reports automatically with digital signatures and delivers them via WhatsApp, email, or patient portal.",
      },
      {
        q: "Can it manage lab inventory?",
        a: "Yes, it tracks reagents and consumables in real time with batch and expiry management and automated reorder alerts.",
      },
      {
        q: "Is it suitable for small labs?",
        a: "Yes, Mx-ERP is scalable and works for small standalone labs, diagnostic chains, and large reference laboratories.",
      },
      {
        q: "Can it track samples digitally?",
        a: "Yes, it uses barcode-based tracking for complete sample traceability from collection to report.",
      },
      {
        q: "Does it support insurance and TPA billing?",
        a: "Yes, it includes insurance integration and TPA billing workflows for seamless claim management.",
      },
      {
        q: "Is it cloud-based or on-premise?",
        a: "Mx-ERP supports both cloud-based and on-premise deployment options.",
      },
    ],
    industriesServed: [
      "Pathology Lab Chains",
      "Microbiology & Biochemistry Labs",
      "Radiology & Imaging Centers",
      "Hospital In-house Labs",
      "Clinical Research Organizations",
      "Diagnostic Franchises",
      "Reference & Referral Laboratories",
    ],
    comparison: [
      { feature: "Accuracy", mxerp: "High — Automated", others: "Error-prone" },
      { feature: "Speed", mxerp: "Fast", others: "Slow" },
      { feature: "Automation", mxerp: "Yes", others: "No" },
      { feature: "Tracking", mxerp: "Real-time", others: "Limited" },
      { feature: "Efficiency", mxerp: "High", others: "Low" }
    ],
    seo: {
      title: "Laboratory Management Software | Lab ERP Software - Acgil",
      description: "Laboratory ERP software for test management, patient records, sample tracking, billing, reports, inventory, and efficient laboratory workflows.",
      keywords:
        "lab management erp software, LIMS software, laboratory management system, pathology lab software, diagnostic lab erp, lab billing software, lab inventory management",
      canonical: SITE_URL + "/products/lab-management-erp-software",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      ogTitle: "Laboratory Management Software | Lab ERP Software - Acgil",
      ogDescription: "Laboratory ERP software for test management, patient records, sample tracking, billing, reports, inventory, and efficient laboratory workflows.",
      ogUrl: SITE_URL + "/products/lab-management-erp-software",
      ogImage: SITE_URL + "/images/lab-erp.jpg",
      hreflang: {
        en: SITE_URL + "/products/lab-management-erp-software",
        "x-default": SITE_URL + "/products/lab-management-erp-software",
      },
      faqSchema: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "What is Lab Management ERP Software?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Lab Management ERP Software helps manage samples, reports, billing, and lab operations in one system.",
            },
          },
          {
            "@type": "Question",
            name: "What is LIMS?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "LIMS stands for Laboratory Information Management System used to digitize lab processes.",
            },
          },
          {
            "@type": "Question",
            name: "Does it support automated reporting?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, it generates reports automatically and reduces manual errors.",
            },
          },
          {
            "@type": "Question",
            name: "Can it manage lab inventory?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, it provides real-time inventory tracking and alerts.",
            },
          },
          {
            "@type": "Question",
            name: "Is it scalable for small labs?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, Mx-ERP is scalable for small labs, diagnostic chains, and large reference laboratories.",
            },
          },
        ],
      },
    },
  },
  {
    id: "ris-software",
    slug: "ris-software",
    category: "electra-his",
    name: "Radiology Information System (RIS)",
    tagline: "Radiology Scheduling and Imaging Records | Orders to Delivered Reports on One Platform",
    image: "/images/products/mx-erp-ris.webp",
    href: "/products/ris-software",
    badge: "Most Popular",
    color: "#0F8B9C",
    family: "Electra-HIS",
    keyWord: "Built",
    accentColor: "#0F8B9C",
    trustCount: "1000+",
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    headlineThin: "Radiology Information System (RIS)",
    capHeadline: "Run Radiology Smarter with Electra-HIS",
    headlineBold: "Radiology Information System for Order Management & Imaging",
    headlineAccent: "Electra-HIS by ACGIL",
    subheadline:
      "Electra-HIS is a Radiology Information System that manages orders, appointments, modality worklists, PACS integration, reporting, billing and compliance registers on one platform.",
    dashHeading:
      "Manage ordered, in-progress, awaiting-report and critical studies with on-time reports, average TAT and modality workload (X-Ray, CT, MRI, Ultrasound) from a single dashboard.",
    PainPointsSubheading:
      "Imaging centres handle high volumes, strict safety rules and tight reporting times. Manual processes make each of these harder.",
    painPoints: [
      { title: "Wrong-Patient and Wrong-Study Errors", desc: "Handwritten requests and manual labelling cause mix-ups, repeat scans and avoidable radiation exposure." },
      { title: "Slow Reports", desc: "Films, CDs and separate reporting tools delay results for emergency and ward patients." },
      { title: "Idle Machines and Long Queues", desc: "Without a shared schedule, CT and MRI slots go unused while patients wait." },
      { title: "Missed Safety Screening", desc: "Pregnancy, contrast allergy, kidney function and MRI implant checks are skipped when they are on paper." },
      { title: "Billing Gaps", desc: "Contrast, consumables and repeat scans are missed or wrongly charged, and TPA approvals are late." },
    ],
    solutionHeadline: "How the RIS Solves These Challenges",
    description:
      "Electra-HIS RIS connects the order, the scanner, the image and the report, so each study is traceable from request to delivery.",
    solutionFeatures: [
      "Digital orders from OPD, IPD and Emergency",
      "Accession number and barcode for every study",
      "Modality worklist sent to the scanner (DICOM)",
      "Critical result alerts with acknowledgement",
      "Contrast and consumable stock linked to each study",
      "One dashboard for turnaround time and workload",
    ],
    capdescription:
      "Advanced RIS modules designed to eliminate errors, accelerate reporting, and maximize imaging efficiency.",
    capabilities: [
      {
        title: "Order and Appointment Management",
        description:
          "Place investigation orders from any ward or clinic and book the right machine slot without phone calls.",
        features: [
          "Order entry with clinical indication and priority (Routine, Urgent, STAT)",
          "Machine-wise slot calendar with emergency slots",
          "Preparation slip by print, SMS or WhatsApp",
          "Reschedule and no-show tracking",
        ],
        tag: "Orders",
        widget: "planning",
        accentColor: "#0F8B9C",
        secondaryColor: "#2C78B9",
      },
      {
        title: "Patient Safety Screening",
        description:
          "Check pregnancy, allergy, kidney function and implants before the scan starts, and keep the record with the study.",
        features: [
          "Pregnancy and LMP check for radiation exams",
          "Contrast screening with latest creatinine / eGFR from the lab",
          "MRI implant and metal checklist",
          "Consent capture and PCPNDT Form F for obstetric ultrasound",
        ],
        tag: "Safety",
        widget: "production",
        accentColor: "#2C78B9",
        secondaryColor: "#0F8B9C",
      },
      {
        title: "Worklist, PACS and Dose Capture",
        description:
          "Technologists work from a live worklist. Details flow to the scanner and images return to PACS automatically.",
        features: [
          "DICOM modality worklist and PACS status",
          "Protocol, contrast batch and volume recorded per study",
          "Radiation dose capture and reference-level alerts",
          "Repeat and reject analysis with reasons",
        ],
        tag: "Modality",
        widget: "inventory",
        accentColor: "#7F77DD",
        secondaryColor: "#0F8B9C",
      },
      {
        title: "Reporting and Delivery",
        description:
          "Radiologists read from one worklist, use structured templates and release signed reports in minutes.",
        features: [
          "Templates, macros and BI-RADS / TI-RADS / LI-RADS scoring",
          "Preliminary and final reports with addendum history",
          "Critical finding alert to the doctor or ward with acknowledgement",
          "Delivery by print, portal, WhatsApp or e-mail with patient consent",
        ],
        tag: "Reports",
        widget: "mis",
        accentColor: "#0F8B9C",
        secondaryColor: "#7F77DD",
      },
      {
        title: "Billing, Stock and Compliance",
        description:
          "Post charges from the order, deduct contrast from stock, and keep regulatory registers ready for audit.",
        features: [
          "Tariff-based charging with TPA pre-authorisation",
          "Contrast and consumables with batch and expiry",
          "AERB dose register and machine licence tracking",
          "PCPNDT register and audit trail on every report",
        ],
        tag: "Finance",
        widget: "billing",
        accentColor: "#2C78B9",
        secondaryColor: "#7F77DD",
      },
    ],
    dashboardType: "ris-erp",
    businessImpact: [
      {
        value: "50%",
        label: "Fewer Repeat Scans",
        description: "Reduce wrong-patient and repeat scans with barcode verification",
        color: "#00F2FE",
        icon: "TrendingDown",
      },
      {
        value: "40%",
        label: "Faster Reports",
        description: "Accelerate report delivery through worklists and templates",
        color: "#7F77DD",
        icon: "Zap",
      },
      {
        value: "30%",
        label: "Higher Utilisation",
        description: "Improve machine utilisation with shared scheduling",
        color: "#F59E0B",
        icon: "TrendingUp",
      },
      {
        value: "2X",
        label: "Traceability",
        description: "Full traceability from order to delivered report",
        color: "#EC4899",
        icon: "Eye",
      },
    ],
    workflow: [
      { id: 1, title: "Order", step: "Step 1", icon: "order", color: "#0F8B9C", bgColor: "bg-teal-500", shadow: "shadow-teal-500/20" },
      { id: 2, title: "Schedule", step: "Step 2", icon: "inventory", color: "#0F8B9C", bgColor: "bg-blue-500", shadow: "shadow-blue-500/20" },
      { id: 3, title: "Screen", step: "Step 3", icon: "registration", color: "#0F8B9C", bgColor: "bg-indigo-500", shadow: "shadow-indigo-500/20" },
      { id: 4, title: "Scan", step: "Step 4", icon: "lab", color: "#0F8B9C", bgColor: "bg-cyan-500", shadow: "shadow-cyan-500/20" },
      { id: 5, title: "Report", step: "Step 5", icon: "reports", color: "#0F8B9C", bgColor: "bg-emerald-500", shadow: "shadow-emerald-500/20" },
      { id: 6, title: "Deliver", step: "Step 6", icon: "delivery", color: "#0F8B9C", bgColor: "bg-amber-500", shadow: "shadow-amber-500/20" },
    ],
    features: [
      { id: "orders", title: "Order & Scheduling", description: "Digital orders with indication and priority, machine-wise slots, prep slips and no-show tracking." },
      { id: "safety", title: "Safety Screening", description: "Pregnancy, contrast/eGFR, MRI implant checks, consent and PCPNDT Form F with every study." },
      { id: "modality", title: "Worklist, PACS & Dose", description: "DICOM modality worklist, PACS status, contrast batch capture and dose reference alerts." },
      { id: "reporting", title: "Reporting & Delivery", description: "Structured templates with RADS scoring, prelim/final/addendum versions and consented delivery." },
      { id: "billing", title: "Billing & Stock", description: "Tariff-based charging, TPA pre-authorisation, contrast stock with batch and expiry." },
      { id: "compliance", title: "Compliance Registers", description: "AERB dose register, machine licence tracking and audit trail on every report." },
    ],
    headline: "Your Entire Radiology Workflow,",
    heroStats: [
      { label: "Active Clients", value: "1000+" },
      { label: "Years of Trust", value: "30+" },
      { label: "GST Ready", value: "100%" },
    ],
    mockupImage: "/images/products/ris-hero.png",
    solutions: [
      { title: "Digital orders with barcode", desc: "Accession number and two-identifier check from OPD, IPD and Emergency." },
      { title: "Shared machine calendar", desc: "Higher CT/MRI utilisation with emergency slots and no phone-tag scheduling." },
      { title: "Scanner-to-PACS flow", desc: "Worklist to modality, images back to PACS, dose and contrast captured per study." },
      { title: "Structured e-signed reports", desc: "Templates, critical alerts with acknowledgement and multi-channel delivery." },
      { title: "Audit-ready registers", desc: "Dose, PCPNDT and licence registers with real-time TAT and workload dashboards." },
    ],
    roiEstimate: "₹8–15 lakhs/year",
    implementationSteps: [
      {
        num: "01",
        title: "Discover",
        description:
          "Our experts study your modalities, volumes and reporting workflow to plan masters, integrations and rollout by department.",
        bullets: ["Modality & volume mapping", "Reporting workflow study", "Gap analysis"],
        image: "/images/products/audit.png",
        bg: "linear-gradient(135deg, #071428, #0d3060)",
      },
      {
        num: "02",
        title: "Implement",
        description:
          "We configure orders, slots, templates and tariffs, connect PACS and scanners over DICOM/HL7, and train technologists and radiologists.",
        bullets: ["RIS configuration", "PACS & scanner integration", "Data migration"],
        image: "/images/products/integration.png",
        bg: "linear-gradient(135deg, #071e1c, #0a3530)",
      },
      {
        num: "03",
        title: "Optimize",
        description:
          "Dedicated training followed by on-site support during go-live, tracking turnaround time to refine templates and slots.",
        bullets: ["Staff training", "On-site support", "TAT optimization"],
        image: "/images/products/support.png",
        bg: "linear-gradient(135deg, #100d28, #1a1040)",
      },
    ],
    clients: [
      { id: "rm1", name: "ACMA Imaging", logo: "/images/clients/acma-logo.webp" },
      { id: "rm2", name: "AHPL Diagnostics", logo: "/images/clients/ahpl-logo.webp" },
      { id: "rm3", name: "AIIMS Radiology", logo: "/images/clients/aiims-logo.webp" },
      { id: "rm4", name: "Allahabad Imaging", logo: "/images/clients/allahabad-bank-logo.webp" },
      { id: "rm5", name: "ANT Scans", logo: "/images/clients/ant-logo.webp" },
      { id: "rm6", name: "AOS Imaging", logo: "/images/clients/aos-logo.webp" },
      { id: "rm7", name: "Arogyavaram Radiology", logo: "/images/clients/arogyavaram-logo.webp" },
      { id: "rm8", name: "Arora Diagnostics", logo: "/images/clients/arora-aromatics-logo.webp" },
    ],
    industry: "Healthcare",
    heroImage: "/images/products/ris-hero.png",
    marqueeItems: [
      "DICOM Ready",
      "PACS Integrated",
      "Barcode Tracking",
      "TAT Analytics",
      "Cloud Security",
      "ISO Certified",
    ],
    testimonials: [
      {
        id: 331,
        name: "Dr. Kavita Rao",
        role: "Head of Radiology",
        company: "Rao Imaging Centre",
        avatar: "/images/partners/1.png",
        quote:
          "Electra-HIS RIS eliminated our film and CD delays. Worklists flow straight to the modality and signed reports reach wards and patients in minutes.",
        rating: 5,
      },
      {
        id: 332,
        name: "Sandeep Kulkarni",
        role: "Operations Manager",
        company: "Kulkarni Diagnostics Chain",
        avatar: "/images/partners/2.webp",
        quote:
          "Shared CT and MRI calendars pushed our machine utilisation up significantly. Emergency slots no longer clash with routine bookings.",
        rating: 5,
      },
      {
        id: 333,
        name: "Dr. Farah Khan",
        role: "Consultant Radiologist",
        company: "Khan Scan & Care",
        avatar: "/images/partners/3.webp",
        quote:
          "Structured templates with BI-RADS scoring and critical alerts with acknowledgement have made our reporting faster and safer.",
        rating: 5,
      },
      {
        id: 334,
        name: "Rohit Malhotra",
        role: "Finance Head",
        company: "Malhotra Hospital Group",
        avatar: "/images/partners/4.webp",
        quote:
          "Contrast stock, TPA pre-authorisation and tariff-based charging are finally linked to each study. Billing leakage on repeats has dropped sharply.",
        rating: 5,
      },
      {
        id: 335,
        name: "Dr. Meera Joshi",
        role: "Medical Superintendent",
        company: "Joshi Multi-Specialty Hospital",
        avatar: "/images/partners/5.webp",
        quote:
          "Dose registers, PCPNDT forms and licence alerts are audit-ready. Our TAT dashboard keeps every modality accountable in real time.",
        rating: 5,
      },
    ],
    integrations: [
      { name: "PACS (DICOM)", icon: "scan", category: "Imaging" },
      { name: "Modality Worklist (MWL)", icon: "monitor", category: "Imaging" },
      { name: "HL7", icon: "activity", category: "Interoperability" },
      { name: "Lab Information System", icon: "flask-conical", category: "Diagnostics" },
      { name: "WhatsApp Business API", icon: "message-square", category: "Communication" },
      { name: "SMS Gateway", icon: "message-circle", category: "Communication" },
      { name: "Tally Prime", icon: "calculator", category: "Accounting" },
      { name: "GSTN Portal", icon: "file-text", category: "Government" },
      { name: "E-Invoice", icon: "file-digit", category: "Government" },
      { name: "Insurance / TPA Portals", icon: "credit-card", category: "Insurance" },
      { name: "Razorpay", icon: "credit-card", category: "Payments" },
    ],
    faqs: [
      {
        q: "What is a Radiology Information System?",
        a: "A RIS manages the radiology workflow: orders, scheduling, patient screening, scan records, reporting, billing and delivery. It works alongside PACS, which stores the images.",
      },
      {
        q: "Does it work with our PACS and scanners?",
        a: "Yes. It uses standard DICOM and HL7 interfaces for worklists and image status. We confirm compatibility during discovery.",
      },
      {
        q: "Can it handle PCPNDT and AERB requirements?",
        a: "It captures Form F for obstetric ultrasound, radiation dose per study and machine licence expiry, and produces registers for audit.",
      },
      {
        q: "Does it support structured reporting?",
        a: "Yes. Templates, macros and scoring systems such as BI-RADS are supported, with preliminary, final and addendum versions.",
      },
      {
        q: "Can we send reports on WhatsApp?",
        a: "Yes, for patients who have given consent, along with print, portal and e-mail delivery.",
      },
      {
        q: "Does it support insurance and TPA billing?",
        a: "Yes. Charges follow the payer tariff, with pre-authorisation checks and posting to the IPD bill.",
      },
      {
        q: "Is it suitable for small imaging centres?",
        a: "Yes. Modules can be enabled as needed, so a small centre can start with orders, reporting and billing.",
      },
      {
        q: "Is it cloud-based or on-premise?",
        a: "Both deployment options are available. Talk to our team to choose what fits your hospital.",
      },
    ],
    industriesServed: [
      "Multi-Specialty Hospitals",
      "Standalone Imaging Centres",
      "Diagnostic Lab Chains",
      "Hospital Radiology Departments",
      "Emergency & Trauma Centres",
      "Obstetric Ultrasound Clinics",
      "Teleradiology Networks",
    ],
    comparison: [
      { feature: "Patient identification", mxerp: "Barcode and two-identifier check", others: "Handwritten, error-prone" },
      { feature: "Scheduling", mxerp: "Shared machine calendar", others: "Phone and registers" },
      { feature: "Hospital data", mxerp: "Same patient, billing and lab records", others: "Re-entered in each system" },
      { feature: "Reporting", mxerp: "Templates, alerts, e-signature", others: "Free typing, printed copies" },
      { feature: "Compliance", mxerp: "Dose and PCPNDT registers built in", others: "Maintained separately" },
      { feature: "Visibility", mxerp: "Real-time dashboard", others: "End-of-day counts" },
    ],
    seo: {
      title: "Radiology Information System (RIS) Software | Radiology ERP - Acgil",
      description: "Radiology Information System for order management, scheduling, modality worklist, PACS integration, structured reporting, billing, dose registers and TAT analytics.",
      keywords: "RIS software, radiology information system, radiology management software, PACS integration, DICOM worklist, diagnostic imaging ERP, radiology billing",
      canonical: SITE_URL + "/products/ris-software",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      ogTitle: "Radiology Information System (RIS) Software | Acgil",
      ogDescription: "Electra-HIS RIS connects orders, scanners, images and reports — scheduling, modality worklist, PACS, structured reporting, billing and compliance on one platform.",
      ogUrl: SITE_URL + "/products/ris-software",
      ogImage: SITE_URL + "/images/ris-erp.jpg",
      hreflang: {
        en: SITE_URL + "/products/ris-software",
        "x-default": SITE_URL + "/products/ris-software",
      },
      faqSchema: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "What is a Radiology Information System?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "A RIS manages the radiology workflow: orders, scheduling, patient screening, scan records, reporting, billing and delivery. It works alongside PACS, which stores the images.",
            },
          },
          {
            "@type": "Question",
            name: "Does it work with our PACS and scanners?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes. It uses standard DICOM and HL7 interfaces for worklists and image status.",
            },
          },
          {
            "@type": "Question",
            name: "Can it handle PCPNDT and AERB requirements?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "It captures Form F for obstetric ultrasound, radiation dose per study and machine licence expiry, and produces registers for audit.",
            },
          },
          {
            "@type": "Question",
            name: "Does it support structured reporting?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, templates, macros and scoring systems such as BI-RADS are supported, with preliminary, final and addendum versions.",
            },
          },
          {
            "@type": "Question",
            name: "Is it suitable for small imaging centres?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, modules can be enabled as needed, so a small centre can start with orders, reporting and billing.",
            },
          },
        ],
      },
    },
  },
  {
    id: "pharmacy-software",
    slug: "pharmacy-software",
    category: "electra-his",
    name: "Pharmacy Management Software",
    tagline: "Drug Inventory, E-Prescriptions & Multi-Store Control | Prescription to Reorder on One Platform",
    image: "/images/products/mx-erp-pharmacy.webp",
    href: "/products/pharmacy-software",
    badge: "Most Popular",
    color: "#0B6BCB",
    family: "Electra-HIS",
    keyWord: "Built",
    accentColor: "#0B6BCB",
    trustCount: "1000+",
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    headlineThin: "Pharmacy Management Software",
    capHeadline: "Run Pharmacies Smarter with Electra-HIS",
    headlineBold: "Pharmacy Management System (PMS)",
    headlineAccent: "Electra-HIS by ACGIL",
    subheadline:
      "Electra-HIS is a powerful Pharmacy Management ERP Software designed to manage drug inventory, prescriptions, dispensing, billing and stock control — all in one unified platform.",
    dashHeading:
      "Manage prescriptions, drug stock, batches, expiry, billing and purchase orders from a single screen with real-time visibility.",
    PainPointsSubheading:
      "Hospital and retail pharmacies deal with stock, safety and compliance pressure that manual processes cannot handle.",
    painPoints: [
      { title: "Dispensing Errors", desc: "Wrong drug, wrong batch or wrong dose due to manual prescription handling." },
      { title: "Expired Stock", desc: "Untracked expiry dates leading to wastage, losses and compliance risk." },
      { title: "Stock-outs", desc: "No reorder triggers, so critical medicines run out when patients need them." },
      { title: "Billing Inaccuracies", desc: "Manual billing, discount and GST errors causing revenue leakage." },
      { title: "Zero Real-Time Visibility", desc: "No central view of stock, sales and pharmacy performance across stores." },
    ],
    solutionHeadline: "How Pharmacy Management ERP Software Solves These Challenges",
    description:
      "Electra-HIS automates and streamlines the entire pharmacy workflow, from prescription to dispensing to reorder.",
    solutionFeatures: [
      "Prescriptions from doctors flow automatically to the dispensing unit",
      "Batch-wise and expiry-wise stock deduction",
      "Automatic alerts when stock reaches minimum level",
      "FIFO and LIFO dispensing as per hospital policy",
      "Centralised pharmacy management across stores",
      "Look-Alike / Sound-Alike (LASA) drug alerts",
      "Emergency stock management",
      "Narcotic and controlled drug verification alerts",
      "High-alert medication management",
      "Expiry date and near-expiry alerts",
      "Real-time stock availability",
    ],
    capdescription:
      "Advanced pharmacy modules designed to eliminate errors, prevent wastage and speed up dispensing.",
    capabilities: [
      {
        title: "Drug Inventory Management",
        description:
          "Extensive drug master with ATC-based classification and multi-store stock control.",
        features: [
          "Batch, manufacturing and expiry tracking",
          "Multi-store and multi-location stock",
          "FIFO / LIFO dispensing methods",
          "Auto reorder and minimum-stock alerts",
        ],
        tag: "Inventory",
        widget: "inventory",
        accentColor: "#0B6BCB",
        secondaryColor: "#059A8F",
      },
      {
        title: "E-Prescription & Dispensing",
        description:
          "Accept prescriptions from any department without manual intervention.",
        features: [
          "E-prescriptions from consulting doctors",
          "Patient-wise issue and return",
          "Batch and expiry shown at dispensing",
          "Integrated with OPD, IPD and wards",
        ],
        tag: "Dispensing",
        widget: "production",
        accentColor: "#059A8F",
        secondaryColor: "#0B6BCB",
      },
      {
        title: "Billing & Accounts",
        description:
          "Accurate, GST-ready billing for cash, credit and insurance.",
        features: [
          "OPD / IPD pharmacy billing",
          "Discounts, schemes and GST handling",
          "Insurance / TPA billing",
          "Integrated financial accounting",
        ],
        tag: "Finance",
        widget: "billing",
        accentColor: "#7F77DD",
        secondaryColor: "#0B6BCB",
      },
      {
        title: "Purchase & Vendor Management",
        description:
          "Control procurement from indent to payment.",
        features: [
          "Indents, PO and GRN",
          "Vendor rate comparison",
          "Purchase returns and credit notes",
          "Reports and MIS for management",
        ],
        tag: "Procurement",
        widget: "vendor",
        accentColor: "#0B6BCB",
        secondaryColor: "#7F77DD",
      },
      {
        title: "Safety & Expiry Alerts",
        description:
          "Catch risky dispensing and dying stock before they become incidents.",
        features: [
          "Look-Alike / Sound-Alike (LASA) drug alerts",
          "Narcotic and controlled drug verification",
          "High-alert medication management",
          "Expiry and near-expiry alerts",
        ],
        tag: "Safety",
        widget: "mis",
        accentColor: "#059A8F",
        secondaryColor: "#7F77DD",
      },
      {
        title: "MIS & Reporting",
        description:
          "Make faster decisions with pharmacy performance reports and real-time dashboards.",
        features: [
          "Stock, sales and expiry analytics",
          "Purchase and vendor performance reports",
          "Multi-store performance comparison",
          "Real-time dashboards",
        ],
        tag: "Analytics",
        widget: "mis",
        accentColor: "#0B6BCB",
        secondaryColor: "#059A8F",
      },
    ],
    dashboardType: "pharmacy-erp",
    businessImpact: [
      {
        value: "60%",
        label: "Fewer Dispensing Errors",
        description: "Reduce errors with batch-level control and e-prescriptions",
        color: "#00F2FE",
        icon: "TrendingDown",
      },
      {
        value: "45%",
        label: "Less Expired Stock",
        description: "Cut wastage with expiry alerts and FIFO discipline",
        color: "#7F77DD",
        icon: "TrendingDown",
      },
      {
        value: "35%",
        label: "Faster Billing",
        description: "Accelerate billing with automation and GST handling",
        color: "#F59E0B",
        icon: "Zap",
      },
      {
        value: "2X",
        label: "Stock Visibility",
        description: "Real-time stock visibility across all stores",
        color: "#EC4899",
        icon: "Eye",
      },
    ],
    workflow: [
      { id: 1, title: "Prescription", step: "Step 1", icon: "order", color: "#0B6BCB", bgColor: "bg-blue-500", shadow: "shadow-blue-500/20" },
      { id: 2, title: "Verification", step: "Step 2", icon: "registration", color: "#0B6BCB", bgColor: "bg-indigo-500", shadow: "shadow-indigo-500/20" },
      { id: 3, title: "Stock Check", step: "Step 3", icon: "inventory", color: "#0B6BCB", bgColor: "bg-cyan-500", shadow: "shadow-cyan-500/20" },
      { id: 4, title: "Dispensing", step: "Step 4", icon: "pharmacy", color: "#0B6BCB", bgColor: "bg-teal-500", shadow: "shadow-teal-500/20" },
      { id: 5, title: "Billing", step: "Step 5", icon: "finance", color: "#0B6BCB", bgColor: "bg-emerald-500", shadow: "shadow-emerald-500/20" },
      { id: 6, title: "Reorder", step: "Step 6", icon: "delivery", color: "#0B6BCB", bgColor: "bg-amber-500", shadow: "shadow-amber-500/20" },
    ],
    features: [
      { id: "inventory", title: "Drug Inventory Management", description: "ATC-based drug master with batch, manufacturing and expiry tracking across stores." },
      { id: "dispensing", title: "E-Prescription & Dispensing", description: "Doctor e-prescriptions flow to dispensing with batch and expiry shown at issue." },
      { id: "billing", title: "Billing & Accounts", description: "OPD/IPD billing with discounts, schemes, GST and insurance/TPA support." },
      { id: "purchase", title: "Purchase & Vendor Management", description: "Indents, PO, GRN, rate comparison, returns and credit notes in one flow." },
      { id: "safety", title: "Safety & Expiry Alerts", description: "LASA, narcotic, high-alert and near-expiry alerts before dispensing." },
      { id: "mis", title: "MIS & Reporting", description: "Stock, sales and multi-store performance dashboards in real time." },
    ],
    headline: "Your Entire Pharmacy Operations,",
    heroStats: [
      { label: "Active Clients", value: "1000+" },
      { label: "Years of Trust", value: "30+" },
      { label: "GST Ready", value: "100%" },
    ],
    mockupImage: "/images/products/pharmacy-hero.png",
    solutions: [
      { title: "Automatic e-prescription flow", desc: "Prescriptions from doctors land directly in the dispensing unit." },
      { title: "Batch and expiry-wise deduction", desc: "Every issue tracked by batch with FIFO/LIFO discipline." },
      { title: "Auto reorder triggers", desc: "Minimum-stock alerts so critical medicines never run out." },
      { title: "Safety alerts at dispensing", desc: "LASA, narcotic and high-alert checks before the drug leaves the counter." },
      { title: "Centralised multi-store control", desc: "Stock, transfers and reports across all stores from one dashboard." },
    ],
    roiEstimate: "₹8–15 lakhs/year",
    implementationSteps: [
      {
        num: "01",
        title: "Process Audit",
        description:
          "Our experts map your pharmacy workflows, drug master, store structure and billing requirements to design the optimal configuration.",
        bullets: ["Workflow mapping", "Drug master review", "Gap analysis"],
        image: "/images/products/audit.png",
        bg: "linear-gradient(135deg, #071428, #0d3060)",
      },
      {
        num: "02",
        title: "Seamless Integration",
        description:
          "We configure inventory, e-prescription, billing and purchase modules to fit your pharmacy's exact needs with 100% data accuracy from day one.",
        bullets: ["PMS configuration", "Tally & GST integration", "Data migration"],
        image: "/images/products/integration.png",
        bg: "linear-gradient(135deg, #071e1c, #0a3530)",
      },
      {
        num: "03",
        title: "Go-Live & Support",
        description:
          "Dedicated training for pharmacists, billing and purchase staff followed by on-site support during the first month to ensure zero downtime.",
        bullets: ["Staff training", "On-site support", "Hypercare period"],
        image: "/images/products/support.png",
        bg: "linear-gradient(135deg, #100d28, #1a1040)",
      },
    ],
    clients: [
      { id: "pm1", name: "ACMA Pharmacy", logo: "/images/clients/acma-logo.webp" },
      { id: "pm2", name: "AHPL Healthcare", logo: "/images/clients/ahpl-logo.webp" },
      { id: "pm3", name: "AIIMS Pharmacy", logo: "/images/clients/aiims-logo.webp" },
      { id: "pm4", name: "Allahabad Medical", logo: "/images/clients/allahabad-bank-logo.webp" },
      { id: "pm5", name: "ANT Medicos", logo: "/images/clients/ant-logo.webp" },
      { id: "pm6", name: "AOS Pharma", logo: "/images/clients/aos-logo.webp" },
      { id: "pm7", name: "Arogyavaram Pharmacy", logo: "/images/clients/arogyavaram-logo.webp" },
      { id: "pm8", name: "Arora Chemists", logo: "/images/clients/arora-aromatics-logo.webp" },
    ],
    industry: "Healthcare",
    heroImage: "/images/products/pharmacy-hero.png",
    marqueeItems: [
      "GST Compliant",
      "Batch Tracking",
      "Expiry Alerts",
      "E-Prescriptions",
      "Cloud Security",
      "ISO Certified",
    ],
    testimonials: [
      {
        id: 336,
        name: "Dr. Nisha Agarwal",
        role: "Chief Pharmacist",
        company: "Agarwal Multi-Specialty Hospital",
        avatar: "/images/partners/1.png",
        quote:
          "Electra-HIS Pharmacy cut our dispensing errors dramatically. E-prescriptions flow straight to the counter with batch and expiry shown at every issue.",
        rating: 5,
      },
      {
        id: 337,
        name: "Manoj Tiwari",
        role: "Pharmacy Manager",
        company: "Tiwari Retail Chain",
        avatar: "/images/partners/2.webp",
        quote:
          "Near-expiry alerts and FIFO discipline reduced our expired-stock losses within two quarters. Auto reorder means critical medicines never run out now.",
        rating: 5,
      },
      {
        id: 338,
        name: "Dr. Shalini Bose",
        role: "Medical Superintendent",
        company: "Bose Nursing Home",
        avatar: "/images/partners/3.webp",
        quote:
          "LASA and high-alert medication checks at dispensing have made our pharmacy measurably safer. Narcotic verification is audit-ready every time.",
        rating: 5,
      },
      {
        id: 339,
        name: "Amit Khanna",
        role: "Finance Controller",
        company: "Khanna Hospital Group",
        avatar: "/images/partners/4.webp",
        quote:
          "GST billing with discounts, schemes and TPA linkage is fully automated. Our pharmacy billing cycle is faster and revenue leakage has dropped.",
        rating: 5,
      },
      {
        id: 340,
        name: "Ritu Saxena",
        role: "Operations Head",
        company: "Saxena Pharmacy Stores",
        avatar: "/images/partners/5.webp",
        quote:
          "Managing stock across six stores from one dashboard changed everything. Transfers, purchase and vendor rates are finally visible in real time.",
        rating: 5,
      },
    ],
    integrations: [
      { name: "Tally Prime", icon: "calculator", category: "Accounting" },
      { name: "Razorpay", icon: "credit-card", category: "Payments" },
      { name: "GSTN Portal", icon: "file-text", category: "Government" },
      { name: "WhatsApp Business API", icon: "message-square", category: "Communication" },
      { name: "Barcode Scanners", icon: "scan", category: "Hardware" },
      { name: "E-Invoice", icon: "file-digit", category: "Government" },
      { name: "Insurance / TPA Portals", icon: "credit-card", category: "Insurance" },
      { name: "Electra-HIS Modules", icon: "hospital", category: "Healthcare" },
    ],
    faqs: [
      {
        q: "What is Pharmacy Management ERP Software?",
        a: "It is a software module that manages drug inventory, prescriptions, dispensing, billing and purchase in one platform, integrated with the hospital or business ERP.",
      },
      {
        q: "Does it support batch and expiry tracking?",
        a: "Yes. Stock is deducted by batch number, manufacturing date and expiry date, and near-expiry alerts are generated automatically.",
      },
      {
        q: "Does it support FIFO and LIFO dispensing?",
        a: "Yes. The dispensing method can be configured as per your hospital or store policy.",
      },
      {
        q: "Can it accept e-prescriptions from doctors?",
        a: "Yes. Prescriptions from consulting doctors and departments reflect directly in the dispensing unit.",
      },
      {
        q: "Can it manage multiple pharmacy stores?",
        a: "Yes. Manage stock, transfers and reports across multiple stores from one dashboard.",
      },
      {
        q: "Does it support insurance and TPA billing?",
        a: "Yes. Pharmacy bills can be linked with insurance and TPA workflows.",
      },
      {
        q: "Is it suitable for small pharmacies?",
        a: "Yes. The system scales from a single store to hospital chains.",
      },
      {
        q: "Is it cloud-based or on-premise?",
        a: "Both options are available, based on your requirement.",
      },
    ],
    industriesServed: [
      "Hospital Pharmacies",
      "Retail Pharmacies",
      "Pharmacy Chains",
      "Clinics & Nursing Homes",
      "Diagnostic Centers",
      "Pharma Distributors",
    ],
    comparison: [
      { feature: "Accuracy", mxerp: "High — Automated", others: "Error-prone" },
      { feature: "Speed", mxerp: "Fast", others: "Slow" },
      { feature: "Automation", mxerp: "Yes", others: "No" },
      { feature: "Tracking", mxerp: "Real-time", others: "Limited" },
      { feature: "HIS Integration", mxerp: "Native (OPD, IPD, Wards)", others: "Separate / manual" },
    ],
    seo: {
      title: "Pharmacy Management Software | Pharmacy ERP Software - Acgil",
      description: "Pharmacy management ERP software for drug inventory, batch & expiry tracking, e-prescriptions, billing, GST and multi-store pharmacy operations.",
      keywords: "pharmacy management software, pharmacy ERP, hospital pharmacy software, drug inventory management, batch expiry tracking, pharmacy billing software, Electra-HIS",
      canonical: SITE_URL + "/products/pharmacy-software",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      ogTitle: "Pharmacy Management Software | Pharmacy ERP Software - Acgil",
      ogDescription: "Pharmacy ERP for drug inventory, batch & expiry tracking, e-prescriptions, billing and multi-store control.",
      ogUrl: SITE_URL + "/products/pharmacy-software",
      ogImage: SITE_URL + "/images/pharmacy-erp.jpg",
      hreflang: {
        en: SITE_URL + "/products/pharmacy-software",
        "x-default": SITE_URL + "/products/pharmacy-software",
      },
      faqSchema: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "What is Pharmacy Management ERP Software?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "It is a software module that manages drug inventory, prescriptions, dispensing, billing and purchase in one platform.",
            },
          },
          {
            "@type": "Question",
            name: "Does it support batch and expiry tracking?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, stock is deducted by batch number, manufacturing date and expiry date with automatic near-expiry alerts.",
            },
          },
          {
            "@type": "Question",
            name: "Can it accept e-prescriptions from doctors?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, prescriptions from consulting doctors and departments reflect directly in the dispensing unit.",
            },
          },
          {
            "@type": "Question",
            name: "Can it manage multiple pharmacy stores?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, manage stock, transfers and reports across multiple stores from one dashboard.",
            },
          },
          {
            "@type": "Question",
            name: "Is it suitable for small pharmacies?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, the system scales from a single store to hospital chains.",
            },
          },
        ],
      },
    },
  },
  {
    id: "blood-bank-software",
    slug: "blood-bank-software",
    category: "electra-his",
    name: "Blood Bank Management Software",
    tagline: "Donor-to-Recipient Traceability | Screening, Components & Safe Issue on One Platform",
    image: "/images/products/mx-erp-blood-bank.webp",
    href: "/products/blood-bank-software",
    badge: "Most Popular",
    color: "#C8102E",
    family: "Electra-HIS",
    keyWord: "Built",
    accentColor: "#C8102E",
    trustCount: "1000+",
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    headlineThin: "Blood Bank Management Software",
    capHeadline: "Run Blood Banks Safer with Electra-HIS",
    headlineBold: "Blood Bank Management for Safe, Traceable Transfusion",
    headlineAccent: "Electra-HIS by ACGIL",
    subheadline:
      "Electra-HIS is a Blood Bank Management Software that covers donor registration, screening, collection, TTI testing, component separation, cross-match, inventory, issue and billing — with full donor-to-recipient traceability on one platform.",
    dashHeading:
      "Monitor blood-group and component stock, donor pipeline, TTI quarantine, near-expiry units and issue turnaround from a single dashboard.",
    PainPointsSubheading:
      "Blood banks run on trust and precision. Manual registers put both at risk.",
    painPoints: [
      { title: "Donor History Gaps", desc: "Paper registers miss deferrals and past donations, risking unsafe collections." },
      { title: "Untested-Unit Risk", desc: "Without system quarantine, TTI-reactive or pending-test units can reach issue." },
      { title: "Component Wastage", desc: "No expiry discipline across PRBC, platelets, FFP and Cryo leads to avoidable discards." },
      { title: "Emergency Issue Delays", desc: "Ward requisitions on phone and manual cross-match slow down urgent transfusions." },
      { title: "Compliance Burden", desc: "NACO, NBTC and Drugs & Cosmetics Act registers compiled by hand before every inspection." },
    ],
    solutionHeadline: "How Blood Bank Software Solves These Challenges",
    description:
      "The module connects every stage from donor to recipient in one continuous, auditable chain.",
    solutionFeatures: [
      "Donor registration with unique ID and barcode",
      "Deferral tracking with repeat-donor reminders",
      "Bag-wise collection with camp and in-house tracking",
      "TTI testing with analyser interface and auto-quarantine",
      "Component separation with parent-child bag linking",
      "ABO/Rh grouping with compatibility cross-match",
      "Ward and patient requisitions received from HIS",
      "Transfusion reaction records linked to every issue",
    ],
    capdescription:
      "Dedicated sub-modules for every blood bank stage, seamlessly connected with lab, HIS and inventory.",
    capabilities: [
      {
        title: "Donor Management",
        description:
          "Register every donor once and track their complete donation journey.",
        features: [
          "Donor registration with unique ID and barcode",
          "Complete donation history per donor",
          "Deferral tracking with reasons",
          "Repeat-donor reminders via SMS and WhatsApp",
        ],
        tag: "Donors",
        widget: "production",
        accentColor: "#C8102E",
        secondaryColor: "#0B5CAB",
      },
      {
        title: "Screening & Collection",
        description:
          "Screen donors against eligibility criteria and track every collection bag-wise.",
        features: [
          "Hb, BP, weight and history questionnaire",
          "Bag-wise collection with unique bag numbers",
          "Camp and in-house collection tracking",
          "Donor barcode and label printing",
        ],
        tag: "Collection",
        widget: "inventory",
        accentColor: "#0B5CAB",
        secondaryColor: "#C8102E",
      },
      {
        title: "TTI Testing & Grouping",
        description:
          "Test every unit and hold reactive stock out of circulation automatically.",
        features: [
          "HIV, HBsAg, HCV, Syphilis and Malaria result entry",
          "Analyser interface for direct result capture",
          "Auto-quarantine of reactive units",
          "ABO/Rh grouping with antibody screening",
        ],
        tag: "Testing",
        widget: "mis",
        accentColor: "#C8102E",
        secondaryColor: "#7F77DD",
      },
      {
        title: "Component Separation & Storage",
        description:
          "Split whole blood into components and store each under its own shelf life.",
        features: [
          "PRBC, FFP, Platelets and Cryo separation",
          "Parent-child bag linking",
          "Expiry auto-calculation per component",
          "Storage location with near-expiry alerts",
        ],
        tag: "Components",
        widget: "vendor",
        accentColor: "#0B5CAB",
        secondaryColor: "#7F77DD",
      },
      {
        title: "Cross-match, Issue & Transfusion",
        description:
          "Serve ward and emergency requisitions fast without breaking compatibility checks.",
        features: [
          "Ward and patient requisitions from HIS",
          "Compatibility check with cross-match reports",
          "Issue slips with return handling",
          "Transfusion reaction records",
        ],
        tag: "Issue",
        widget: "billing",
        accentColor: "#C8102E",
        secondaryColor: "#0B5CAB",
      },
      {
        title: "Billing, Reports & Compliance",
        description:
          "Charge correctly, report instantly and face every inspection audit-ready.",
        features: [
          "Processing charges with replacement-donor adjustment",
          "Insurance and TPA billing integration",
          "Stock, discard, donor, TTI and camp reports with PDF/Excel export",
          "NACO / NBTC record-keeping with audit trail and role access",
        ],
        tag: "Compliance",
        widget: "mis",
        accentColor: "#0B5CAB",
        secondaryColor: "#C8102E",
      },
    ],
    dashboardType: "blood-bank-erp",
    businessImpact: [
      {
        value: "60%",
        label: "Less Component Wastage",
        description: "Cut discards with expiry discipline and component tracking",
        color: "#00F2FE",
        icon: "TrendingDown",
      },
      {
        value: "50%",
        label: "Faster Issue",
        description: "Serve requisitions faster with HIS-linked cross-match",
        color: "#7F77DD",
        icon: "Zap",
      },
      {
        value: "100%",
        label: "Reactive-Unit Quarantine",
        description: "Hold every TTI-reactive unit out of circulation automatically",
        color: "#F59E0B",
        icon: "Eye",
      },
      {
        value: "2X",
        label: "Camp Throughput",
        description: "Register and bleed more donors per camp with barcode flow",
        color: "#EC4899",
        icon: "TrendingUp",
      },
    ],
    workflow: [
      { id: 1, title: "Donor Registration", step: "Step 1", icon: "registration", color: "#C8102E", bgColor: "bg-rose-500", shadow: "shadow-rose-500/20" },
      { id: 2, title: "Screening", step: "Step 2", icon: "lab", color: "#C8102E", bgColor: "bg-red-500", shadow: "shadow-red-500/20" },
      { id: 3, title: "Collection", step: "Step 3", icon: "inventory", color: "#C8102E", bgColor: "bg-orange-500", shadow: "shadow-orange-500/20" },
      { id: 4, title: "Testing & Grouping", step: "Step 4", icon: "reports", color: "#C8102E", bgColor: "bg-amber-500", shadow: "shadow-amber-500/20" },
      { id: 5, title: "Component & Storage", step: "Step 5", icon: "pharmacy", color: "#C8102E", bgColor: "bg-indigo-500", shadow: "shadow-indigo-500/20" },
      { id: 6, title: "Cross-match & Issue", step: "Step 6", icon: "delivery", color: "#C8102E", bgColor: "bg-emerald-500", shadow: "shadow-emerald-500/20" },
    ],
    features: [
      { id: "donor", title: "Donor Management", description: "Barcoded donor IDs with complete history, deferral tracking and recall reminders." },
      { id: "collection", title: "Screening & Collection", description: "Eligibility screening with bag-wise camp and in-house collection tracking." },
      { id: "testing", title: "TTI Testing & Grouping", description: "Five-panel TTI testing with analyser interface and automatic quarantine." },
      { id: "components", title: "Component Separation", description: "PRBC, FFP, platelets and Cryo with parent-child linking and auto-expiry." },
      { id: "issue", title: "Cross-match & Issue", description: "HIS requisitions, compatibility cross-match, issue slips and reaction records." },
      { id: "compliance", title: "Billing & Compliance", description: "Processing charges, TPA billing and NACO-ready registers with audit trail." },
    ],
    headline: "Your Entire Blood Bank Operations,",
    heroStats: [
      { label: "Active Clients", value: "1000+" },
      { label: "Years of Trust", value: "30+" },
      { label: "GST Ready", value: "100%" },
    ],
    mockupImage: "/images/products/blood-bank-hero.png",
    solutions: [
      { title: "Barcoded donor-to-bag chain", desc: "Unique donor and bag IDs from registration to transfusion." },
      { title: "Automatic TTI quarantine", desc: "Reactive units blocked from issue the moment results are entered." },
      { title: "Component-level expiry control", desc: "Each component tracked against its own shelf life and location." },
      { title: "HIS-linked requisitions", desc: "Ward requests arrive digitally; cross-match and issue in minutes." },
      { title: "Inspection-ready registers", desc: "NACO/NBTC records, audit trails and NABH-aligned documentation." },
    ],
    roiEstimate: "₹8–15 lakhs/year",
    implementationSteps: [
      {
        num: "01",
        title: "Process Audit",
        description:
          "Our experts study your donation workflows, camp operations and licensing needs to design the optimal configuration.",
        bullets: ["Workflow mapping", "Licence requirement review", "Gap analysis"],
        image: "/images/products/audit.png",
        bg: "linear-gradient(135deg, #071428, #0d3060)",
      },
      {
        num: "02",
        title: "Seamless Integration",
        description:
          "We configure donor, testing, component and issue modules, connect HIS, LIS and analysers, and migrate existing records.",
        bullets: ["Module configuration", "HIS, LIS & analyser integration", "Data migration"],
        image: "/images/products/integration.png",
        bg: "linear-gradient(135deg, #071e1c, #0a3530)",
      },
      {
        num: "03",
        title: "Go-Live & Support",
        description:
          "Dedicated training for medical officers, technicians and counsellors followed by on-site support during go-live, on-premise or on ACGIL cloud.",
        bullets: ["Staff training", "On-site support", "Hypercare period"],
        image: "/images/products/support.png",
        bg: "linear-gradient(135deg, #100d28, #1a1040)",
      },
    ],
    clients: [
      { id: "bb1", name: "ACMA Blood Centre", logo: "/images/clients/acma-logo.webp" },
      { id: "bb2", name: "AHPL Hospitals", logo: "/images/clients/ahpl-logo.webp" },
      { id: "bb3", name: "AIIMS Blood Bank", logo: "/images/clients/aiims-logo.webp" },
      { id: "bb4", name: "Allahabad Medical", logo: "/images/clients/allahabad-bank-logo.webp" },
      { id: "bb5", name: "ANT Hospitals", logo: "/images/clients/ant-logo.webp" },
      { id: "bb6", name: "AOS Healthcare", logo: "/images/clients/aos-logo.webp" },
      { id: "bb7", name: "Arogyavaram Hospital", logo: "/images/clients/arogyavaram-logo.webp" },
      { id: "bb8", name: "Arora Medical", logo: "/images/clients/arora-aromatics-logo.webp" },
    ],
    industry: "Healthcare",
    heroImage: "/images/products/blood-bank-hero.png",
    marqueeItems: [
      "NACO Ready",
      "Barcode Tracking",
      "TTI Screening",
      "Component Control",
      "Cloud Security",
      "ISO Certified",
    ],
    testimonials: [
      {
        id: 341,
        name: "Dr. Vikram Sethi",
        role: "In-charge, Blood Bank",
        company: "Sethi Superspeciality Hospital",
        avatar: "/images/partners/1.png",
        quote:
          "Auto-quarantine of TTI-reactive units removed our biggest fear. No unit moves to issue without complete testing and grouping on record.",
        rating: 5,
      },
      {
        id: 342,
        name: "Sunita Deshmukh",
        role: "Blood Bank Technician",
        company: "Deshmukh Medical College",
        avatar: "/images/partners/2.webp",
        quote:
          "Component separation with parent-child linking and auto-expiry cut our platelet and FFP discards sharply. Near-expiry alerts come before it's too late.",
        rating: 5,
      },
      {
        id: 343,
        name: "Dr. Imran Sheikh",
        role: "Emergency Physician",
        company: "Sheikh Trauma Centre",
        avatar: "/images/partners/3.webp",
        quote:
          "Emergency requisitions from the ward reach the bank instantly and cross-matched units arrive in minutes. That speed saves lives during night trauma calls.",
        rating: 5,
      },
      {
        id: 344,
        name: "Pooja Rathi",
        role: "Donor Counsellor",
        company: "Rathi Voluntary Blood Centre",
        avatar: "/images/partners/4.webp",
        quote:
          "Camp collections used to mean a week of data entry. With barcode registration and donor recall messages, our repeat-donor turnout has doubled.",
        rating: 5,
      },
      {
        id: 345,
        name: "Dr. Anil Fernandes",
        role: "Medical Superintendent",
        company: "Fernandes City Hospital",
        avatar: "/images/partners/5.webp",
        quote:
          "Our last NACO inspection was the smoothest ever — donor-to-recipient traceability, registers and audit trails were all ready in one click.",
        rating: 5,
      },
    ],
    integrations: [
      { name: "Electra-HIS", icon: "hospital", category: "Healthcare" },
      { name: "Lab Information System", icon: "flask-conical", category: "Diagnostics" },
      { name: "Barcode & Label Printers", icon: "scan", category: "Hardware" },
      { name: "Analyser Interface", icon: "cpu", category: "Diagnostics Integration" },
      { name: "Tally Prime", icon: "calculator", category: "Accounting" },
      { name: "E-Invoice", icon: "file-digit", category: "Government" },
      { name: "WhatsApp Business API", icon: "message-square", category: "Communication" },
      { name: "SMS Gateway", icon: "message-circle", category: "Communication" },
    ],
    faqs: [
      {
        q: "Does it work standalone or only with HIS?",
        a: "Both. It runs as a standalone blood bank system and also integrates with Electra-HIS to receive ward requisitions directly.",
      },
      {
        q: "What are the deployment options?",
        a: "On-premise installation or hosted on ACGIL cloud servers, based on your requirement.",
      },
      {
        q: "Is barcode labelling supported?",
        a: "Yes, donor and bag-level barcode and label printing is supported.",
      },
      {
        q: "Can existing records be migrated?",
        a: "Yes, migration of existing donor and stock records is planned during the implementation phase.",
      },
      {
        q: "How are TTI-reactive units handled?",
        a: "Reactive results trigger automatic quarantine, blocking those units from issue while keeping a full audit record.",
      },
      {
        q: "Does it manage blood components?",
        a: "Yes, PRBC, FFP, platelets and Cryo are tracked with parent-child bag linking and automatic expiry calculation.",
      },
      {
        q: "Can it handle emergency requisitions?",
        a: "Yes, ward and emergency requisitions flow in from HIS with compatibility cross-match and rapid issue slips.",
      },
      {
        q: "Does it support replacement-donor billing?",
        a: "Yes, processing charges support replacement-donor adjustment along with insurance and TPA billing.",
      },
    ],
    industriesServed: [
      "Hospital Blood Banks",
      "Standalone Blood Centres",
      "Medical College Blood Banks",
      "Maternity & Trauma Centres",
      "Diagnostic Lab Chains",
      "Donation Camps & NGOs",
    ],
    comparison: [
      { feature: "Donor tracking", mxerp: "Barcoded IDs with history & deferrals", others: "Paper registers, history missed" },
      { feature: "TTI safety", mxerp: "Auto-quarantine on reactive results", others: "Manual segregation, error-prone" },
      { feature: "Components & expiry", mxerp: "Parent-child linking with auto-expiry", others: "Hand-tracked, high wastage" },
      { feature: "Cross-match & issue", mxerp: "HIS requisitions, issue in minutes", others: "Phone requests, slow manual match" },
      { feature: "Compliance & audit", mxerp: "NACO-ready registers with audit trail", others: "Compiled by hand before inspections" },
    ],
    seo: {
      title: "Blood Bank Management Software | Safe Transfusion & Donor Tracking - Acgil",
      description: "Blood bank management software for donor registration, TTI screening, component separation, cross-match, inventory and NACO-ready donor-to-recipient traceability.",
      keywords: "blood bank management software, blood bank ERP, donor management system, TTI screening software, blood component management, cross-match software, NACO compliance, Electra-HIS",
      canonical: SITE_URL + "/products/blood-bank-software",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      ogTitle: "Blood Bank Management Software | Safe Transfusion & Donor Tracking - Acgil",
      ogDescription: "Donor registration to safe issue — screening, TTI testing, components, cross-match and billing with full traceability on one platform.",
      ogUrl: SITE_URL + "/products/blood-bank-software",
      ogImage: SITE_URL + "/images/blood-bank.jpg",
      hreflang: {
        en: SITE_URL + "/products/blood-bank-software",
        "x-default": SITE_URL + "/products/blood-bank-software",
      },
      faqSchema: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "Does it work standalone or only with HIS?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Both. It runs standalone and also integrates with Electra-HIS to receive ward requisitions directly.",
            },
          },
          {
            "@type": "Question",
            name: "Is barcode labelling supported?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, donor and bag-level barcode and label printing is supported.",
            },
          },
          {
            "@type": "Question",
            name: "How are TTI-reactive units handled?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Reactive results trigger automatic quarantine, blocking those units from issue.",
            },
          },
          {
            "@type": "Question",
            name: "Does it manage blood components?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, PRBC, FFP, platelets and Cryo are tracked with parent-child bag linking and automatic expiry.",
            },
          },
          {
            "@type": "Question",
            name: "What are the deployment options?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "On-premise installation or hosted on ACGIL cloud servers.",
            },
          },
        ],
      },
    },
  },
  {
    id: "sales-automation",
    slug: "sales-automation-crm-software",
    category: "mx-crm",
    name: "Sales Automation CRM Software",
    tagline: "Sales Force Automation (SFA) & CRM System for Business Growth",
    href: "/products/sales-automation-crm-software",
    family: "Mx-CRM",
     headlineThin: "Sales Automation CRM Software",
     capHeadline: "Close More Deals with Mx-CRM",
     headlineBold: "Sales Force Automation & CRM System",
    headlineAccent: "Mx-CRM by ACGIL",
    subheadline:
      "Mx-CRM by ACGIL is a powerful Sales Automation CRM Software designed to manage leads, sales pipeline, follow-ups, deals, and customer relationships — all in one unified platform.",
    headline: "Your Entire Sales Process,",
    dashHeading:
      "Manage leads, follow-ups, sales teams, deals, and revenue from a single dashboard. Mx-CRM provides real-time visibility into sales performance, team productivity, and customer engagement.",
    challengesSubheadline:
      "Businesses face multiple sales challenges that impact conversions, revenue, and team efficiency.",
    PainPointsSubheading:
      "Businesses face multiple sales challenges that impact conversions, revenue, and team efficiency.",
    accentColor: "#2C78B9",
    dashboardType: "sales-auto",
    // ── stats / steps (unchanged) ──────────────────────────────
    stats: [
      { value: "40%", label: "TIME SAVED" },
      { value: "2x", label: "PIPELINE VELOCITY" },
      { value: "100%", label: "FORECAST ACCURACY" },
    ],
    steps: [
      {
        num: "01",
        title: "Workflow Design",
        description: "Map out your ideal sales process and trigger points.",
        bullets: ["Process mapping", "Trigger setup", "Email templates"],
        duration: "Week 1",
      },
      {
        num: "02",
        title: "Sequence Setup",
        description: "Build automated email/WhatsApp sequences for follow-ups.",
        bullets: ["Drip campaigns", "Auto-responders", "A/B testing"],
        duration: "Week 2",
      },
      {
        num: "03",
        title: "Enablement",
        description: "Train reps on using smart views and one-click actions.",
        bullets: ["Rep training", "Smart views", "Go live"],
        duration: "Week 3",
      },
    ],
    // ── workflow (unchanged) ───────────────────────────────────
    workflow: [
      { id: 1, title: "Lead Capture", step: "Step 1", icon: "scoring", color: "#2C78B9", bgColor: "bg-indigo-500", shadow: "shadow-indigo-500/20" },
      { id: 2, title: "Qualification", step: "Step 2", icon: "intel", color: "#2C78B9", bgColor: "bg-purple-500", shadow: "shadow-purple-500/20" },
      { id: 3, title: "Follow-up", step: "Step 3", icon: "sequences", color: "#2C78B9", bgColor: "bg-blue-500", shadow: "shadow-blue-500/20" },
      { id: 4, title: "Deal Closure", step: "Step 4", icon: "cpq", color: "#2C78B9", bgColor: "bg-emerald-500", shadow: "shadow-emerald-500/20" },
      { id: 5, title: "Customer Retention", step: "Step 5", icon: "commission", color: "#2C78B9", bgColor: "bg-amber-500", shadow: "shadow-amber-500/20" },
    ],
    // ── features (unchanged) ──────────────────────────────────
    features: [
      { id: "sequences", title: "Sales Sequences", description: "Multi-step automated outreach campaigns.", componentType: "sequences", colSpan: 2 },
      { id: "scoring", title: "Lead Scoring", description: "AI-based scoring to prioritize hot leads.", componentType: "scoring", colSpan: 1 },
      { id: "cpq", title: "CPQ Engine", description: "Configure, Price, Quote automation.", componentType: "cpq", colSpan: 1 },
      { id: "approvals", title: "Discount Approvals", description: "Automated hierarchy for special pricing.", componentType: "approvals", colSpan: 1 },
      { id: "forecast", title: "Forecasting", description: "Predictive revenue forecasting.", componentType: "forecast", colSpan: 2 },
      { id: "commission", title: "Commission Tracker", description: "Real-time rep commission calculation.", componentType: "commission", colSpan: 1 },
      { id: "intel", title: "Competitor Intel", description: "Track win/loss reasons by competitor.", componentType: "intel", colSpan: 1 },
    ],
    // ── capabilities (unchanged) ──────────────────────────────
    capabilities: [
      {
        title: "Lead Management System",
        description: "Centralized lead capture, assignment, scoring, and conversion tracking.",
        features: [
          "Lead capture from multiple channels",
          "Lead assignment & tracking",
          "Lead scoring & qualification",
          "Conversion tracking",
        ],
        tag: "Leads",
        widget: "production",
        accentColor: "#2C78B9",
        secondaryColor: "#7F77DD",
      },
      {
        title: "Sales Pipeline Management",
        description: "Visual sales funnel with stage-wise deal tracking and forecasting.",
        features: [
          "Visual sales funnel",
          "Stage-wise deal tracking",
          "Opportunity management",
          "Sales forecasting",
        ],
        tag: "Pipeline",
        widget: "inventory",
        accentColor: "#7F77DD",
        secondaryColor: "#059A8F",
      },
      {
        title: "Task & Follow-Up Automation",
        description: "Automated reminders, email & call tracking, and workflow automation.",
        features: [
          "Automated reminders",
          "Email & call tracking",
          "Activity scheduling",
          "Workflow automation",
        ],
        tag: "Automation",
        widget: "hr",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9",
      },
      {
        title: "Customer Relationship Management (CRM)",
        description: "Centralized customer database with interaction history and insights.",
        features: [
          "Centralized customer database",
          "Interaction history tracking",
          "Customer insights",
          "Relationship tracking",
        ],
        tag: "CRM",
        widget: "billing",
        accentColor: "#2C78B9",
        secondaryColor: "#7F77DD",
      },
      {
        title: "Sales Team Management",
        description: "Field sales tracking, activity monitoring, and target management.",
        features: [
          "Field sales tracking",
          "Activity monitoring",
          "Performance tracking",
          "Target management",
        ],
        tag: "Team",
        widget: "procurement",
        accentColor: "#7F77DD",
        secondaryColor: "#059A8F",
      },
      {
        title: "MIS & Reporting",
        description: "Sales reports, conversion analytics, and real-time revenue dashboards.",
        features: [
          "Sales reports & dashboards",
          "Conversion analytics",
          "Revenue tracking",
          "Real-time insights",
        ],
        tag: "Analytics",
        widget: "mis",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9",
      },
    ],
    // ── pain points (verified) ─────────────────────────────────
    painPoints: [
      { title: "Lead Leakage", desc: "Missed opportunities due to no centralized lead tracking system." },
      { title: "Manual Follow-ups", desc: "Delayed follow-ups and reminders causing lost deals." },
      { title: "No Pipeline Visibility", desc: "Lack of real-time visibility into sales pipeline and deal stages." },
      { title: "Poor CRM Tracking", desc: "Inefficient customer relationship and interaction tracking." },
      { title: "Team Monitoring Gaps", desc: "No effective way to monitor sales team activity and performance." },
    ],
    // ── solution (verified) ────────────────────────────────────
    solutionHeadline: "How Sales Automation CRM Software Solves These Challenges",
    description: "Mx-CRM automates and streamlines the entire sales lifecycle.",
    solutionFeatures: [
      "Centralized lead management",
      "Automated follow-ups and reminders",
      "Real-time sales pipeline tracking",
      "Deal and opportunity management",
      "Customer relationship management (CRM)",
    ],
    solutions: [
      { title: "Sales Sequences", desc: "Automated multi-step outreach across email and WhatsApp." },
      { title: "AI Forecasting", desc: "Predict revenue accurately based on historical win rates." },
      { title: "Smart Approvals", desc: "Automated approval hierarchy for instant pricing decisions." },
      { title: "CPQ Engine", desc: "Generate complex B2B quotes with one click." },
      { title: "Workflow Automation", desc: "Eliminate admin tasks so reps can focus on selling." },
    ],
    // ── business impact (verified) ────────────────────────────
    businessImpact: [
      { value: "60%", label: "Lead Conversion Rate", description: "Higher lead-to-deal conversion rates with smart tracking", color: "#00F2FE", icon: "TrendingUp" },
      { value: "45%", label: "Sales Team Productivity", description: "More selling time with automated workflows", color: "#7F77DD", icon: "TrendingUp" },
      { value: "40%", label: "Manual Work Reduced", description: "Eliminate repetitive tasks with automation", color: "#F59E0B", icon: "TrendingDown" },
      { value: "2X", label: "Better Pipeline Visibility", description: "Real-time deal tracking and forecasting", color: "#EC4899", icon: "Eye" },
    ],
    // ── comparison (verified) ─────────────────────────────────
    comparison: [
      { feature: "Accuracy", mxerp: "High", others: "Error-prone" },
      { feature: "Speed", mxerp: "Fast", others: "Slow" },
      { feature: "Automation", mxerp: "Yes", others: "No" },
      { feature: "Tracking", mxerp: "Real-time", others: "Limited" },
      { feature: "Efficiency", mxerp: "High", others: "Low" },
    ],
    // ── integrations (unchanged) ──────────────────────────────
    integrations: [
      { name: "LinkedIn Sales Navigator", category: "Prospecting & Intelligence", icon: "users" },
      { name: "WhatsApp Cloud API", category: "Outreach Sequences", icon: "message-square" },
      { name: "Google Workspace / Outlook", category: "Email Sequence Sync", icon: "mail" },
      { name: "Zoom / Google Meet", category: "Meeting Scheduling", icon: "video" },
      { name: "Tally / SAP / Oracle", category: "Revenue & Order Sync", icon: "database" },
    ],
    // ── industries (unchanged) ────────────────────────────────
    industriesServed: [
      "Enterprise SaaS & Software",
      "Industrial Machinery & Capital Goods",
      "B2B Wholesale & Distribution",
      "Professional Services & Consulting",
      "Corporate Financial Services",
      "Commercial Real Estate",
      "Telecommunications & IT Services",
    ],
    // ── clients / testimonials (unchanged) ───────────────────
    clients: [
      { id: "sa1", name: "ACMA Automations", logo: "/images/clients/acma-logo.webp" },
      { id: "sa2", name: "AHPL Networks", logo: "/images/clients/ahpl-logo.webp" },
      { id: "sa3", name: "AIIMS Ventures", logo: "/images/clients/aiims-logo.webp" },
      { id: "sa4", name: "Allahabad Industries", logo: "/images/clients/allahabad-bank-logo.webp" },
      { id: "sa5", name: "ANT Group", logo: "/images/clients/ant-logo.webp" },
      { id: "sa6", name: "AOS Corporate", logo: "/images/clients/aos-logo.webp" },
      { id: "sa7", name: "Arogyavaram Systems", logo: "/images/clients/arogyavaram-logo.webp" },
      { id: "sa8", name: "Arora Enterprises", logo: "/images/clients/arora-aromatics-logo.webp" },
    ],
    testimonials: [
      {
        id: 225,
        name: "Amit Sharma",
        role: "Sales Head",
        company: "Wipro Ltd",
        avatar: "/images/partners/30.webp",
        quote: "Mx-CRM's multi-channel lead capture eliminated our lead leakage completely. We now capture 100% of leads from IndiaMart, JustDial, and our website. The 30% conversion lift has directly impacted our revenue growth.",
        rating: 5,
      },
      {
        id: 226,
        name: "Neha Gupta",
        role: "Business Development Manager",
        company: "Infosys",
        avatar: "/images/partners/31.webp",
        quote: "The field sales tracking feature gives us complete visibility into our 150-member sales team. Response time improved from 4 hours to 15 minutes, and we've seen a 40% improvement in deal closure rates.",
        rating: 5,
      },
      {
        id: 227,
        name: "Rajiv Malhotra",
        role: "Director",
        company: "TCS",
        avatar: "/images/partners/32.webp",
        quote: "WhatsApp integration through the official API has transformed our client communication. We can now send quotes, follow up, and close deals entirely through WhatsApp with full tracking and compliance.",
        rating: 5,
      },
    ],
    ctaHeadline: "Supercharge Your Sales Team",
    ctaSubtext: "Join enterprise teams closing complex deals faster.",
    // ── FAQs (verified) ───────────────────────────────────────
    faqs: [
      { q: "What is Sales Automation CRM Software?", a: "It is a system that helps manage leads, sales pipeline, follow-ups, and customer relationships efficiently." },
      { q: "What is CRM Software?", a: "CRM (Customer Relationship Management) software helps businesses manage customer interactions and improve relationships." },
      { q: "What is Sales Force Automation (SFA)?", a: "SFA automates sales activities like lead tracking, follow-ups, and reporting." },
      { q: "Can it track sales team performance?", a: "Yes, it provides real-time tracking and performance analytics." },
      { q: "Is it suitable for small businesses?", a: "Yes, Mx-CRM is scalable for startups and enterprises." },
      { q: "Can it automate follow-ups?", a: "Yes, it automates reminders, emails, and tasks." },
      { q: "Is it cloud-based or on-premise?", a: "Mx-CRM supports both deployment options." },
    ],
    // ── SEO (verified) ────────────────────────────────────────
    seo: {
      title: "Sales Automation CRM Software | CRM for Sales Management - Acgil",
      description: "Sales automation CRM software for lead tracking, customer follow-ups, sales activities, pipeline visibility, team coordination, and performance reporting.",
      keywords: "sales automation crm software, crm software india, sales force automation software, lead management system, crm for sales teams, pipeline management crm, customer relationship management software",
      canonical: SITE_URL + "/products/sales-automation-crm-software",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      ogTitle: "Sales Automation CRM Software | CRM for Sales Management - Acgil",
      ogDescription: "Sales automation CRM software for lead tracking, customer follow-ups, sales activities, pipeline visibility, team coordination, and performance reporting.",
      ogUrl: SITE_URL + "/products/sales-automation-crm-software",
      ogImage: SITE_URL + "/images/crm-software.jpg",
      hreflang: {
        en: SITE_URL + "/products/sales-automation-crm-software",
        "x-default": SITE_URL + "/products/sales-automation-crm-software",
      },
      faqSchema: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          { "@type": "Question", name: "What is Sales Automation CRM Software?", acceptedAnswer: { "@type": "Answer", text: "Sales Automation CRM Software helps manage leads, pipeline, follow-ups, and customer relationships in one system." } },
          { "@type": "Question", name: "What is CRM Software?", acceptedAnswer: { "@type": "Answer", text: "CRM software helps businesses manage customer interactions and improve relationships." } },
          { "@type": "Question", name: "What is Sales Force Automation (SFA)?", acceptedAnswer: { "@type": "Answer", text: "SFA automates sales activities like lead tracking, follow-ups, and reporting." } },
          { "@type": "Question", name: "Can it track sales team performance?", acceptedAnswer: { "@type": "Answer", text: "Yes, it provides real-time tracking and performance analytics." } },
          { "@type": "Question", name: "Can it automate follow-ups?", acceptedAnswer: { "@type": "Answer", text: "Yes, it automates reminders, emails, and tasks." } },
        ],
      },
    },
  },
  {
    id: "lead-management",
    slug: "lead-management-crm-software",
    category: "mx-crm",
    name: "Lead Management CRM Software",
    tagline: "Lead Tracking & Sales Conversion System",
    href: "/products/lead-management-crm-software",
    family: "Mx-CRM",
     headlineThin: "Lead Management CRM Software",
     capHeadline: "Turn More Leads into Customers with Mx-CRM",
     headlineBold: "Lead Tracking & Sales Conversion System",
    headlineAccent: "Mx-CRM by ACGIL",
    subheadline:
      "Mx-CRM by ACGIL is a powerful Lead Management CRM Software designed to capture, track, nurture, and convert leads into customers — all in one unified platform.",
    headline: "Your Entire Lead Funnel,",
    dashHeading:
      "Manage lead capture, follow-ups, pipeline, and conversions from a single dashboard. Mx-CRM provides real-time visibility into lead status, sales performance, and conversion rates.",
    challengesSubheadline:
      "Businesses struggle with managing leads effectively, resulting in lost opportunities and low conversion rates.",
    PainPointsSubheading:
      "Businesses struggle with managing leads effectively, resulting in lost opportunities and low conversion rates.",
    accentColor: "#2C78B9",
    dashboardType: "lead-management",
    // ── stats (unchanged) ─────────────────────────────────────
    stats: [
      { value: "30%", label: "CONVERSION LIFT" },
      { value: "0", label: "LEADS DROPPED" },
      { value: "100%", label: "VISIBILITY" },
    ],
    // ── steps (unchanged) ─────────────────────────────────────
    steps: [
      {
        num: "01",
        title: "Capture Automation",
        description: "Connect website, WhatsApp, IndiaMart, and JustDial.",
        bullets: ["API integration", "Web forms", "WhatsApp"],
        duration: "Day 1",
      },
      {
        num: "02",
        title: "Pipeline Setup",
        description: "Configure stages, tasks, and follow-up rules.",
        bullets: ["Stage mapping", "Auto-tasks", "Team setup"],
        duration: "Day 2",
      },
      {
        num: "03",
        title: "Sales Execution",
        description: "Teams start calling and updating from the mobile app.",
        bullets: ["Mobile app", "Call logging", "Live tracking"],
        duration: "Day 3",
      },
    ],
    workflow: [
      { id: 1, title: "Lead Capture", step: "Step 1", icon: "scoring", color: "#2C78B9", bgColor: "bg-indigo-500", shadow: "shadow-indigo-500/20" },
      { id: 2, title: "Qualification", step: "Step 2", icon: "intel", color: "#2C78B9", bgColor: "bg-purple-500", shadow: "shadow-purple-500/20" },
      { id: 3, title: "Follow-up", step: "Step 3", icon: "sequences", color: "#2C78B9", bgColor: "bg-blue-500", shadow: "shadow-blue-500/20" },
      { id: 4, title: "Nurturing", step: "Step 4", icon: "approvals", color: "#2C78B9", bgColor: "bg-emerald-500", shadow: "shadow-emerald-500/20" },
      { id: 5, title: "Conversion", step: "Step 5", icon: "cpq", color: "#2C78B9", bgColor: "bg-amber-500", shadow: "shadow-amber-500/20" },
      { id: 6, title: "Customer", step: "Step 6", icon: "commission", color: "#2C78B9", bgColor: "bg-rose-500", shadow: "shadow-rose-500/20" },
    ],
    features: [
      { id: "capture", title: "Lead Capture", description: "Capture leads automatically from website, ads, social media, and major portals.", componentType: "scoring", colSpan: 1 },
      { id: "pipeline", title: "Lead Pipeline", description: "Track lead status, stage movement, and conversion progress in real time.", componentType: "forecast", colSpan: 2 },
      { id: "nurturing", title: "Lead Nurturing", description: "Automate reminders, email, WhatsApp, and follow-up scheduling.", componentType: "sequences", colSpan: 1 },
      { id: "scoring", title: "Lead Scoring", description: "Prioritize hot, warm, and cold leads with smart qualification rules.", componentType: "intel", colSpan: 1 },
      { id: "team", title: "Sales Team Management", description: "Automate lead assignment and monitor team activity, targets, and performance.", componentType: "approvals", colSpan: 1 },
      { id: "reporting", title: "MIS & Reporting", description: "View lead conversion, campaign performance, ROI, and real-time dashboards.", componentType: "commission", colSpan: 2 },
    ],
    // ── capabilities (updated to match verified content) ──────
    capabilities: [
      {
        title: "Lead Capture System",
        description: "Capture leads automatically from website, ads, social media, and all major portals.",
        features: [
          "Capture leads from website, ads, social media",
          "API integrations (IndiaMart, JustDial)",
          "Auto lead entry & deduplication",
          "Duplicate lead prevention",
        ],
        tag: "Capture",
        widget: "production",
        accentColor: "#2C78B9",
        secondaryColor: "#059A8F",
      },
      {
        title: "Lead Tracking & Pipeline",
        description: "Real-time lead status tracking with sales pipeline visualization and stage-wise movement.",
        features: [
          "Real-time lead status tracking",
          "Sales pipeline visualization",
          "Stage-wise lead movement",
          "Conversion tracking",
        ],
        tag: "Pipeline",
        widget: "inventory",
        accentColor: "#059A8F",
        secondaryColor: "#7F77DD",
      },
      {
        title: "Lead Nurturing & Follow-Ups",
        description: "Automated reminders, email & WhatsApp integration, and follow-up scheduling.",
        features: [
          "Automated reminders",
          "Email & WhatsApp integration",
          "Follow-up scheduling",
          "Activity tracking",
        ],
        tag: "Nurturing",
        widget: "hr",
        accentColor: "#7F77DD",
        secondaryColor: "#2C78B9",
      },
      {
        title: "Lead Scoring & Qualification",
        description: "Priority-based lead scoring with hot/warm/cold categorization and smart filtering.",
        features: [
          "Priority-based lead scoring",
          "Qualification rules",
          "Hot/warm/cold lead categorization",
          "Smart filtering",
        ],
        tag: "Qualification",
        widget: "procurement",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9",
      },
      {
        title: "Sales Team Management",
        description: "Automated lead assignment with performance tracking and target management.",
        features: [
          "Lead assignment automation",
          "Performance tracking",
          "Activity monitoring",
          "Target tracking",
        ],
        tag: "Team",
        widget: "billing",
        accentColor: "#2C78B9",
        secondaryColor: "#7F77DD",
      },
      {
        title: "MIS & Reporting",
        description: "Lead conversion reports, campaign performance analytics, and real-time dashboards.",
        features: [
          "Lead conversion reports",
          "Campaign performance analytics",
          "ROI tracking",
          "Real-time dashboards",
        ],
        tag: "Analytics",
        widget: "mis",
        accentColor: "#7F77DD",
        secondaryColor: "#059A8F",
      },
    ],
    // ── pain points (verified) ─────────────────────────────────
    painPoints: [
      { title: "Lead Leakage", desc: "Missed follow-ups and lost opportunities due to no centralized lead database." },
      { title: "Manual Data Entry", desc: "Error-prone manual entry wasting hours and causing inaccurate records." },
      { title: "No Centralized DB", desc: "Lead data scattered across sheets, emails, and portals with no single view." },
      { title: "Poor Tracking", desc: "No real-time visibility into lead status or pipeline movement." },
      { title: "Low Conversion Rates", desc: "Unstructured follow-ups and nurturing leading to lost deals." },
    ],
    // ── solution (verified) ────────────────────────────────────
    solutionHeadline: "How Lead Management CRM Software Solves These Challenges",
    description: "Mx-CRM automates and streamlines the entire lead lifecycle.",
    solutionFeatures: [
      "Automatic lead capture from multiple sources",
      "Smart lead assignment to sales teams",
      "Automated follow-ups and reminders",
      "Real-time lead tracking and pipeline visibility",
      "Lead nurturing and conversion management",
    ],
    solutions: [
      { title: "Auto-Capture", desc: "Fetch leads from website, portals, and WhatsApp instantly with zero manual entry." },
      { title: "Smart Workflows", desc: "Automate tasks and reminders to ensure zero lead drop with escalation rules." },
      { title: "Visual Pipeline", desc: "Track every deal stage with intuitive Kanban boards and real-time analytics." },
      { title: "Instant Alerts", desc: "Get real-time notifications for new leads and follow-ups with mobile push alerts." },
      { title: "Unified CRM", desc: "Centralized database for all customer interactions, history, and documents." },
    ],
    // ── business impact (verified) ────────────────────────────
    businessImpact: [
      { value: "65%", label: "Lead Conversion Rate", description: "Higher conversions with smart lead nurturing", color: "#00F2FE", icon: "TrendingUp" },
      { value: "50%", label: "Lead Leakage Reduced", description: "Zero missed leads with auto-capture and assignment", color: "#7F77DD", icon: "TrendingDown" },
      { value: "40%", label: "Sales Productivity", description: "More selling time with automated workflows", color: "#F59E0B", icon: "TrendingUp" },
      { value: "2X", label: "Faster Lead Response Time", description: "Instant alerts and auto-assignment speed up response", color: "#EC4899", icon: "Zap" },
    ],
    // ── comparison (verified) ─────────────────────────────────
    comparison: [
      { feature: "Accuracy", mxerp: "High", others: "Error-prone" },
      { feature: "Speed", mxerp: "Fast", others: "Slow" },
      { feature: "Automation", mxerp: "Yes", others: "No" },
      { feature: "Tracking", mxerp: "Real-time", others: "Limited" },
      { feature: "Efficiency", mxerp: "High", others: "Low" },
    ],
    // ── integrations (unchanged) ──────────────────────────────
    integrations: [
      { name: "IndiaMart / JustDial API", category: "Lead Source Integration", icon: "zap" },
      { name: "WhatsApp Cloud API", category: "Communication & Follow-up", icon: "message-square" },
      { name: "Exotel / Knowlarity", category: "Cloud Telephony & Calling", icon: "phone" },
      { name: "Google Ads / Meta Ads", category: "Marketing Attribution", icon: "trending-up" },
      { name: "Tally / SAP", category: "ERP Billing Sync", icon: "database" },
    ],
    // ── industries (unchanged) ────────────────────────────────
    industriesServed: [
      "B2B Manufacturing Sales",
      "Software & IT Services",
      "Real Estate Brokerage",
      "Financial Services & Insurance",
      "Education & Training Institutes",
      "Travel & Hospitality Booking",
      "Consulting & Professional Services",
    ],
    // ── clients / testimonials (unchanged) ───────────────────
    clients: [
      { id: "lm1", name: "ACMA Sales", logo: "/images/clients/acma-logo.webp" },
      { id: "lm2", name: "AHPL Corp", logo: "/images/clients/ahpl-logo.webp" },
      { id: "lm3", name: "AIIMS Outreach", logo: "/images/clients/aiims-logo.webp" },
      { id: "lm4", name: "Allahabad Finance", logo: "/images/clients/allahabad-bank-logo.webp" },
      { id: "lm5", name: "ANT Consulting", logo: "/images/clients/ant-logo.webp" },
      { id: "lm6", name: "AOS Technologies", logo: "/images/clients/aos-logo.webp" },
      { id: "lm7", name: "Arogyavaram Edu", logo: "/images/clients/arogyavaram-logo.webp" },
      { id: "lm8", name: "Arora Real Estate", logo: "/images/clients/arora-aromatics-logo.webp" },
    ],
    testimonials: [
      {
        id: 225,
        name: "Amit Sharma",
        role: "Sales Head",
        company: "Wipro Ltd",
        avatar: "/images/partners/30.webp",
        quote: "Mx-CRM's multi-channel lead capture eliminated our lead leakage completely. We now capture 100% of leads from IndiaMart, JustDial, and our website. The 30% conversion lift has directly impacted our revenue growth.",
        rating: 5,
      },
      {
        id: 226,
        name: "Neha Gupta",
        role: "Business Development Manager",
        company: "Infosys",
        avatar: "/images/partners/31.webp",
        quote: "The field sales tracking feature gives us complete visibility into our 150-member sales team. Response time improved from 4 hours to 15 minutes, and we've seen a 40% improvement in deal closure rates.",
        rating: 5,
      },
      {
        id: 227,
        name: "Rajiv Malhotra",
        role: "Director",
        company: "TCS",
        avatar: "/images/partners/32.webp",
        quote: "WhatsApp integration through the official API has transformed our client communication. We can now send quotes, follow up, and close deals entirely through WhatsApp with full tracking and compliance.",
        rating: 5,
      },
    ],
    ctaHeadline: "Stop Losing Leads to Competitors",
    ctaSubtext: "Join fast-growing B2B teams closing deals with Mx-CRM.",
    // ── FAQs (verified) ───────────────────────────────────────
    faqs: [
      { q: "What is Lead Management CRM Software?", a: "It is a system that helps capture, track, and convert leads efficiently." },
      { q: "Can it capture leads automatically?", a: "Yes, it captures leads from multiple sources like websites, ads, and social media." },
      { q: "Does it support follow-up automation?", a: "Yes, it automates reminders, emails, and notifications." },
      { q: "Can it track lead status?", a: "Yes, it provides real-time tracking of every lead." },
      { q: "Is it suitable for small businesses?", a: "Yes, Mx-CRM is scalable for startups and enterprises." },
      { q: "Can it improve conversion rates?", a: "Yes, it helps optimize lead nurturing and tracking." },
      { q: "Is it cloud-based or on-premise?", a: "Mx-CRM supports both deployment options." },
    ],
    // ── SEO (verified) ────────────────────────────────────────
    seo: {
      title: "Lead Management CRM Software | Lead Tracking & CRM Solution - Acgil",
      description: "Lead management CRM software for capturing leads, tracking prospects, recording interactions, assigning follow-ups, monitoring sales pipelines, and improving lead visibility.",
      keywords: "lead management crm software, lead tracking system, crm for lead management, lead capture software, sales lead management system, lead conversion software, crm software india",
      canonical: SITE_URL + "/products/lead-management-crm-software",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      ogTitle: "Lead Management CRM Software | Lead Tracking & CRM Solution - Acgil",
      ogDescription: "Lead management CRM software for capturing leads, tracking prospects, recording interactions, assigning follow-ups, monitoring sales pipelines, and improving lead visibility.",
      ogUrl: SITE_URL + "/products/lead-management-crm-software",
      ogImage: SITE_URL + "/images/lead-crm.jpg",
      hreflang: {
        en: SITE_URL + "/products/lead-management-crm-software",
        "x-default": SITE_URL + "/products/lead-management-crm-software",
      },
      faqSchema: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          { "@type": "Question", name: "What is Lead Management CRM Software?", acceptedAnswer: { "@type": "Answer", text: "Lead Management CRM Software helps capture, track, and convert leads into customers." } },
          { "@type": "Question", name: "Can it capture leads automatically?", acceptedAnswer: { "@type": "Answer", text: "Yes, it captures leads from websites, ads, and social media." } },
          { "@type": "Question", name: "Does it support follow-up automation?", acceptedAnswer: { "@type": "Answer", text: "Yes, it automates reminders and follow-ups." } },
          { "@type": "Question", name: "Can it improve conversion rates?", acceptedAnswer: { "@type": "Answer", text: "Yes, it improves lead nurturing and increases conversions." } },
        ],
      },
    },
  },
  {
    id: "service-management",
    slug: "service-management-crm-software",
    category: "mx-crm",
    name: "Service Management CRM Software",
    tagline: "Service & Support Automation System",
    href: "/products/service-management-crm-software",
    family: "Mx-CRM",
     headlineThin: "Service Management CRM Software",
     capHeadline: "Deliver Better Service with Mx-CRM",
     headlineBold: "Service & Support Automation System",
    headlineAccent: "Mx-CRM by ACGIL",
    subheadline:
      "Mx-CRM by ACGIL is a powerful Service Management CRM Software designed to manage service requests, tickets, customer support, field service, and after-sales operations — all in one unified platform.",
    headline: "Your Entire Service Operations,",
    dashHeading:
      "Manage service tickets, customer issues, field staff, SLAs, and resolutions from a single dashboard. Mx-CRM provides real-time visibility into service performance, response times, and customer satisfaction.",
    challengesSubheadline:
      "Businesses face multiple service-related challenges that impact customer satisfaction and operational efficiency.",
    PainPointsSubheading:
      "Businesses face multiple service-related challenges that impact customer satisfaction and operational efficiency.",
    accentColor: "#059A8F",
    dashboardType: "service",
    // ── stats (unchanged) ─────────────────────────────────────
    stats: [
      { value: "50%", label: "FASTER RESOLUTION" },
      { value: "100%", label: "SLA COMPLIANCE" },
      { value: "4.8", label: "AVG CSAT" },
    ],
    // ── steps (unchanged) ─────────────────────────────────────
    steps: [
      {
        num: "01",
        title: "Asset & Contract Setup",
        description: "Load customer assets and active AMC contracts.",
        bullets: ["Asset register", "AMC upload", "SLA config"],
        duration: "Week 1",
      },
      {
        num: "02",
        title: "Ticketing Workflow",
        description: "Configure routing rules and escalation matrices.",
        bullets: ["Routing rules", "Escalations", "Email/SMS"],
        duration: "Week 2",
      },
      {
        num: "03",
        title: "Engineer Enablement",
        description: "Deploy field app for job cards and part requests.",
        bullets: ["Mobile app", "Part inventory", "Sign-offs"],
        duration: "Week 3",
      },
    ],
    workflow: [
      { id: 1, title: "Service Request", step: "Step 1", icon: "quotes", color: "#2C78B9", bgColor: "bg-indigo-500", shadow: "shadow-indigo-500/20" },
      { id: 2, title: "Ticket Creation", step: "Step 2", icon: "scoring", color: "#2C78B9", bgColor: "bg-purple-500", shadow: "shadow-purple-500/20" },
      { id: 3, title: "Assignment", step: "Step 3", icon: "approvals", color: "#2C78B9", bgColor: "bg-blue-500", shadow: "shadow-blue-500/20" },
      { id: 4, title: "Resolution", step: "Step 4", icon: "intel", color: "#2C78B9", bgColor: "bg-emerald-500", shadow: "shadow-emerald-500/20" },
      { id: 5, title: "Feedback", step: "Step 5", icon: "sequences", color: "#2C78B9", bgColor: "bg-amber-500", shadow: "shadow-amber-500/20" },
      { id: 6, title: "Closure", step: "Step 6", icon: "commission", color: "#2C78B9", bgColor: "bg-rose-500", shadow: "shadow-rose-500/20" },
    ],
    features: [
      { id: "tickets", title: "Ticket Management", description: "Create, categorize, prioritize, track, and assign service tickets from one system.", componentType: "scoring", colSpan: 2 },
      { id: "support", title: "Customer Support", description: "Manage complaints, customer interactions, feedback, and multi-channel support.", componentType: "quotes", colSpan: 1 },
      { id: "field-service", title: "Field Service", description: "Assign technicians, schedule jobs, track GPS activity, and capture service reports.", componentType: "sequences", colSpan: 1 },
      { id: "sla", title: "SLA & Escalation", description: "Define SLAs, monitor response times, and trigger automatic escalation rules.", componentType: "approvals", colSpan: 1 },
      { id: "contracts", title: "Service Contracts", description: "Track AMC contracts, renewals, warranties, and customer service history.", componentType: "cpq", colSpan: 1 },
      { id: "reporting", title: "MIS & Reporting", description: "Analyze service performance, ticket trends, and customer satisfaction dashboards.", componentType: "commission", colSpan: 2 },
    ],
    // ── capabilities (updated to match verified content) ──────
    capabilities: [
      {
        title: "Ticket Management System",
        description: "Centralized service ticket creation, categorization, prioritization, and auto assignment.",
        features: [
          "Service ticket creation",
          "Ticket categorization & prioritization",
          "Status tracking",
          "Auto ticket assignment",
        ],
        tag: "Tickets",
        widget: "production",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9",
      },
      {
        title: "Customer Support Management",
        description: "Multi-channel support with complaint management and interaction history tracking.",
        features: [
          "Multi-channel support (call, email, WhatsApp)",
          "Complaint management",
          "Customer interaction history",
          "Feedback tracking",
        ],
        tag: "Support",
        widget: "billing",
        accentColor: "#2C78B9",
        secondaryColor: "#7F77DD",
      },
      {
        title: "Field Service Management",
        description: "Technician assignment with GPS tracking, job scheduling, and service reporting.",
        features: [
          "Technician assignment",
          "GPS tracking",
          "Job scheduling",
          "Service reporting",
        ],
        tag: "Field Ops",
        widget: "hr",
        accentColor: "#7F77DD",
        secondaryColor: "#059A8F",
      },
      {
        title: "SLA & Escalation Management",
        description: "SLA definition and tracking with auto escalation rules and resolution monitoring.",
        features: [
          "SLA definition & tracking",
          "Auto escalation rules",
          "Response time monitoring",
          "Resolution tracking",
        ],
        tag: "SLA",
        widget: "inventory",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9",
      },
      {
        title: "Service Contract Management",
        description: "AMC/contract tracking with renewal alerts, service history, and warranty management.",
        features: [
          "AMC/contract tracking",
          "Renewal alerts",
          "Service history",
          "Warranty management",
        ],
        tag: "Contracts",
        widget: "mis",
        accentColor: "#2C78B9",
        secondaryColor: "#7F77DD",
      },
      {
        title: "MIS & Reporting",
        description: "Service performance reports, ticket analytics, and customer satisfaction dashboards.",
        features: [
          "Service performance reports",
          "Ticket analytics",
          "Customer satisfaction reports",
          "Real-time dashboards",
        ],
        tag: "Analytics",
        widget: "procurement",
        accentColor: "#7F77DD",
        secondaryColor: "#059A8F",
      },
    ],
    // ── pain points (verified) ─────────────────────────────────
    painPoints: [
      { title: "Delayed Service Responses", desc: "Slow response times impacting customer trust and satisfaction scores." },
      { title: "Poor Ticket Tracking", desc: "No real-time visibility into ticket status causing follow-up chaos." },
      { title: "Field Staff Gaps", desc: "Lack of field staff visibility leading to scheduling conflicts and delays." },
      { title: "Inefficient Complaints", desc: "Manual complaint management causing missed escalations and repeat issues." },
      { title: "Low Customer Satisfaction", desc: "Unresolved issues and lack of transparency driving low CSAT scores." },
    ],
    // ── solution (verified) ────────────────────────────────────
    solutionHeadline: "How Service Management CRM Software Solves These Challenges",
    description: "Mx-CRM automates and streamlines the entire service lifecycle.",
    solutionFeatures: [
      "Centralized ticket management system",
      "Automated ticket assignment",
      "Real-time service tracking",
      "SLA-based service management",
      "Customer communication tracking",
    ],
    solutions: [
      { title: "SLA Tracking", desc: "Auto-escalations to ensure every ticket is resolved on time with real-time alerts." },
      { title: "Auto-Renewals", desc: "Smart alerts for upcoming AMC expiries and renewals with automated quote generation." },
      { title: "Mobile App", desc: "Digital job cards and real-time updates from field engineers with offline capability." },
      { title: "Inventory Sync", desc: "Track part consumption directly from the field app with cost center allocation." },
      { title: "Client Portal", desc: "Let customers log and track their own service requests with real-time status updates." },
    ],
    // ── business impact (verified) ────────────────────────────
    businessImpact: [
      { value: "50%", label: "Faster Issue Resolution", description: "Resolve tickets faster with SLA tracking and smart routing", color: "#00F2FE", icon: "TrendingUp" },
      { value: "40%", label: "Customer Satisfaction", description: "Improved CSAT with transparent, timely service delivery", color: "#7F77DD", icon: "TrendingUp" },
      { value: "35%", label: "Service Delays Reduced", description: "Fewer bottlenecks with automated assignments and escalations", color: "#F59E0B", icon: "TrendingDown" },
      { value: "2X", label: "Better Ticket Tracking", description: "Full real-time visibility into every ticket and its status", color: "#EC4899", icon: "Eye" },
    ],
    // ── comparison (verified) ─────────────────────────────────
    comparison: [
      { feature: "Accuracy", mxerp: "High", others: "Error-prone" },
      { feature: "Speed", mxerp: "Fast", others: "Slow" },
      { feature: "Automation", mxerp: "Yes", others: "No" },
      { feature: "Tracking", mxerp: "Real-time", others: "Limited" },
      { feature: "Efficiency", mxerp: "High", others: "Low" },
    ],
    // ── integrations (unchanged) ──────────────────────────────
    integrations: [
      { name: "WhatsApp / Email Ticketing", category: "Omnichannel Support", icon: "message-square" },
      { name: "Google Maps API", category: "Field Dispatch & Routing", icon: "map" },
      { name: "Tally Prime", category: "Service Billing Sync", icon: "calculator" },
      { name: "Razorpay / UPI", category: "Field Payment Collection", icon: "credit-card" },
      { name: "Freshdesk / Zendesk", category: "Helpdesk Migration", icon: "life-buoy" },
    ],
    // ── industries (unchanged) ────────────────────────────────
    industriesServed: [
      "Consumer Electronics Service",
      "Industrial Equipment Maintenance",
      "IT Hardware & Networking Support",
      "HVAC & Appliance Repair",
      "Medical Device Servicing",
      "Automotive After-sales Support",
      "Facility Management Services",
    ],
    // ── clients / testimonials (unchanged) ───────────────────
    clients: [
      { id: "sm1", name: "ACMA Service", logo: "/images/clients/acma-logo.webp" },
      { id: "sm2", name: "AHPL Care", logo: "/images/clients/ahpl-logo.webp" },
      { id: "sm3", name: "AIIMS Support", logo: "/images/clients/aiims-logo.webp" },
      { id: "sm4", name: "Allahabad Facilities", logo: "/images/clients/allahabad-bank-logo.webp" },
      { id: "sm5", name: "ANT Services", logo: "/images/clients/ant-logo.webp" },
      { id: "sm6", name: "AOS Equipment", logo: "/images/clients/aos-logo.webp" },
      { id: "sm7", name: "Arogyavaram Tech", logo: "/images/clients/arogyavaram-logo.webp" },
      { id: "sm8", name: "Arora Maintenance", logo: "/images/clients/arora-aromatics-logo.webp" },
    ],
    testimonials: [
      {
        id: 228,
        name: "Rajesh Khanna",
        role: "Service Director",
        company: "Samsung India",
        avatar: "/images/partners/33.webp",
        quote: "Mx-CRM Service Management transformed our after-sales service. SLA compliance improved from 65% to 100%, and our CSAT scores jumped from 3.2 to 4.8. The mobile app alone saved 15 hours per engineer weekly.",
        rating: 5,
      },
      {
        id: 229,
        name: "Priya Nair",
        role: "Operations Manager",
        company: "Voltas Ltd",
        avatar: "/images/partners/34.webp",
        quote: "The AMC renewal tracking recovered ₹2.3 crores in the first year. We went from missing 25% of renewals to 100% capture rate. The parts tracking eliminated our inventory leakage completely.",
        rating: 5,
      },
      {
        id: 230,
        name: "Amit Verma",
        role: "Service Head",
        company: "Blue Star",
        avatar: "/images/partners/35.webp",
        quote: "Our 200 field engineers now use the mobile app for all service calls. Real-time updates and digital signatures have improved our first-time fix rate by 35% and reduced paperwork by 90%.",
        rating: 5,
      },
    ],
    ctaHeadline: "Elevate Your Customer Service",
    ctaSubtext: "Join top brands delivering 5-star post-sales support.",
    // ── FAQs (verified) ───────────────────────────────────────
    faqs: [
      { q: "What is Service Management CRM Software?", a: "It is a system that helps manage service requests, tickets, and customer support efficiently." },
      { q: "Can it track service tickets?", a: "Yes, it provides real-time tracking of all service tickets." },
      { q: "Does it support field service management?", a: "Yes, it includes technician tracking and job scheduling." },
      { q: "What is SLA management?", a: "SLA ensures timely response and resolution of service requests." },
      { q: "Is it suitable for small businesses?", a: "Yes, Mx-CRM is scalable for startups and enterprises." },
      { q: "Can it improve customer satisfaction?", a: "Yes, faster response and tracking improve customer experience." },
      { q: "Is it cloud-based or on-premise?", a: "Mx-CRM supports both deployment options." },
    ],
    // ── SEO (verified) ────────────────────────────────────────
    seo: {
      title: "Service Management CRM Software - ACGIL",
      description: "Service management CRM software for customer support, service requests, complaint tracking, follow-ups, field activities, and customer relationship management.",
      keywords: "service management crm software, customer support crm, ticket management system, field service management software, service crm software, complaint management system, after sales service software",
      canonical: SITE_URL + "/products/service-management-crm-software",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      ogTitle: "Service Management CRM Software - ACGIL",
      ogDescription: "Service management CRM software for customer support, service requests, complaint tracking, follow-ups, field activities, and customer relationship management.",
      ogUrl: SITE_URL + "/products/service-management-crm-software",
      ogImage: SITE_URL + "/images/service-crm.jpg",
      hreflang: {
        en: SITE_URL + "/products/service-management-crm-software",
        "x-default": SITE_URL + "/products/service-management-crm-software",
      },
      faqSchema: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          { "@type": "Question", name: "What is Service Management CRM Software?", acceptedAnswer: { "@type": "Answer", text: "Service Management CRM Software helps manage service requests, tickets, and customer support in one system." } },
          { "@type": "Question", name: "Can it track service tickets?", acceptedAnswer: { "@type": "Answer", text: "Yes, it provides real-time tracking of service tickets." } },
          { "@type": "Question", name: "Does it support field service?", acceptedAnswer: { "@type": "Answer", text: "Yes, it includes technician tracking and job scheduling." } },
          { "@type": "Question", name: "Can it improve customer satisfaction?", acceptedAnswer: { "@type": "Answer", text: "Yes, faster response and service tracking improve customer experience." } },
        ],
      },
    },
  },
  {
    id: "queue-management-system",
    slug: "queue-management-system",
    category: "electra-his",
    name: "Queue Management System",
    tagline: "Built for Zero-Wait Experiences — From Token to Consultation",
    image: "/images/products/qms-queue.webp",
    href: "/products/queue-management-system",
    badge: "New Launch",
    color: "#7C3AED",
    family: "Electra-HIS",
    keyWord: "Built",
    accentColor: "#7C3AED",
    trustCount: "500+",
    videoUrl: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    headlineThin: "Queue Management System",
    capHeadline: "End Waiting Lines with Smart Queues",
    headlineBold: "Queue Management System for Hospitals",
    headlineAccent: "QMS by ACGIL",
    subheadline:
      "QMS by ACGIL is a powerful Queue Management System designed to manage tokens, appointments, live queues, and patient flow — all in one unified platform.",
    dashHeading:
      "Manage tokens, appointments, live queues, and service counters from a single dashboard. QMS delivers real-time visibility into wait times, counter loads, and patient flow.",
    PainPointsSubheading:
      "Hospitals, clinics, and service centers lose patients and revenue every day to unmanaged queues and waiting chaos.",
    painPoints: [
      { title: "Overcrowded Waiting Halls", desc: "Unmanaged walk-ins pile up, frustrating patients and overwhelming reception staff." },
      { title: "Manual Token Chaos", desc: "Paper tokens and registers cause disputes, jumping queues, and lost patients." },
      { title: "Long Patient Wait Times", desc: "No slot discipline means hours-long waits and poor patient satisfaction scores." },
      { title: "Doctor Schedule Clashes", desc: "Appointments booked when doctors are on leave, in OT, or overbooked." },
      { title: "Zero Queue Visibility", desc: "No live view of counter loads, wait times, or no-shows across departments." },
    ],
    solutionHeadline: "How Queue Management System Solves These Challenges",
    description:
      "QMS digitizes the entire journey from token generation to consultation into one intelligent queue engine.",
    solutionFeatures: [
      "Smart token generation across walk-in, online, and kiosk channels",
      "Live queue boards with now-serving counters and display screens",
      "Doctor-wise slot calendars with leave and OT blocking",
      "Auto SMS, email, and WhatsApp reminders that cut no-shows",
    ],
    capdescription:
      "Everything you need to run zero-wait OPDs, clinics, labs, and service counters.",
    capabilities: [
      {
        title: "Token Generation & Live Queue Board",
        description:
          "Issue smart tokens from reception, kiosk, or online, and show live now-serving status on display screens across your facility.",
        features: [
          "Walk-in, online & kiosk token generation",
          "Live now-serving counter displays",
          "Doctor & counter-wise queues with transfers",
          "Emergency & priority token handling",
        ],
        tag: "Tokens",
        widget: "planning",
        accentColor: "#7C3AED",
        secondaryColor: "#059A8F",
      },
      {
        title: "Doctor Availability & Slot Calendar",
        description:
          "Give every doctor a day and time-wise availability grid that blocks bookings during leave, OT hours, or overbooked slots.",
        features: [
          "Day & time-wise availability grids",
          "Leave & OT auto-blocking",
          "Hospital calendar with non-working days",
          "Max visits per consultant caps",
        ],
        tag: "Scheduling",
        widget: "hr",
        accentColor: "#059A8F",
        secondaryColor: "#7C3AED",
      },
      {
        title: "Multi-Service Appointment Booking",
        description:
          "Let patients and staff book by department, doctor, or service — with multiple slots and appointments per slot when needed.",
        features: [
          "Search doctors by department & service",
          "Multiple slots per patient",
          "Multiple appointments per slot",
          "Cancellation & re-scheduling",
        ],
        tag: "Appointments",
        widget: "inventory",
        accentColor: "#2C78B9",
        secondaryColor: "#7C3AED",
      },
      {
        title: "Patient Alerts & Reminders",
        description:
          "Keep patients informed at every step with automated reminders, queue-position updates, and recall follow-ups.",
        features: [
          "SMS, email & WhatsApp reminders",
          "Queue position updates",
          "Erroneous-appointment alerts",
          "No-show recall & medico-legal records",
        ],
        tag: "Alerts",
        widget: "vendor",
        accentColor: "#7C3AED",
        secondaryColor: "#2C78B9",
      },
      {
        title: "Billing & Reception Integration",
        description:
          "Connect the queue to OPD billing so tokens, consultations, and payments stay in sync from reception to discharge.",
        features: [
          "Queue-linked OPD billing",
          "GST-compliant invoicing",
          "TPA & insurance-ready bills",
          "Real-time collection tracking",
        ],
        tag: "Billing",
        widget: "billing",
        accentColor: "#059A8F",
        secondaryColor: "#2C78B9",
      },
      {
        title: "MIS & Queue Analytics",
        description:
          "Track wait times, throughput, and no-show rates by doctor, counter, and hour to continuously shorten queues.",
        features: [
          "Average wait-time reports",
          "Doctor & counter load analytics",
          "Peak-hour dashboards",
          "No-show rate tracking",
        ],
        tag: "Analytics",
        widget: "mis",
        accentColor: "#2C78B9",
        secondaryColor: "#059A8F",
      },
    ],
    dashboardType: "queue",
    businessImpact: [
      {
        value: "60%",
        label: "Shorter Waits",
        description: "Cut average patient waiting time with disciplined slots",
        color: "#00F2FE",
        icon: "TrendingDown",
      },
      {
        value: "40%",
        label: "Higher Throughput",
        description: "Serve more patients per OPD hour with live queues",
        color: "#7F77DD",
        icon: "TrendingUp",
      },
      {
        value: "50%",
        label: "Fewer No-Shows",
        description: "Recover lost appointments with auto reminders",
        color: "#F59E0B",
        icon: "Zap",
      },
      {
        value: "Live",
        label: "Queue Visibility",
        description: "See every token, counter, and wait time in real time",
        color: "#EC4899",
        icon: "Eye",
      },
    ],
    workflow: [
      { id: 1, title: "Token Generation", step: "Step 1", icon: "order", color: "#7C3AED", bgColor: "bg-indigo-500", shadow: "shadow-indigo-500/20" },
      { id: 2, title: "Slot Booking", step: "Step 2", icon: "planning", color: "#7C3AED", bgColor: "bg-purple-500", shadow: "shadow-purple-500/20" },
      { id: 3, title: "Live Queue", step: "Step 3", icon: "production", color: "#7C3AED", bgColor: "bg-blue-500", shadow: "shadow-blue-500/20" },
      { id: 4, title: "Consultation", step: "Step 4", icon: "qc", color: "#7C3AED", bgColor: "bg-emerald-500", shadow: "shadow-emerald-500/20" },
      { id: 5, title: "Billing", step: "Step 5", icon: "finance", color: "#7C3AED", bgColor: "bg-amber-500", shadow: "shadow-amber-500/20" },
      { id: 6, title: "Feedback & Recall", step: "Step 6", icon: "dispatch", color: "#7C3AED", bgColor: "bg-rose-500", shadow: "shadow-rose-500/20" },
    ],
    features: [
      { id: "tokens", title: "Smart Token Engine", description: "Walk-in, online, and kiosk tokens with priority and transfer support." },
      { id: "slots", title: "Slot Calendar", description: "Doctor-wise availability with leave, OT, and overbooking protection." },
      { id: "display", title: "Live Queue Display", description: "Now-serving boards and counter screens across your facility." },
      { id: "alerts", title: "Auto Reminders", description: "SMS, email, and WhatsApp alerts that cut no-shows in half." },
      { id: "billing", title: "Queue Billing", description: "OPD billing linked to tokens with GST and TPA-ready invoicing." },
      { id: "analytics", title: "Queue Analytics", description: "Wait-time, throughput, and no-show dashboards for every counter." },
    ],
    headline: "Your Entire Patient Queue,",
    heroStats: [
      { label: "Shorter Waits", value: "60%" },
      { label: "Higher Throughput", value: "40%" },
      { label: "Live Tracking", value: "100%" },
    ],
    mockupImage: "/images/products/qms-hero.png",
    solutions: [
      { title: "Instant token generation", desc: "Issue tokens in seconds across reception, kiosk, and online channels." },
      { title: "Live queue displays", desc: "Now-serving boards keep waiting halls calm and informed." },
      { title: "Smart slot booking", desc: "Right patient, right doctor, right slot — with zero clashes." },
      { title: "Automatic reminders", desc: "Patients arrive on time with SMS and WhatsApp alerts." },
      { title: "Real-time queue analytics", desc: "Wait times and counter loads visible at a glance." },
    ],
    roiEstimate: "₹5–10 lakhs/year",
    implementationSteps: [
      {
        num: "01",
        title: "Queue Audit",
        description:
          "Our experts map your OPD flow, counters, peak hours, and doctor schedules to design the optimal token and slot structure.",
        bullets: ["Flow mapping", "Counter setup", "Peak-hour analysis"],
        image: "/images/products/audit.png",
        bg: "linear-gradient(135deg, #071428, #0d3060)",
      },
      {
        num: "02",
        title: "Display & Channel Setup",
        description:
          "We configure tokens, display screens, kiosks, and reminder channels to fit your facility — with 100% data accuracy from day one.",
        bullets: ["Display configuration", "Reminder integration", "Data migration"],
        image: "/images/products/integration.png",
        bg: "linear-gradient(135deg, #071e1c, #0a3530)",
      },
      {
        num: "03",
        title: "Go-Live & Support",
        description:
          "Dedicated training for reception, nursing, and admin staff followed by on-site support during the first month to ensure zero downtime.",
        bullets: ["Staff training", "On-site support", "Hypercare period"],
        image: "/images/products/support.png",
        bg: "linear-gradient(135deg, #100d28, #1a1040)",
      },
    ],
    clients: [
      { id: "qm1", name: "ACMA Hospitals", logo: "/images/clients/acma-logo.webp" },
      { id: "qm2", name: "AHPL Clinics", logo: "/images/clients/ahpl-logo.webp" },
      { id: "qm3", name: "AIIMS Care", logo: "/images/clients/aiims-logo.webp" },
      { id: "qm4", name: "Allahabad Health", logo: "/images/clients/allahabad-bank-logo.webp" },
      { id: "qm5", name: "ANT Diagnostics", logo: "/images/clients/ant-logo.webp" },
      { id: "qm6", name: "AOS Wellness", logo: "/images/clients/aos-logo.webp" },
      { id: "qm7", name: "Arogyavaram Care", logo: "/images/clients/arogyavaram-logo.webp" },
      { id: "qm8", name: "Arora Clinics", logo: "/images/clients/arora-aromatics-logo.webp" },
    ],
    industry: "Healthcare",
    heroImage: "/images/products/qms-hero.png",
    marqueeItems: [
      "Token Management",
      "Live Queue Display",
      "SMS Reminders",
      "Slot Booking",
      "Cloud Security",
      "ISO Certified",
    ],
    testimonials: [
      {
        id: 401,
        name: "Dr. Kavita Rao",
        role: "Medical Superintendent",
        company: "Rao Multi-Specialty Hospital",
        avatar: "/images/partners/1.png",
        quote:
          "QMS ended the chaos in our OPD. Average waiting time dropped from 90 minutes to 25, and our reception handles double the footfall with the same staff.",
        rating: 5,
      },
      {
        id: 402,
        name: "Sandeep Kulkarni",
        role: "Administrator",
        company: "Kulkarni Clinic Chain",
        avatar: "/images/partners/2.webp",
        quote:
          "No-shows fell by half within two months of switching on SMS reminders. Our doctors finally run on schedule — even on Mondays.",
        rating: 5,
      },
      {
        id: 403,
        name: "Dr. Farah Khan",
        role: "Operations Head",
        company: "Khan Diagnostics",
        avatar: "/images/partners/3.webp",
        quote:
          "The live queue boards changed patient behavior completely. Nobody crowds the counter anymore — everyone watches the display and waits calmly.",
        rating: 5,
      },
      {
        id: 404,
        name: "Ramesh Gupta",
        role: "Facility Manager",
        company: "Gupta Eye Care Centers",
        avatar: "/images/partners/4.webp",
        quote:
          "Five centers, one queue dashboard. I can see counter loads and wait times across all locations from my phone and rebalance staff instantly.",
        rating: 5,
      },
      {
        id: 405,
        name: "Dr. Latha Menon",
        role: "CEO",
        company: "Menon Health Group",
        avatar: "/images/partners/5.webp",
        quote:
          "The best ROI purchase we made this year. OPD throughput is up 40% and patient satisfaction scores have never been higher.",
        rating: 5,
      },
    ],
    integrations: [
      { name: "SMS Gateway", icon: "message-square", category: "Communication" },
      { name: "WhatsApp Business", icon: "message-square", category: "Communication" },
      { name: "Display Screens", icon: "monitor", category: "Hardware" },
      { name: "Kiosk Hardware", icon: "tablet", category: "Hardware" },
      { name: "Electra-HIS", icon: "heart-pulse", category: "Healthcare" },
      { name: "Tally Prime", icon: "calculator", category: "Accounting" },
      { name: "Razorpay / UPI", icon: "credit-card", category: "Payments" },
      { name: "E-Invoice", icon: "file-digit", category: "Government" },
    ],
    faqs: [
      {
        q: "What is a Queue Management System (QMS)?",
        a: "A Queue Management System digitizes token generation, appointment slots, and live queues so patients and customers are served in order without crowding or confusion.",
      },
      {
        q: "Who should use QMS?",
        a: "Hospitals, clinics, diagnostic labs, pharmacies, banks, and any service center with walk-in footfall benefit from QMS.",
      },
      {
        q: "Does it support both tokens and appointments?",
        a: "Yes, QMS handles walk-in tokens, online appointments, and kiosk check-ins in one unified queue.",
      },
      {
        q: "Can it show live status on display screens?",
        a: "Yes, now-serving counters and queue boards can run on TVs and display screens across your facility.",
      },
      {
        q: "Does it send reminders to patients?",
        a: "Yes, it sends automatic SMS, email, and WhatsApp reminders plus queue-position updates.",
      },
      {
        q: "Does it work with Electra-HIS?",
        a: "Yes, QMS integrates with Electra-HIS for patient records, OPD billing, and EMR continuity.",
      },
      {
        q: "Can it manage multiple doctors and counters?",
        a: "Yes, it supports doctor-wise and counter-wise queues with transfers and priority handling.",
      },
      {
        q: "Is it cloud-based or on-premise?",
        a: "QMS supports both cloud-based and on-premise deployment options.",
      },
    ],
    industriesServed: [
      "Multi-Specialty Hospitals",
      "Clinics & OPD Centers",
      "Diagnostic & Imaging Labs",
      "Dental & Eye Care Chains",
      "Government Health Centers",
      "Retail Service Desks",
      "Banks & Citizen Services",
    ],
    comparison: [
      { feature: "Accuracy", mxerp: "High — Automated", others: "Error-prone" },
      { feature: "Speed", mxerp: "Fast", others: "Slow" },
      { feature: "Automation", mxerp: "Yes", others: "No" },
      { feature: "Tracking", mxerp: "Real-time", others: "Limited" },
      { feature: "Efficiency", mxerp: "High", others: "Low" }
    ],
    seo: {
      title: "Queue Management System for Hospitals, Clinics & Services | ACGIL",
      description: "ACGIL's Queue Management System ends waiting lines with smart tokens, live queue displays, slot booking, and SMS reminders for hospitals, clinics & services. Book a free demo.",
      keywords:
        "queue management system, patient queue management software, queue management system for hospitals, token management system, opd queue software, appointment and queue management, waiting line management software",
      canonical: SITE_URL + "/products/queue-management-system",
      robots: "index, follow, max-snippet:-1, max-image-preview:large",
      ogTitle: "Queue Management System for Hospitals, Clinics & Services | ACGIL",
      ogDescription: "ACGIL's Queue Management System ends waiting lines with smart tokens, live queue displays, slot booking, and SMS reminders for hospitals, clinics & services. Book a free demo.",
      ogUrl: SITE_URL + "/products/queue-management-system",
      ogImage: SITE_URL + "/images/queue-management-system.jpg",
      hreflang: {
        en: SITE_URL + "/products/queue-management-system",
        "x-default": SITE_URL + "/products/queue-management-system",
      },
      faqSchema: {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {
            "@type": "Question",
            name: "What is a Queue Management System (QMS)?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "A Queue Management System digitizes token generation, appointment slots, and live queues for orderly, wait-free service.",
            },
          },
          {
            "@type": "Question",
            name: "Who should use QMS?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Hospitals, clinics, diagnostic labs, and service centers with walk-in footfall benefit from QMS.",
            },
          },
          {
            "@type": "Question",
            name: "Does it support display screens?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, it shows live now-serving status on TVs and display screens.",
            },
          },
          {
            "@type": "Question",
            name: "Does it send reminders?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, it sends automatic SMS, email, and WhatsApp reminders.",
            },
          },
          {
            "@type": "Question",
            name: "Is it scalable?",
            acceptedAnswer: {
              "@type": "Answer",
              text: "Yes, QMS scales from single clinics to multi-location hospital groups.",
            },
          },
        ],
      },
    },
  },
];
